<template>
  <div>
    <div v-if="sensoresTokenMaquina" class="alertas-page">

      <!-- Page header -->
      <header class="alertas-page__header alertas-hero">
        <div class="alertas-hero__main">
          <div class="alertas-hero__icon">
            <v-icon size="30">mdi-radar</v-icon>
          </div>
          <div class="alertas-hero__text">
            <h1 class="alertas-page__title">Alertas y sensores</h1>
            <p class="alertas-page__subtitle">
              Administra los niveles de alerta, medios de notificacion, programaciones y reglas para generar notificaciones efectivas.
            </p>
          </div>
        </div>

        <div class="alertas-hero__stats">
          <div class="alertas-hero__stat">
            <span class="alertas-hero__stat-value">{{ alertasTotalRegistros }}</span>
            <span class="alertas-hero__stat-label">Registros totales</span>
          </div>
          <div class="alertas-hero__stat-divider"></div>
          <div class="alertas-hero__stat">
            <span class="alertas-hero__stat-value">{{ alertasCatalogosActivos }}</span>
            <span class="alertas-hero__stat-label">Secciones activas</span>
          </div>
        </div>
      </header>

      <!-- 4 navigation cards -->
      <section class="alertas-nav">
        <div class="alertas-nav__label">CONFIGURACION DE ALERTA</div>
        <div class="alertas-nav__grid">
          <article
            v-for="(step, index) in alarmSteps"
            :key="'nav-' + step.key"
            class="alertas-nav-card"
            :class="[
              'alertas-nav-card--' + alarmStepTone(step.key),
              { 'alertas-nav-card--active': alarmStep === index + 1 }
            ]"
            @click="selectAlarmStep(index + 1)"
            tabindex="0"
            @keyup.enter="selectAlarmStep(index + 1)"
          >
            <div class="alertas-nav-card__top">
              <div class="alertas-nav-card__icon">
                <v-icon size="26">{{ alarmStepIcon(step.key) }}</v-icon>
              </div>
              <span v-if="alarmStep === index + 1" class="alertas-nav-card__pill">
                <v-icon size="12">mdi-check</v-icon>
                Activo
              </span>
            </div>
            <div class="alertas-nav-card__body">
              <h3 class="alertas-nav-card__title">{{ alarmStepTitle(step.key) }}</h3>
              <p class="alertas-nav-card__description">{{ alarmStepDescription(step.key) }}</p>
            </div>
            <div class="alertas-nav-card__footer">
              <span class="alertas-nav-card__count">
                <span class="alertas-nav-card__count-num">{{ alarmStepCount(step.key) }}</span>
                <span class="alertas-nav-card__count-label">registros</span>
              </span>
              <span class="alertas-nav-card__cta">
                {{ alarmStep === index + 1 ? "Ver activo" : "Administrar" }}
                <v-icon small>mdi-arrow-right</v-icon>
              </span>
            </div>
            <div class="alertas-nav-card__glow" aria-hidden="true"></div>
          </article>
        </div>
      </section>

      <v-row class="alarm-builder__body">
        <v-col cols="12" v-if="false">
          <v-card outlined elevation="0">
            <div
              v-for="(step, index) in alarmSteps"
              :key="step.key"
              @click="!step.locked && (alarmStep = index + 1)"
            ></div>
          </v-card>
        </v-col>

        <v-col cols="12">
          <!-- Section header: colored uppercase title + CTA -->
          <header class="alertas-section__header" :class="'alertas-section__header--' + currentSectionTone">
            <div class="alertas-section__title-block">
              <div class="alertas-section__icon">
                <v-icon>{{ currentSectionIcon }}</v-icon>
              </div>
              <div>
                <div class="alertas-section__label">{{ currentSectionLabel }}</div>
                <p class="alertas-section__description">{{ currentAlarmSubtitle }}</p>
              </div>
            </div>
            <button
              v-if="alarmStep !== 1"
              class="alertas-section__cta"
              :class="'alertas-section__cta--' + currentSectionTone"
              @click="openCurrentAlarmDialog"
            >
              <v-icon left small dark>mdi-plus</v-icon>
              {{ currentAlarmButtonText }}
            </button>
            <button
              v-else
              class="alertas-section__cta alertas-section__cta--blue"
              @click="openConfigurarSeveridades"
            >
              <v-icon left small dark>mdi-cog-outline</v-icon>
              Configurar severidades
            </button>
          </header>

          <v-card outlined elevation="0" class="alarm-levels-card alertas-table-card">

            <v-card-text class="pa-0">
              <div class="alarm-list-toolbar">
                <v-text-field
                  v-if="alarmStep === 1"
                  v-model="nivelAlertaSearch"
                  append-icon="mdi-magnify"
                  label="Buscar nivel"
                  outlined
                  dense
                  hide-details
                  class="alarm-list-card__search"
                ></v-text-field>
                <v-text-field
                  v-else-if="alarmStep === 4"
                  v-model="reglasMaquinaSearch"
                  append-icon="mdi-magnify"
                  label="Buscar regla de máquina"
                  outlined
                  dense
                  hide-details
                  class="alarm-list-card__search"
                ></v-text-field>
                <v-text-field
                  v-else
                  v-model="currentCatalogState.search"
                  append-icon="mdi-magnify"
                  :label="`Buscar ${currentCatalogConfig.shortLabel.toLowerCase()}`"
                  outlined
                  dense
                  hide-details
                  class="alarm-list-card__search"
                ></v-text-field>
                <v-btn icon color="primary" :loading="currentAlarmLoading" @click="refreshCurrentAlarmStep">
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </div>

              <v-data-table
                v-if="alarmStep === 1"
                :headers="headersNivelesAlerta"
                :items="nivelesAlerta"
                :search="nivelAlertaSearch"
                :loading="loadingNivelesAlerta"
                loading-text="Cargando niveles..."
                no-data-text="No hay niveles de alerta configurados."
                class="alarm-levels-table"
                hide-default-footer
              >
                <template v-slot:[`item.Nombre`]="{ item }">
                  <div class="alarm-level-name">
                    <v-icon :color="item.ColorHex || '#94a3b8'" size="34">mdi-alert-outline</v-icon>
                    <div>
                      <div class="alarm-level-name__title">{{ item.Nombre || item.Codigo }}</div>
                      <v-chip x-small :color="softColor(item.ColorHex)" :text-color="item.ColorHex || '#0f172a'" class="alarm-level-name__tag">
                        {{ priorityTag(item.Prioridad) }}
                      </v-chip>
                    </div>
                  </div>
                </template>
                <template v-slot:[`item.Codigo`]="{ item }">
                  <v-chip small class="alarm-code-chip" :style="{ color: item.ColorHex || '#0f172a', background: softColor(item.ColorHex) }">
                    {{ item.Codigo }}
                  </v-chip>
                </template>
                <template v-slot:[`item.Descripcion`]="{ item }">
                  <div class="alarm-description-cell">{{ item.Descripcion || 'Sin descripcion' }}</div>
                </template>
                <template v-slot:[`item.ColorHex`]="{ item }">
                  <div class="alarm-color-cell">
                    <span class="alarm-color-dot alarm-color-dot--table" :style="{ background: item.ColorHex || '#94a3b8' }"></span>
                    <div>
                      <div class="alarm-color-cell__name">{{ colorName(item.ColorHex) }}</div>
                      <div class="alarm-color-cell__hex">{{ item.ColorHex }}</div>
                    </div>
                  </div>
                </template>
                <template v-slot:[`item.Prioridad`]="{ item }">
                  <div class="alarm-priority-cell">
                    <span class="alarm-priority-cell__number" :style="{ background: softColor(item.ColorHex), color: item.ColorHex || '#0f172a' }">
                      {{ priorityNumber(item.Prioridad) }}
                    </span>
                    <span>{{ priorityLabel(item.Prioridad) }}</span>
                  </div>
                </template>
                <template v-slot:[`item.RespetaHorario`]="{ item }">
                  <div class="alarm-schedule-cell">
                    <v-chip small :color="item.RespetaHorario ? 'green lighten-4' : 'red lighten-4'" :text-color="item.RespetaHorario ? 'green darken-2' : 'red darken-2'">
                      {{ item.RespetaHorario ? 'Si' : 'No' }}
                    </v-chip>
                    <div class="alarm-schedule-cell__hint">
                      {{ item.RespetaHorario ? '(Recomendado)' : '(Recomendado deshabilitar)' }}
                    </div>
                  </div>
                </template>
                <template v-slot:[`item.Activo`]="{ item }">
                  <v-chip small color="green lighten-4" text-color="green darken-2">
                    {{ item.Activo === false ? 'Inactivo' : 'Activo' }}
                  </v-chip>
                </template>
                <template v-slot:[`item.actions`]>
                  <div class="alarm-actions-cell">
                    <span class="alarm-fixed-chip">
                      <v-icon x-small color="#64748b">mdi-lock-outline</v-icon>
                      Fijo
                    </span>
                  </div>
                </template>
              </v-data-table>
              <v-data-table
                v-else-if="alarmStep === 4"
                :headers="headersReglasMaquina"
                :items="reglasMaquina"
                :search="reglasMaquinaSearch"
                :loading="loadingReglasMaquina"
                :items-per-page="10"
                loading-text="Cargando reglas de máquina..."
                no-data-text="No hay reglas de máquina configuradas."
                class="alarm-levels-table"
              >
                <template v-slot:[`item.TipoAlerta`]="{ item }">
                  <v-chip small :color="item.TipoAlerta === 'MAQUINA' ? 'blue lighten-5' : 'teal lighten-5'" :text-color="item.TipoAlerta === 'MAQUINA' ? 'blue darken-3' : 'teal darken-3'">
                    <v-icon left small :color="tipoAlertaColor(item.TipoAlerta)">{{ tipoAlertaIcon(item.TipoAlerta) }}</v-icon>
                    {{ tipoAlertaLabel(item.TipoAlerta) }}
                  </v-chip>
                </template>
                <template v-slot:[`item.Objetivo`]="{ item }">
                  <div class="alarm-level-name">
                    <v-icon :color="tipoAlertaColor(item.TipoAlerta)" size="32">{{ tipoAlertaIcon(item.TipoAlerta) }}</v-icon>
                    <div>
                      <div class="alarm-level-name__title">{{ reglaObjetivoLabel(item) }}</div>
                      <v-chip x-small class="alarm-level-name__tag" style="color:#0f766e;background:#0f766e18">
                        {{ reglaObjetivoSubtitle(item) }}
                      </v-chip>
                    </div>
                  </div>
                </template>
                <template v-slot:[`item.NivelAlerta`]="{ item }">
                  <v-chip small color="red lighten-5" text-color="red darken-3">
                    {{ item.NivelAlerta || catalogNombre(nivelesAlerta, item.IdNivelAlerta, 'IdNivelAlerta') }}
                  </v-chip>
                </template>
                <template v-slot:[`item.MedioNotificacion`]="{ item }">
                  {{ item.MedioNotificacion || catalogNombre(mediosNotificacion, item.IdMedioNotificacion, 'IdMedioNotificacion') }}
                </template>
                <template v-slot:[`item.usuarios`]="{ item }">
                  <div class="alarm-description-cell">{{ reglaUsuariosLabel(item) }}</div>
                </template>
                <template v-slot:[`item.Activo`]="{ item }">
                  <v-switch
                    :key="'regla-sw-' + item.IdReglaNotificacion + '-' + reglaActivoNonce"
                    :input-value="item.Activo !== false"
                    :loading="deletingReglaMaquinaId === item.IdReglaNotificacion"
                    :disabled="deletingReglaMaquinaId === item.IdReglaNotificacion"
                    color="success"
                    dense
                    hide-details
                    inset
                    class="estado-switch"
                    :label="item.Activo === false ? 'Inactiva' : 'Activa'"
                    @change="onToggleReglaMaquina(item)"
                  ></v-switch>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                  <div class="alarm-actions-cell">
                    <v-btn small depressed color="primary" class="mr-2" @click="openAsignarUsuariosRegla(item)">
                      <v-icon left small>mdi-account-multiple-plus-outline</v-icon>
                      Asignar usuarios
                    </v-btn>
                    <v-btn icon small class="alarm-action-btn alarm-action-btn--edit" @click="editReglaMaquina(item)">
                      <v-icon small color="#0d47a1">mdi-pencil-outline</v-icon>
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
              <v-data-table
                v-else
                :headers="currentCatalogConfig.headers"
                :items="currentCatalogState.items"
                :search="currentCatalogState.search"
                :loading="currentCatalogState.loading"
                :loading-text="`Cargando ${currentCatalogConfig.shortLabel.toLowerCase()}...`"
                :no-data-text="`No hay ${currentCatalogConfig.shortLabel.toLowerCase()} configurados.`"
                class="alarm-levels-table"
                hide-default-footer
              >
                <template v-slot:[`item.Nombre`]="{ item }">
                  <div class="alarm-level-name">
                    <v-icon :color="catalogItemColor(item)" size="32">{{ catalogItemIcon(item) }}</v-icon>
                    <div>
                      <div class="alarm-level-name__title">{{ item.Nombre || item.Codigo }}</div>
                      <v-chip x-small class="alarm-level-name__tag" :style="catalogItemSoftStyle(item)">
                        {{ catalogBadge(item) }}
                      </v-chip>
                    </div>
                  </div>
                </template>
                <template v-slot:[`item.Codigo`]="{ item }">
                  <v-chip small class="alarm-code-chip" :style="catalogItemSoftStyle(item)">
                    {{ item.Codigo }}
                  </v-chip>
                </template>
                <template v-slot:[`item.Descripcion`]="{ item }">
                  <div class="alarm-description-cell">{{ item.Descripcion || 'Sin descripcion' }}</div>
                </template>
                <template v-slot:[`item.Canal`]="{ item }">
                  <v-chip small class="alarm-catalog-value-chip" :style="catalogItemSoftStyle(item)">
                    <v-icon left x-small>{{ catalogItemIcon(item) }}</v-icon>
                    {{ item.Canal }}
                  </v-chip>
                </template>
                <template v-slot:[`item.TipoFrecuencia`]="{ item }">
                  <v-chip small class="alarm-catalog-value-chip" :style="catalogItemSoftStyle(item)">
                    <v-icon left x-small>{{ catalogItemIcon(item) }}</v-icon>
                    {{ item.TipoFrecuencia }}
                  </v-chip>
                </template>
                <template v-slot:[`item.IntervaloMinutos`]="{ item }">
                  <div class="alarm-priority-cell">
                    <span class="alarm-priority-cell__number" :style="catalogItemSoftStyle(item)">
                      {{ item.IntervaloMinutos || '-' }}
                    </span>
                    <span>{{ item.IntervaloMinutos ? 'Minutos' : 'Sin intervalo' }}</span>
                  </div>
                </template>
                <template v-slot:[`item.Horario`]="{ item }">
                  <div class="alarm-schedule-cell">
                    <v-chip small color="green lighten-5" text-color="green darken-3">
                      {{ item.HoraInicio }} - {{ item.HoraFin }}
                    </v-chip>
                  </div>
                </template>
                <template v-slot:[`item.Dias`]="{ item }">
                  <div class="alarm-days-cell">
                    <span
                      v-for="day in notificationDays"
                      :key="day.key"
                      :title="day.label"
                      :class="{ 'alarm-days-cell__day--active': horarioDiaActivo(item, day) }"
                      :style="notificationDayStyle(day, horarioDiaActivo(item, day))"
                      class="alarm-days-cell__day"
                    >
                      {{ day.short }}
                    </span>
                  </div>
                </template>
                <template v-slot:[`item.FrecuenciaNotificacion`]="{ item }">
                  <v-chip small class="alarm-catalog-value-chip" :style="{ color: '#0f766e', background: 'rgba(15, 118, 110, 0.10)' }">
                    <v-icon left x-small color="#0f766e">mdi-bell-alert</v-icon>
                    {{ item.FrecuenciaNotificacion || '-' }}
                  </v-chip>
                </template>
                <template v-slot:[`item.HorarioNotificacion`]="{ item }">
                  <v-chip small class="alarm-catalog-value-chip" :style="{ color: '#b45309', background: 'rgba(245, 158, 11, 0.12)' }">
                    <v-icon left x-small color="#b45309">mdi-calendar-clock</v-icon>
                    {{ item.HorarioNotificacion || '-' }}
                  </v-chip>
                </template>
                <template v-slot:[`item.ReglasAsociadas`]="{ item }">
                  <div class="asoc-cell">
                    <span class="asoc-cell__count">
                      {{ item.ReglasAsociadas || 0 }} regla{{ item.ReglasAsociadas === 1 ? '' : 's' }}
                    </span>
                    <a v-if="item.ReglasAsociadas > 0" class="asoc-cell__link" @click="verAsociacionesCatalog(item)">Ver reglas</a>
                    <span v-else class="asoc-cell__empty">Sin reglas</span>
                  </div>
                </template>
                <template v-slot:[`item.Activo`]="{ item }">
                  <v-switch
                    :key="'cat-sw-' + getCatalogItemId(item) + '-' + catalogEstadoNonce"
                    :input-value="item.Activo !== false"
                    :loading="currentCatalogState.deletingId === getCatalogItemId(item)"
                    :disabled="currentCatalogState.deletingId === getCatalogItemId(item)"
                    color="success"
                    dense
                    hide-details
                    inset
                    class="estado-switch"
                    :label="item.Activo === false ? 'Inactivo' : 'Activo'"
                    @change="toggleCatalogItem(item)"
                  ></v-switch>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                  <div class="alarm-actions-cell">
                    <v-btn icon small class="alarm-action-btn alarm-action-btn--edit" @click="currentCatalogKey === 'programaciones' ? openProgramacionFormView(item) : editCatalogItem(item)">
                      <v-icon small color="#0d47a1">mdi-pencil-outline</v-icon>
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-dialog v-model="dialogDependenciasNotif" max-width="540px">
        <v-card v-if="dependCatalogItem" class="depend-dialog">
          <v-card-title class="depend-dialog__title">
            <v-icon color="warning" left>mdi-alert-outline</v-icon>
            No se puede desactivar {{ dependCatalogLabel === 'Medios' ? 'el medio' : 'la programación' }}
          </v-card-title>
          <v-card-text>
            <p class="mb-3">
              <strong>{{ dependCatalogItem.Codigo || dependCatalogItem.Nombre }}</strong> no puede desactivarse porque tiene
              <strong>{{ dependCatalogItem.ReglasAsociadas }} regla(s) activa(s) asociada(s)</strong>.
            </p>
            <div class="subtitle-2 mb-1">Reglas asociadas:</div>
            <ul class="depend-dialog__list">
              <li v-for="(r, i) in (dependCatalogItem.ReglasAsociadasLista || [])" :key="i">{{ r }}</li>
            </ul>
            <v-alert dense text type="warning" class="mb-0 mt-3">
              Para desactivar, primero desactiva o reasigna las reglas de notificación asociadas.
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue darken-1" text @click="dialogDependenciasNotif = false">Cerrar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="dialogDesactivarRegla" max-width="520px">
        <v-card>
          <v-card-title class="headline">
            <v-icon color="warning" left>mdi-alert-outline</v-icon>
            Desactivar regla de notificación
          </v-card-title>
          <v-card-text>
            <p class="mb-3">
              ¿Estás seguro de que deseas desactivar la siguiente regla?
              Mientras la regla esté inactiva, no se enviarán notificaciones.
            </p>
            <div v-if="reglaDesactivarItem" class="regla-desactivar-card">
              <div class="regla-desactivar-card__name">
                {{ reglaDesactivarItem.Nombre || reglaDesactivarItem.Codigo }}
              </div>
              <div class="regla-desactivar-card__obj">{{ reglaObjetivoLabel(reglaDesactivarItem) }}</div>
            </div>
            <v-alert dense text type="info" class="mb-0 mt-3">
              La regla puede volver a activarse en cualquier momento.
              Las configuraciones y usuarios asignados se mantendrán.
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="cancelarDesactivarRegla">Cancelar</v-btn>
            <v-btn
              color="error"
              :loading="deletingReglaMaquinaId === (reglaDesactivarItem && reglaDesactivarItem.IdReglaNotificacion)"
              @click="confirmarDesactivarRegla"
            >
              <v-icon left small>mdi-pause-circle-outline</v-icon>
              Desactivar regla
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="programacionFormView" persistent max-width="980px">
        <v-card class="uni-dlg" :style="uniDlgStyle('#8b5cf6')">
          <header class="uni-dlg__header">
            <div class="uni-dlg__title-row">
              <div class="uni-dlg__icon">
                <v-icon>mdi-calendar-clock</v-icon>
              </div>
              <div class="uni-dlg__title-block">
                <span class="uni-dlg__eyebrow">Programaciones</span>
                <h2 class="uni-dlg__title">
                  {{ programacionFormEditing ? 'Editar programacion' : 'Nueva programacion' }}
                </h2>
                <p class="uni-dlg__subtitle">
                  Combina una frecuencia y un horario existentes en una programacion reutilizable.
                </p>
              </div>
            </div>
            <div class="uni-dlg__actions">
              <button class="uni-dlg__btn uni-dlg__btn--ghost" :disabled="notificationCatalogState.programaciones.saving" @click="closeProgramacionFormView">
                <v-icon small>mdi-close</v-icon>
                Cancelar
              </button>
              <button class="uni-dlg__btn uni-dlg__btn--primary" :disabled="notificationCatalogState.programaciones.saving" @click="saveProgramacionFromView">
                <v-icon small>{{ programacionFormEditing ? 'mdi-content-save-edit-outline' : 'mdi-content-save-outline' }}</v-icon>
                {{ notificationCatalogState.programaciones.saving ? 'Guardando...' : (programacionFormEditing ? 'Actualizar' : 'Guardar') }}
              </button>
            </div>
          </header>
          <v-card-text class="uni-dlg__body">
            <section class="uni-dlg__section">
              <div class="uni-dlg__step">
                <span class="uni-dlg__step-num">1</span>
                <span class="uni-dlg__step-label">Informacion general</span>
              </div>
              <p class="uni-dlg__hint">Identifica la programacion con un nombre claro y una clave unica.</p>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <label class="uni-dlg__field-label">Nombre <span class="uni-dlg__required">*</span></label>
                  <v-text-field
                    v-model.trim="notificationCatalogForm.Nombre"
                    placeholder="Ej. Horario Laboral 15 Minutos"
                    outlined dense hide-details="auto"
                    maxlength="100" counter
                    :error-messages="notificationCatalogErrors.Nombre"
                    @focus="notificationCatalogErrors.Nombre = null"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <label class="uni-dlg__field-label">Codigo <span class="uni-dlg__required">*</span></label>
                  <v-text-field
                    v-model.trim="notificationCatalogForm.Codigo"
                    placeholder="Ej. PROG-007"
                    outlined dense hide-details="auto"
                    maxlength="50" counter
                    :error-messages="notificationCatalogErrors.Codigo"
                    @focus="notificationCatalogErrors.Codigo = null"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <label class="uni-dlg__field-label">Descripcion</label>
                  <v-textarea
                    v-model.trim="notificationCatalogForm.Descripcion"
                    placeholder="Descripcion de la programacion"
                    outlined dense hide-details="auto"
                    rows="2" auto-grow maxlength="300" counter
                  ></v-textarea>
                </v-col>
              </v-row>
            </section>

            <section class="uni-dlg__section">
              <div class="uni-dlg__step">
                <span class="uni-dlg__step-num">2</span>
                <span class="uni-dlg__step-label">Composicion</span>
              </div>
              <p class="uni-dlg__hint">Asocia una frecuencia y un horario ya configurados en el sistema.</p>
              <div class="uni-dlg__split">
                <article class="uni-dlg__split-card">
                  <div class="uni-dlg__split-header">
                    <div class="uni-dlg__split-icon"><v-icon>mdi-bell-alert</v-icon></div>
                    <div>
                      <h3 class="uni-dlg__split-title">Frecuencia</h3>
                      <p class="uni-dlg__split-description">Cada cuanto se envia la notificacion.</p>
                    </div>
                  </div>
                  <label class="uni-dlg__field-label">Frecuencia <span class="uni-dlg__required">*</span></label>
                  <v-autocomplete
                    v-model="notificationCatalogForm.IdFrecuenciaNotificacion"
                    :items="frecuenciasNotificacion"
                    :item-text="frecuenciaItemText"
                    item-value="IdFrecuenciaNotificacion"
                    placeholder="Seleccione una frecuencia"
                    outlined dense hide-details="auto"
                    :loading="loadingFrecuenciasNotificacion"
                    :error-messages="notificationCatalogErrors.IdFrecuenciaNotificacion"
                    @focus="notificationCatalogErrors.IdFrecuenciaNotificacion = null"
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.Nombre || item.Codigo }}</v-list-item-title>
                        <v-list-item-subtitle class="alarm-fk-subtitle">
                          {{ item.Codigo }} <span v-if="item.TipoFrecuencia">- {{ item.TipoFrecuencia }}</span>
                          <span v-if="item.IntervaloMinutos"> ({{ item.IntervaloMinutos }} min)</span>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                  <div class="uni-dlg__note">
                    <v-icon small>mdi-information-outline</v-icon>
                    Las frecuencias disponibles se configuran por administrador.
                  </div>
                </article>
                <article class="uni-dlg__split-card">
                  <div class="uni-dlg__split-header">
                    <div class="uni-dlg__split-icon"><v-icon>mdi-calendar-clock</v-icon></div>
                    <div>
                      <h3 class="uni-dlg__split-title">Horario</h3>
                      <p class="uni-dlg__split-description">Cuando aplica esta programacion.</p>
                    </div>
                  </div>
                  <label class="uni-dlg__field-label">Horario <span class="uni-dlg__required">*</span></label>
                  <v-autocomplete
                    v-model="notificationCatalogForm.IdHorarioNotificacion"
                    :items="horariosNotificacion"
                    :item-text="horarioItemText"
                    item-value="IdHorarioNotificacion"
                    placeholder="Seleccione un horario"
                    outlined dense hide-details="auto"
                    :loading="loadingHorariosNotificacion"
                    :error-messages="notificationCatalogErrors.IdHorarioNotificacion"
                    @focus="notificationCatalogErrors.IdHorarioNotificacion = null"
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.Nombre || item.Codigo }}</v-list-item-title>
                        <v-list-item-subtitle class="alarm-fk-subtitle">
                          {{ item.Codigo }}
                          <span v-if="item.HoraInicio && item.HoraFin"> - {{ item.HoraInicio }} a {{ item.HoraFin }}</span>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                  <div class="uni-dlg__note">
                    <v-icon small>mdi-information-outline</v-icon>
                    Los horarios definen los dias y horas en que estara activa.
                  </div>
                </article>
              </div>
            </section>

            <section class="uni-dlg__summary">
              <div class="uni-dlg__summary-title">
                <v-icon small>mdi-clipboard-check-outline</v-icon>
                Resumen de la programacion
              </div>
              <p class="uni-dlg__summary-hint">Asi quedara configurada la programacion antes de guardar.</p>
              <div class="uni-dlg__summary-grid">
                <div class="uni-dlg__summary-cell">
                  <v-icon>mdi-tag-text-outline</v-icon>
                  <div class="uni-dlg__summary-label">Nombre</div>
                  <div class="uni-dlg__summary-value" :class="{ 'uni-dlg__summary-value--empty': !notificationCatalogForm.Nombre }">
                    {{ notificationCatalogForm.Nombre || 'Sin nombre' }}
                  </div>
                </div>
                <div class="uni-dlg__summary-cell">
                  <v-icon>mdi-bell-alert</v-icon>
                  <div class="uni-dlg__summary-label">Frecuencia</div>
                  <div class="uni-dlg__summary-value" :class="{ 'uni-dlg__summary-value--empty': !selectedFrecuencia }">
                    {{ selectedFrecuencia ? (selectedFrecuencia.Nombre || selectedFrecuencia.Codigo) : 'No seleccionada' }}
                  </div>
                </div>
                <div class="uni-dlg__summary-cell">
                  <v-icon>mdi-calendar-clock</v-icon>
                  <div class="uni-dlg__summary-label">Horario</div>
                  <div class="uni-dlg__summary-value" :class="{ 'uni-dlg__summary-value--empty': !selectedHorario }">
                    {{ selectedHorario ? (selectedHorario.Nombre || selectedHorario.Codigo) : 'No seleccionado' }}
                  </div>
                </div>
                <div class="uni-dlg__summary-cell">
                  <v-icon>mdi-calendar-week</v-icon>
                  <div class="uni-dlg__summary-label">Dias</div>
                  <div class="uni-dlg__summary-value" :class="{ 'uni-dlg__summary-value--empty': !horarioDiasResumen }">
                    {{ horarioDiasResumen || 'No definidos' }}
                  </div>
                </div>
                <div class="uni-dlg__summary-cell">
                  <v-icon>mdi-clock-outline</v-icon>
                  <div class="uni-dlg__summary-label">Rango horario</div>
                  <div class="uni-dlg__summary-value" :class="{ 'uni-dlg__summary-value--empty': !horarioRangoResumen }">
                    {{ horarioRangoResumen || 'No definido' }}
                  </div>
                </div>
              </div>
            </section>
          </v-card-text>
          <div class="uni-dlg__footer-note">
            <v-icon small>mdi-information-outline</v-icon>
            Los campos con <span class="uni-dlg__required">*</span> son obligatorios.
          </div>
        </v-card>
      </v-dialog>

      <v-dialog v-model="dialogConfigurarSeveridades" persistent max-width="1280px" scrollable>
        <div class="severidades-page">
          <header class="severidades-page__header">
            <div class="severidades-page__back-row">
              <a class="severidades-page__back" @click="closeConfigurarSeveridadesDialog">
                <v-icon small>mdi-chevron-left</v-icon>
                Volver a niveles de alerta
              </a>
            </div>
            <div class="severidades-page__title-row">
              <div class="severidades-page__icon">
                <v-icon>mdi-cog-outline</v-icon>
              </div>
              <div class="severidades-page__title-block">
                <h1 class="severidades-page__title">Configurar severidades de alerta</h1>
                <p class="severidades-page__subtitle">
                  Define como se comporta cada severidad de alerta detectada por el sistema.<br>
                  Esta configuracion se utilizara para generar notificaciones y visualizaciones.
                </p>
              </div>
              <div class="severidades-page__actions">
                <button
                  class="severidades-page__btn severidades-page__btn--ghost"
                  :disabled="savingSeveridades"
                  @click="closeConfigurarSeveridadesDialog"
                >
                  <v-icon small>mdi-close</v-icon>
                  Cancelar
                </button>
                <button
                  class="severidades-page__btn severidades-page__btn--primary"
                  :disabled="savingSeveridades"
                  @click="saveSeveridades"
                >
                  <v-icon small>mdi-content-save-outline</v-icon>
                  {{ savingSeveridades ? 'Guardando...' : 'Guardar cambios' }}
                </button>
              </div>
            </div>
          </header>

          <div class="severidades-info">
            <v-icon small color="#1976d2">mdi-information-outline</v-icon>
            Estas severidades son fijas y no se pueden eliminar. Solo puedes configurar su comportamiento.
          </div>

          <div class="severidades-table-head">
            <div>Severidad detectada</div>
            <div>Descripcion</div>
            <div>Color visual</div>
            <div>Prioridad de notificacion</div>
            <div>Respeta horario</div>
            <div>Estado</div>
          </div>

          <article
            v-for="(item, idx) in severidadesConfig"
            :key="'sev-' + idx"
            class="severidades-row"
            :class="'severidades-row--' + severidadTone(item)"
          >
            <!-- Severidad detectada (read-only) -->
            <div class="severidades-row__name">
              <div class="severidades-row__name-icon">
                <v-icon>{{ severidadIcon(item) }}</v-icon>
              </div>
              <div>
                <div class="severidades-row__name-title">{{ (item.Nombre || '').toUpperCase() }}</div>
                <span class="severidades-row__name-chip">{{ severidadSublabel(item) }}</span>
              </div>
            </div>

            <!-- Descripcion -->
            <div class="severidades-row__field">
              <v-textarea
                v-model="item.Descripcion"
                outlined
                dense
                rows="2"
                no-resize
                hide-details
                maxlength="120"
                placeholder="Describe esta severidad..."
              ></v-textarea>
              <div class="severidades-row__field-hint">Max. 120 caracteres</div>
            </div>

            <!-- Color -->
            <div class="severidades-row__field">
              <v-select
                v-model="item.ColorHex"
                :items="colorPalette"
                item-text="name"
                item-value="hex"
                outlined
                dense
                hide-details
              >
                <template v-slot:selection="{ item: c }">
                  <span class="severidades-row__color-swatch" :style="{ background: c.hex }"></span>
                  {{ c.name }}
                </template>
                <template v-slot:item="{ item: c }">
                  <span class="severidades-row__color-swatch" :style="{ background: c.hex }"></span>
                  <span class="ml-2">{{ c.name }}</span>
                </template>
              </v-select>
              <div class="severidades-row__field-hint">{{ item.ColorHex }}</div>
            </div>

            <!-- Prioridad -->
            <div class="severidades-row__field">
              <v-select
                v-model="item.Prioridad"
                :items="prioridadesPermitidas"
                outlined
                dense
                hide-details
              >
                <template v-slot:selection="{ item: p }">
                  <v-icon left small :color="severidadTone({ Codigo: item.Codigo }) === 'alarma' ? '#dc2626' : '#f59e0b'">mdi-flag</v-icon>
                  {{ severidadPriorityNumber(p) }} - {{ severidadPriorityLabel(p) }}
                </template>
                <template v-slot:item="{ item: p }">
                  {{ severidadPriorityNumber(p) }} - {{ severidadPriorityLabel(p) }}
                </template>
              </v-select>
              <div
                class="severidades-row__priority-note"
                :class="severidadTone(item) === 'alarma' ? 'severidades-row__priority-note--alarma' : 'severidades-row__priority-note--adv'"
              >
                {{ severidadPriorityNote(item.Prioridad) }}
              </div>
            </div>

            <!-- Respeta horario -->
            <div class="severidades-row__field">
              <div class="severidades-row__horario">
                <v-switch
                  v-model="item.RespetaHorario"
                  color="success"
                  inset
                  hide-details
                  class="ma-0 pa-0"
                ></v-switch>
                <span class="severidades-row__horario-label">{{ item.RespetaHorario ? 'Si' : 'No' }}</span>
              </div>
              <div class="severidades-row__field-hint">{{ severidadHorarioNote(item) }}</div>
            </div>

            <!-- Estado -->
            <div class="severidades-row__field severidades-row__field--center">
              <v-chip
                small
                :color="item.Activo ? 'green lighten-4' : 'grey lighten-3'"
                :text-color="item.Activo ? 'green darken-2' : 'blue-grey darken-1'"
                class="severidades-row__estado-chip"
              >
                {{ item.Activo ? 'Activo' : 'Inactivo' }}
              </v-chip>
            </div>
          </article>
        </div>
      </v-dialog>

      <v-dialog v-if="alarmStep !== 1 && alarmStep !== 4" v-model="dialogNotificationCatalog" persistent max-width="820px">
        <v-card class="uni-dlg" :style="uniDlgStyle('#2e7d32')">
          <header class="uni-dlg__header">
            <div class="uni-dlg__title-row">
              <div class="uni-dlg__icon">
                <v-icon>{{ currentCatalogConfig.dialogIcon || currentCatalogConfig.icon }}</v-icon>
              </div>
              <div class="uni-dlg__title-block">
                <span class="uni-dlg__eyebrow">Medios de notificacion</span>
                <h2 class="uni-dlg__title">
                  {{ notificationCatalogForm.id ? 'Editar medio de notificacion' : 'Nuevo medio de notificacion' }}
                </h2>
                <p class="uni-dlg__subtitle">{{ currentCatalogConfig.dialogSubtitle }}</p>
              </div>
            </div>
            <div class="uni-dlg__actions">
              <button class="uni-dlg__btn uni-dlg__btn--ghost" :disabled="currentCatalogState.saving" @click="closeCatalogDialog">
                <v-icon small>mdi-close</v-icon>
                Cancelar
              </button>
              <button class="uni-dlg__btn uni-dlg__btn--primary" :disabled="currentCatalogState.saving" @click="saveCatalogItem">
                <v-icon small>{{ notificationCatalogForm.id ? 'mdi-content-save-edit-outline' : 'mdi-content-save-outline' }}</v-icon>
                {{ currentCatalogState.saving ? 'Guardando...' : (notificationCatalogForm.id ? 'Actualizar' : 'Guardar') }}
              </button>
            </div>
          </header>
          <v-card-text class="uni-dlg__body">
            <v-form ref="notificationCatalogFormRef" lazy-validation>
              <section class="uni-dlg__section">
                <div class="uni-dlg__step">
                  <span class="uni-dlg__step-num">1</span>
                  <span class="uni-dlg__step-label">Datos del medio</span>
                </div>
                <p class="uni-dlg__hint">Identifica el medio y describe en pocas palabras como se usara.</p>
                <v-row dense>
                  <v-col cols="12" sm="6">
                    <label class="uni-dlg__field-label">Nombre <span class="uni-dlg__required">*</span></label>
                    <v-text-field
                      v-model.trim="notificationCatalogForm.Nombre"
                      :placeholder="currentCatalogConfig.exampleNombre"
                      outlined dense hide-details="auto"
                      :error-messages="notificationCatalogErrors.Nombre"
                      @focus="notificationCatalogErrors.Nombre = null"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <label class="uni-dlg__field-label">Clave <span class="uni-dlg__required">*</span></label>
                    <v-text-field
                      v-model.trim="notificationCatalogForm.Codigo"
                      :placeholder="currentCatalogConfig.exampleCodigo"
                      outlined dense hide-details="auto"
                      :error-messages="notificationCatalogErrors.Codigo"
                      @focus="notificationCatalogErrors.Codigo = null"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12">
                    <label class="uni-dlg__field-label">Descripcion</label>
                    <v-textarea
                      v-model.trim="notificationCatalogForm.Descripcion"
                      :placeholder="currentCatalogConfig.exampleDescripcion"
                      outlined dense rows="2" hide-details="auto"
                    ></v-textarea>
                  </v-col>
                </v-row>
              </section>

              <section class="uni-dlg__section">
                <div class="uni-dlg__step">
                  <span class="uni-dlg__step-num">2</span>
                  <span class="uni-dlg__step-label">Canal de entrega</span>
                </div>
                <p class="uni-dlg__hint">Selecciona el canal por el cual se enviaran las notificaciones.</p>
                <v-row dense>
                  <v-col cols="12" sm="6">
                    <label class="uni-dlg__field-label">Canal <span class="uni-dlg__required">*</span></label>
                    <v-select
                      v-model="notificationCatalogForm.Canal"
                      :items="canalesPermitidos"
                      placeholder="Ejemplo: EMAIL"
                      outlined dense hide-details="auto"
                      :error-messages="notificationCatalogErrors.Canal"
                      @focus="notificationCatalogErrors.Canal = null"
                    ></v-select>
                  </v-col>
                </v-row>
              </section>

              <section class="uni-dlg__summary">
                <div class="uni-dlg__summary-title">
                  <v-icon small>mdi-clipboard-check-outline</v-icon>
                  Resumen del medio
                </div>
                <p class="uni-dlg__summary-hint">Verifica los datos antes de guardar.</p>
                <div class="uni-dlg__summary-grid">
                  <div class="uni-dlg__summary-cell">
                    <v-icon>mdi-tag-text-outline</v-icon>
                    <div class="uni-dlg__summary-label">Nombre</div>
                    <div class="uni-dlg__summary-value" :class="{ 'uni-dlg__summary-value--empty': !notificationCatalogForm.Nombre }">
                      {{ notificationCatalogForm.Nombre || 'Sin nombre' }}
                    </div>
                  </div>
                  <div class="uni-dlg__summary-cell">
                    <v-icon>mdi-pound</v-icon>
                    <div class="uni-dlg__summary-label">Clave</div>
                    <div class="uni-dlg__summary-value" :class="{ 'uni-dlg__summary-value--empty': !notificationCatalogForm.Codigo }">
                      {{ notificationCatalogForm.Codigo || 'Sin clave' }}
                    </div>
                  </div>
                  <div class="uni-dlg__summary-cell">
                    <v-icon>mdi-cellphone-message</v-icon>
                    <div class="uni-dlg__summary-label">Canal</div>
                    <div class="uni-dlg__summary-value" :class="{ 'uni-dlg__summary-value--empty': !notificationCatalogForm.Canal }">
                      {{ notificationCatalogForm.Canal || 'No seleccionado' }}
                    </div>
                  </div>
                </div>
              </section>
            </v-form>
          </v-card-text>
          <div class="uni-dlg__footer-note">
            <v-icon small>mdi-information-outline</v-icon>
            Los campos con <span class="uni-dlg__required">*</span> son obligatorios.
          </div>
        </v-card>
      </v-dialog>

      <v-dialog v-if="alarmStep === 4" v-model="dialogReglaMaquina" persistent max-width="1280px">
        <v-card class="rule-dialog uni-dlg" :style="uniDlgStyle('#ef6c00')">
          <header class="uni-dlg__header">
            <div class="uni-dlg__title-row">
              <div class="uni-dlg__icon">
                <v-icon>mdi-sitemap</v-icon>
              </div>
              <div class="uni-dlg__title-block">
                <span class="uni-dlg__eyebrow">Reglas de notificacion</span>
                <h2 class="uni-dlg__title">
                  {{ reglaMaquinaForm.IdReglaNotificacion ? 'Editar regla de notificacion' : 'Nueva regla de notificacion' }}
                </h2>
                <p class="uni-dlg__subtitle">
                  Define cuando, donde y como se generan las notificaciones por sensor o por maquina.
                </p>
              </div>
            </div>
            <div class="uni-dlg__actions">
              <button class="uni-dlg__btn uni-dlg__btn--ghost" :disabled="savingReglaMaquina" @click="closeReglaMaquinaDialog">
                <v-icon small>mdi-close</v-icon>
                Cancelar
              </button>
              <button class="uni-dlg__btn uni-dlg__btn--primary" :disabled="savingReglaMaquina" @click="saveReglaMaquina">
                <v-icon small>{{ reglaMaquinaForm.IdReglaNotificacion ? 'mdi-content-save-edit-outline' : 'mdi-content-save-outline' }}</v-icon>
                {{ savingReglaMaquina ? 'Guardando...' : (reglaMaquinaForm.IdReglaNotificacion ? 'Actualizar' : 'Guardar') }}
              </button>
            </div>
          </header>
          <v-card-text class="rule-dialog__body">
            <div class="rule-dialog__content">
            <v-form ref="reglaMaquinaFormRef" lazy-validation class="rule-dialog__form">
              <v-row dense>
                <v-col cols="12">
                  <div class="uni-dlg__step">
                    <span class="uni-dlg__step-num">1</span>
                    <span class="uni-dlg__step-label">Informacion general</span>
                  </div>
                  <p class="uni-dlg__hint">Identifica la regla con un nombre claro y una clave unica.</p>
                </v-col>
                <v-col cols="12" sm="6">
                  <label class="uni-dlg__field-label">Nombre de la regla <span class="uni-dlg__required">*</span></label>
                  <v-text-field
                    v-model.trim="reglaMaquinaForm.Nombre"
                    placeholder="Ej. Temperatura alta sensor 01"
                    outlined dense hide-details="auto"
                    :error-messages="reglaMaquinaErrors.Nombre"
                    @focus="reglaMaquinaErrors.Nombre = null"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <label class="uni-dlg__field-label">Clave <span class="uni-dlg__required">*</span></label>
                  <v-text-field
                    v-model.trim="reglaMaquinaForm.Codigo"
                    placeholder="Ej. TEMP_ALTA_SENSOR_01"
                    outlined dense hide-details="auto"
                    :error-messages="reglaMaquinaErrors.Codigo"
                    @focus="reglaMaquinaErrors.Codigo = null"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <label class="uni-dlg__field-label">Descripcion</label>
                  <v-textarea
                    v-model.trim="reglaMaquinaForm.Descripcion"
                    placeholder="Ej. Notifica cuando el sensor sale del rango configurado."
                    outlined dense rows="2" hide-details="auto"
                  ></v-textarea>
                </v-col>
                <v-col cols="12">
                  <div class="alarm-rule-type">
                    <div class="uni-dlg__step mt-5">
                      <span class="uni-dlg__step-num">2</span>
                      <span class="uni-dlg__step-label">Origen de la alerta</span>
                    </div>
                    <p class="uni-dlg__hint">Selecciona el origen que generara la alerta.</p>
                    <div class="alarm-rule-type__grid">
                      <button
                        type="button"
                        class="alarm-rule-type__card alarm-rule-type__card--maquina"
                        :class="{ 'alarm-rule-type__card--active': reglaMaquinaForm.TipoAlerta === 'MAQUINA' }"
                        @click="selectTipoAlertaRegla('MAQUINA')"
                      >
                        <div class="alarm-rule-type__main">
                          <div class="alarm-rule-type__icon-wrap">
                            <v-icon class="alarm-rule-type__icon">mdi-robot-industrial</v-icon>
                          </div>
                          <div>
                            <div class="alarm-rule-type__title">Por maquina</div>
                            <div class="alarm-rule-type__description">Configura reglas para alertas generales de la maquina.</div>
                          </div>
                        </div>
                        <span class="alarm-rule-type__radio">
                          <span v-if="reglaMaquinaForm.TipoAlerta === 'MAQUINA'"></span>
                        </span>
                      </button>
                      <button
                        type="button"
                        class="alarm-rule-type__card alarm-rule-type__card--sensor"
                        :class="{ 'alarm-rule-type__card--active': reglaMaquinaForm.TipoAlerta === 'SENSOR' }"
                        @click="selectTipoAlertaRegla('SENSOR')"
                      >
                        <div class="alarm-rule-type__main">
                          <div class="alarm-rule-type__icon-wrap">
                            <v-icon class="alarm-rule-type__icon">mdi-access-point</v-icon>
                          </div>
                          <div>
                            <div class="alarm-rule-type__title">Por sensor / variable</div>
                            <div class="alarm-rule-type__description">Configura reglas para un sensor y variable especificos.</div>
                          </div>
                        </div>
                        <span class="alarm-rule-type__radio">
                          <span v-if="reglaMaquinaForm.TipoAlerta === 'SENSOR'"></span>
                        </span>
                      </button>
                    </div>
                  </div>
                </v-col>
                <v-col v-if="reglaMaquinaForm.TipoAlerta === 'SENSOR'" cols="12">
                  <v-autocomplete
                    v-model="reglaMaquinaForm.IdMaquinaSensorVariable"
                    :items="maquinaSensorVariables"
                    :item-text="maquinaSensorVariableLabel"
                    item-value="idMaquinaSensorVariable"
                    label="Máquina / Sensor / Variable"
                    outlined
                    dense
                    hide-details="auto"
                    :loading="loadingMaquinaSensorVariables"
                    :disabled="loadingMaquinaSensorVariables"
                    :error-messages="reglaMaquinaErrors.IdMaquinaSensorVariable"
                    @focus="reglaMaquinaErrors.IdMaquinaSensorVariable = null"
                  ></v-autocomplete>
                </v-col>
                <v-col v-if="reglaMaquinaForm.TipoAlerta === 'MAQUINA'" cols="12" sm="6">
                  <v-autocomplete
                    v-model="reglaMaquinaForm.IdMaquinaIns"
                    :items="maquinasIns"
                    :item-text="maquinaLabel"
                    item-value="idMaquinaIns"
                    label="Maquina"
                    outlined
                    dense
                    hide-details="auto"
                    :disabled="reglaMaquinaForm.TipoFallaMaquina === 'TOKEN_INVALIDO'"
                    :error-messages="reglaMaquinaErrors.IdMaquinaIns"
                    @focus="reglaMaquinaErrors.IdMaquinaIns = null"
                  ></v-autocomplete>
                </v-col>
                <v-col v-if="reglaMaquinaForm.TipoAlerta === 'MAQUINA'" cols="12" sm="6">
                  <v-select
                    v-model="reglaMaquinaForm.TipoFallaMaquina"
                    :items="tipoFallaMaquina"
                    item-text="label"
                    item-value="value"
                    label="Tipo de falla"
                    outlined
                    dense
                    hide-details="auto"
                    :error-messages="reglaMaquinaErrors.TipoFallaMaquina"
                    @focus="reglaMaquinaErrors.TipoFallaMaquina = null"
                    @change="onTipoFallaMaquinaChange"
                  ></v-select>
                </v-col>
                <v-col cols="12">
                  <div class="uni-dlg__step mt-5">
                    <span class="uni-dlg__step-num">3</span>
                    <span class="uni-dlg__step-label">Configuracion de la notificacion</span>
                  </div>
                  <p class="uni-dlg__hint">Define el nivel de alerta, el medio por el que se notificara y la programacion.</p>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-autocomplete
                    v-model="reglaMaquinaForm.IdNivelAlerta"
                    :items="nivelesAlerta"
                    item-text="Nombre"
                    item-value="IdNivelAlerta"
                    label="Nivel de alerta"
                    outlined
                    dense
                    hide-details="auto"
                    :error-messages="reglaMaquinaErrors.IdNivelAlerta"
                    @focus="reglaMaquinaErrors.IdNivelAlerta = null"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-autocomplete
                    v-model="reglaMaquinaForm.IdMedioNotificacion"
                    :items="mediosNotificacion"
                    item-text="Nombre"
                    item-value="IdMedioNotificacion"
                    label="Medio de notificación"
                    outlined
                    dense
                    hide-details="auto"
                    :error-messages="reglaMaquinaErrors.IdMedioNotificacion"
                    @focus="reglaMaquinaErrors.IdMedioNotificacion = null"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-autocomplete
                    v-model="reglaMaquinaForm.IdProgramacionNotificacion"
                    :items="programacionesNotificacion"
                    :item-text="programacionItemText"
                    item-value="IdProgramacionNotificacion"
                    label="Programacion"
                    outlined
                    dense
                    hide-details="auto"
                    :loading="notificationCatalogState.programaciones.loading"
                    :error-messages="reglaMaquinaErrors.IdProgramacionNotificacion"
                    @change="applyProgramacionRegla"
                    @focus="reglaMaquinaErrors.IdProgramacionNotificacion = null"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-form>
            <aside class="rule-summary">
              <h3 class="rule-summary__title">RESUMEN DE LA REGLA</h3>
              <p class="rule-summary__subtitle">Revisa la configuracion de la nueva regla antes de guardarla.</p>

              <div class="rule-summary__item">
                <v-icon>mdi-flash-outline</v-icon>
                <div>
                  <strong>Origen de la alerta</strong>
                  <span class="rule-summary__accent">{{ reglaResumenOrigen }}</span>
                </div>
              </div>
              <div class="rule-summary__item">
                <v-icon>mdi-access-point</v-icon>
                <div>
                  <strong>Maquina - Sensor - Variable</strong>
                  <span>{{ reglaResumenObjetivo }}</span>
                </div>
              </div>
              <div class="rule-summary__item">
                <v-icon>mdi-alert-outline</v-icon>
                <div>
                  <strong>Nivel de alerta</strong>
                  <span>{{ reglaResumenNivel }}</span>
                </div>
              </div>
              <div class="rule-summary__item">
                <v-icon>mdi-email-outline</v-icon>
                <div>
                  <strong>Medio de notificacion</strong>
                  <span>{{ reglaResumenMedio }}</span>
                </div>
              </div>
              <div class="rule-summary__item">
                <v-icon>mdi-calendar-clock</v-icon>
                <div>
                  <strong>Programacion</strong>
                  <span>{{ reglaResumenProgramacion }}</span>
                </div>
              </div>
              <div class="rule-summary__note">
                <v-icon small>mdi-information-outline</v-icon>
                Completa todos los campos obligatorios para poder guardar la regla.
              </div>
            </aside>
            </div>
          </v-card-text>
          <v-card-actions v-if="false" class="alarm-dialog-card__actions">
            <v-btn text color="blue-grey darken-1" @click="closeReglaMaquinaDialog" :disabled="savingReglaMaquina">
              Cancelar
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              :loading="savingReglaMaquina"
              :disabled="savingReglaMaquina"
              @click="saveReglaMaquina"
            >
              <v-icon left small>{{ reglaMaquinaForm.IdReglaNotificacion ? 'mdi-content-save-edit-outline' : 'mdi-plus-circle-outline' }}</v-icon>
              {{ reglaMaquinaForm.IdReglaNotificacion ? 'Actualizar' : 'Crear' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="dialogAsignarUsuariosRegla" persistent max-width="1100px">
        <v-card class="alarm-dialog-card alarm-dialog-card--featured">
          <div class="alarm-dialog-hero">
            <div class="alarm-dialog-hero__icon">
              <v-icon>mdi-account-multiple-plus-outline</v-icon>
            </div>
            <div class="alarm-dialog-hero__copy">
              <div class="alarm-dialog-hero__step">Reglas - Usuarios</div>
              <div class="alarm-dialog-card__title">Asignar usuarios</div>
              <div class="alarm-dialog-card__subtitle">{{ asignarUsuariosReglaTitulo }}</div>
            </div>
          </div>
          <v-card-text class="alarm-dialog-card__body">
            <div class="alarm-user-picker" :class="{ 'alarm-user-picker--loading': loadingAsignarUsuariosRegla }">
              <div class="alarm-user-picker__panel">
                <div class="alarm-user-picker__title">
                  Usuarios disponibles ({{ usuariosDisponiblesRegla.length }})
                </div>
                <v-text-field
                  v-model="asignarUsuariosDisponiblesSearch"
                  prepend-inner-icon="mdi-magnify"
                  placeholder="Buscar usuario..."
                  outlined
                  dense
                  hide-details
                  class="alarm-user-picker__search"
                  :disabled="loadingAsignarUsuariosRegla || savingAsignarUsuariosRegla"
                ></v-text-field>
                <div class="alarm-user-picker__list">
                  <button
                    v-for="usuario in usuariosDisponiblesFiltradosRegla"
                    :key="`disponible-${usuario.idUsuario}`"
                    type="button"
                    class="alarm-user-picker__row"
                    :class="{ 'alarm-user-picker__row--checked': asignarUsuariosSeleccionDisponibles.includes(usuario.idUsuario) }"
                    :disabled="savingAsignarUsuariosRegla"
                    @click="toggleUsuarioDisponibleRegla(usuario.idUsuario)"
                  >
                    <span class="alarm-user-picker__avatar">{{ usuarioIniciales(usuario) }}</span>
                    <span class="alarm-user-picker__name">{{ usuarioLabel(usuario) }}</span>
                    <span class="alarm-user-picker__role">{{ usuarioRol(usuario) }}</span>
                    <span class="alarm-user-picker__check">
                      <v-icon v-if="asignarUsuariosSeleccionDisponibles.includes(usuario.idUsuario)" small color="primary">mdi-check</v-icon>
                    </span>
                  </button>
                  <div v-if="!usuariosDisponiblesFiltradosRegla.length" class="alarm-user-picker__empty">
                    No hay usuarios disponibles.
                  </div>
                </div>
              </div>

              <div class="alarm-user-picker__actions">
                <v-btn color="primary" depressed :disabled="!asignarUsuariosSeleccionDisponibles.length || savingAsignarUsuariosRegla" @click="agregarUsuariosSeleccionadosRegla">
                  <v-icon left small>mdi-chevron-right</v-icon>
                  Agregar
                </v-btn>
                <v-btn color="primary" depressed :disabled="!usuariosDisponiblesRegla.length || savingAsignarUsuariosRegla" @click="agregarTodosUsuariosRegla">
                  <v-icon left small>mdi-chevron-double-right</v-icon>
                  Agregar todos
                </v-btn>
                <v-btn outlined color="primary" :disabled="!asignarUsuariosSeleccionAsignados.length || savingAsignarUsuariosRegla" @click="quitarUsuariosSeleccionadosRegla">
                  <v-icon left small>mdi-chevron-left</v-icon>
                  Quitar
                </v-btn>
                <v-btn outlined color="primary" :disabled="!asignarUsuariosReglaForm.IdsUsuarios.length || savingAsignarUsuariosRegla" @click="quitarTodosUsuariosRegla">
                  <v-icon left small>mdi-chevron-double-left</v-icon>
                  Quitar todos
                </v-btn>
              </div>

              <div class="alarm-user-picker__panel">
                <div class="alarm-user-picker__title">
                  Usuarios seleccionados ({{ usuariosSeleccionadosRegla.length }})
                </div>
                <div class="alarm-user-picker__list alarm-user-picker__list--selected">
                  <button
                    v-for="usuario in usuariosSeleccionadosRegla"
                    :key="`seleccionado-${usuario.idUsuario}`"
                    type="button"
                    class="alarm-user-picker__row"
                    :class="{ 'alarm-user-picker__row--checked': asignarUsuariosSeleccionAsignados.includes(usuario.idUsuario) }"
                    :disabled="savingAsignarUsuariosRegla"
                    @click="toggleUsuarioAsignadoRegla(usuario.idUsuario)"
                  >
                    <span class="alarm-user-picker__avatar">{{ usuarioIniciales(usuario) }}</span>
                    <span class="alarm-user-picker__name">{{ usuarioLabel(usuario) }}</span>
                    <span class="alarm-user-picker__role">{{ usuarioRol(usuario) }}</span>
                    <v-btn icon small color="error" @click.stop="quitarUsuarioRegla(usuario.idUsuario)" :disabled="savingAsignarUsuariosRegla">
                      <v-icon small>mdi-delete-outline</v-icon>
                    </v-btn>
                  </button>
                  <div v-if="!usuariosSeleccionadosRegla.length" class="alarm-user-picker__empty">
                    No hay usuarios seleccionados.
                  </div>
                </div>
                <div class="alarm-user-picker__note">
                  <v-icon small color="primary">mdi-information-outline</v-icon>
                  Estos usuarios recibiran las notificaciones segun la configuracion de la regla.
                </div>
              </div>
            </div>
          </v-card-text>
          <v-card-actions class="alarm-dialog-card__actions">
            <v-btn text color="blue-grey darken-1" @click="closeAsignarUsuariosRegla" :disabled="savingAsignarUsuariosRegla">
              Cancelar
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              :loading="savingAsignarUsuariosRegla"
              :disabled="savingAsignarUsuariosRegla"
              @click="saveAsignarUsuariosRegla"
            >
              <v-icon left small>mdi-content-save-outline</v-icon>
              Guardar usuarios
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-snackbar v-model="nivelAlertaSnackbar.show" top :color="nivelAlertaSnackbar.color">
        <v-icon dark small class="mr-2">{{ nivelAlertaSnackbar.icon }}</v-icon>
        {{ nivelAlertaSnackbar.text }}
        <v-btn text dark @click="nivelAlertaSnackbar.show = false">Cerrar</v-btn>
      </v-snackbar>
    </div>

    <div v-else>
    <v-dialog v-model="dialogTiempo" persistent max-width="600px">
      <v-card>
        <v-card-title
          ><span class="text-h5">Alerta de Inactividad</span></v-card-title
        >
        <v-card-text class="mt-3">
          <v-autocomplete
            v-model="tiempoSinActividad"
            :items="bloques"
            label="Tiempo estimado"
            item-text="text"
            item-value="value"
            hide-details
            dense
          ></v-autocomplete>
          <p v-if="errorTime" style="color: #D50000">Selecciona un item</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeDialosTime">
            Cerrar
          </v-btn>
          <v-btn color="red darken-1" text @click="EditTimeClose">
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogMaquinaInstancia" persistent max-width="600px">
      <v-card>
        <v-card-title
          ><span class="text-h5">Configurar Alerta</span></v-card-title
        >
        <v-card-text class="mt-3">
          <v-row>
            <v-col cols="12" sm="12" md="12" xl="12">
              <v-autocomplete
                v-model="tiempoSinActividad"
                label="Agrupador Tiempo"
                :items="bloques"
                item-text="text"
                item-value="value"
                hide-details
                dense
                clearable
              ></v-autocomplete>
              <p
                v-if="erroresMaquinasIns.tiempoSinActividad"
                style="color: #D50000"
                :key="erroresMaquinasIns.tiempoSinActividad"
              >
                Selecciona un tiempo
              </p>
            </v-col>
            <v-col cols="12" sm="12" md="12" xl="12">
              <v-select
                v-model="operacion"
                label="Operación"
                :items="itemsOperacion"
                item-text="text"
                item-value="value"
                hide-details
                dense
                clearable
              ></v-select>
              <p v-if="erroresMaquinasIns.operacion" style="color: #D50000">
                Selecciona un tipo de Operación
              </p>
            </v-col>
            <v-col
              :cols="
                comparador == null
                  ? 6
                  : comparador == 7 || comparador == 8
                  ? 4
                  : 6
              "
              sm="12"
              :md="
                comparador == null
                  ? 6
                  : comparador == 7 || comparador == 8
                  ? 4
                  : 6
              "
              xl="12"
            >
              <v-select
                v-model="comparador"
                label="Comparador"
                :items="itemsComparacion"
                item-text="text"
                item-value="value"
                hide-details
                dense
                clearable
              ></v-select>
              <p v-if="erroresMaquinasIns.comparador" style="color: #D50000">
                Selecciona un comparador
              </p>
            </v-col>
            <v-col
              :cols="
                comparador == null
                  ? 6
                  : comparador == 7 || comparador == 8
                  ? 4
                  : 6
              "
              sm="12"
              :md="
                comparador == null
                  ? 6
                  : comparador == 7 || comparador == 8
                  ? 4
                  : 6
              "
              xl="12"
            >
              <v-text-field
                v-model="valor"
                label="Valor"
                hide-details
                dense
              ></v-text-field>
              <p v-if="erroresMaquinasIns.valor" style="color: #D50000">
                Ingrese un valor
              </p>
            </v-col>
            <v-col
              v-if="comparador == 7 || comparador == 8"
              cols="4"
              sm="4"
              md="4"
              xl="4"
            >
              <v-text-field
                v-model="valor2"
                label="Valor 2"
                hide-details
                dense
              ></v-text-field>
              <p v-if="erroresMaquinasIns.valor2" style="color: #D50000">
                Ingrese un valor
              </p>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="CloseMaquinaInstancia">
            Cerrar
          </v-btn>
          <v-btn color="red darken-1" text @click="editMaquinaInstanciaClose">
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-data-table
      :headers="headers"
      :items="itemsData"
      :loading="loadingTable"
      loading-text="Cargando... Espere, Por Favor."
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-dialog v-model="dialog" persistent max-width="800px">
            <template v-slot:activator="{ on, attrs }">
              <v-btn color="primary" dark class="mb-2" v-bind="attrs" v-on="on">
                Nueva alerta
              </v-btn>
            </template>
            <v-card>
              <v-card-title>
                <span class="text-h5">{{ formTitle }}</span>
              </v-card-title>

              <v-card-text>
                <v-container>
                  <v-row>
                    <v-col cols="4" sm="4" md="4">
                      <v-autocomplete
                        v-model="editedItem.idMaquinaIns"
                        :items="maquinasIns"
                        label="Máquina"
                        item-value="idMaquinaIns"
                        :item-text="maquinaLabel"
                        hide-details
                        dense
                        @change="getKeys()"
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="4" sm="4" md="4">
                      <v-autocomplete
                        v-model="editedItem.clave"
                        :items="keys"
                        label="Seleccione los Indicadores a Mostrar"
                        
            		  
									clearable
									hide-details
                        dense
                        :loading="loadingClaves"
                        :disabled="loadingClaves"
                      >
                        <template v-slot:item="data">
                          <v-checkbox
                            :label="data.item"
                            hide-details
                            dense
                            v-model="data.attrs.inputValue"
                          >
                          </v-checkbox>
                          <!-- <v-spacer></v-spacer>
                          <v-list-item-action @click.stop>
                            <v-btn
                              icon
                              color="grey"
                              @click.stop.prevent="editarClave(data.item)"
                            >
                              <v-icon>{{
                                editing !== item ? "mdi-pencil" : "mdi-check"
                              }}</v-icon>
                            </v-btn>
                          </v-list-item-action> -->
                        </template>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="4" sm="4" md="4">
                      <v-text-field
                        v-model="editedItem.unidad"
                        label="Unidad"
                        hide-details
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col cols="6" sm="6" md="6">
                      <v-autocomplete
                        v-model="editedItem.metodosNotificacion"
                        label="Métodos de Notificación"
                        :items="metodosNotificacion"
                        item-value="id"
                        item-text="value"
                        multiple
                        chips
                        small-chips
                        hide-details
                        dense
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="6" sm="6" md="6">
                      <v-autocomplete
                        v-model="editedItem.idsUsuarios"
                        :items="usuarios"
                        item-text="nombre"
                        item-value="idUsuario"
                        label="Usuario"
                        hide-details
                        clearable
                        multiple
                        chips
                        small-chips
                        dense
                      ></v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="12" md="12">
                      <v-divider></v-divider>
                      <span class="text-h6">Alerta de Inactividad</span>
                      <v-data-table
                        dense
                        :headers="headersTiempo"
                        :items="editedItem.tiempoInactividad"
                        :items-per-page="5"
                        :sort-by="['tiempoNoActividad']"
                        :sort-desc="false"
                        class="mt-3"
                        hide-default-footer
                      >
                        <template v-slot:[`item.tiempoNoActividad`]="{ item }">
                          <span v-if="item.tiempoNoActividad == null"></span>
                          <span v-else>{{
                            secondsToMinutes(item.tiempoNoActividad)
                          }}</span>
                        </template>
                        <template v-slot:[`item.actions`]="{ item }">
                          <v-row>
                            <v-col cols="6" md="6" sm="6" xl="6">
                              <v-tooltip
                                bottom
                                color="black"
                                class="white--text"
                              >
                                <template v-slot:activator="{ on }">
                                  <v-icon
                                    v-on="on"
                                    small
                                    @click="showDialogTime(item)"
                                  >
                                    mdi-pencil
                                  </v-icon>
                                </template>
                                <span class="white--text">Editar</span>
                              </v-tooltip>
                            </v-col>
                            <v-col cols="6" md="6" sm="6" xl="6">
                              <v-tooltip
                                bottom
                                color="black"
                                class="white--text"
                              >
                                <template v-slot:activator="{ on }">
                                  <v-icon
                                    v-on="on"
                                    small
                                    @click="DeleteTime(item)"
                                  >
                                    mdi-delete
                                  </v-icon>
                                </template>
                                <span class="white--text">Eliminar</span>
                              </v-tooltip>
                            </v-col>
                          </v-row>
                        </template>
                      </v-data-table>
                      <div class="text-center">
                        <v-btn
                          x-small
                          color="primary"
                          class="my-2"
                          outlined
                          @click="agregarTiempo"
                          >Agregar Alerta de Inactividad</v-btn
                        >
                      </div>
                    </v-col>
                    <v-col cols="12" sm="12" md="12">
                      <v-divider></v-divider>
                      <span class="text-h6">Alerta de Valor</span>
                      <v-data-table
                        dense
                        :headers="headersMaquinaInstancia"
                        :items="editedItem.alertaValor"
                        :items-per-page="5"
                        :sort-by="['agrupadorTiempo']"
                        :sort-desc="false"
                        class="mt-3"
                        hide-default-footer
                      >
                        <template v-slot:[`item.agrupadorTiempo`]="{ item }">
                          <span v-if="item.agrupadorTiempo != null">{{
                            secondsToMinutes(item.agrupadorTiempo)
                          }}</span>
                        </template>

                        <template v-slot:[`item.operacion`]="{ item }">
                          <span v-if="item.operacion == 1">Suma</span>
                          <span v-if="item.operacion == 2">Promedio</span>
                          <span v-if="item.operacion == 3">Maximo</span>
                          <span v-if="item.operacion == 4">Minimo</span>
                          <span v-if="item.operacion == 5">Conteo</span>
                        </template>

                        <template v-slot:[`item.validacion`]="{ item }">
                          <div
                            v-if="
                              item.valor === null &&
                                item.comparador === null &&
                                item.valor2 === null
                            "
                          >
                            <span>N/A</span>
                          </div>
                          <div v-else>
                            <span v-if="item.comparador != null">
                              <p v-if="item.comparador == 1">
                                {{ "El valor es < Menor a " + item.valor }}
                              </p>
                              <p v-if="item.comparador == 2">
                                {{
                                  "El valor es <= Menor o Igual a " + item.valor
                                }}
                              </p>
                              <p v-if="item.comparador == 3">
                                {{ "El valor es = Igual a " + item.valor }}
                              </p>
                              <p v-if="item.comparador == 4">
                                {{
                                  "El valor es != Diferente de " + item.valor
                                }}
                              </p>
                              <p v-if="item.comparador == 5">
                                {{ "El valor es > Mayor que " + item.valor }}
                              </p>
                              <p v-if="item.comparador == 6">
                                {{
                                  "El valor es >= Mayor o igual que " +
                                    item.valor
                                }}
                              </p>
                              <p v-if="item.comparador == 7">
                                {{
                                  "El valor se encuentra <> Entre " +
                                    item.valor +
                                    " y " +
                                    item.valor
                                }}
                              </p>
                              <p v-if="item.comparador == 8">
                                {{
                                  "El valor !<> No esta Entre " +
                                    item.valor +
                                    " y " +
                                    item.valor2
                                }}
                              </p>
                            </span>
                          </div>
                        </template>

                        <template v-slot:[`item.actions`]="{ item }">
                          <v-row>
                            <v-col cols="6" md="6" sm="6" xl="6">
                              <v-tooltip
                                bottom
                                color="black"
                                class="white--text"
                              >
                                <template v-slot:activator="{ on }">
                                  <v-icon
                                    v-on="on"
                                    small
                                    @click="showDialogMaquinaInstancia(item)"
                                  >
                                    mdi-pencil
                                  </v-icon>
                                </template>
                                <span class="white--text">Editar</span>
                              </v-tooltip>
                            </v-col>
                            <v-col cols="6" md="6" sm="6" xl="6">
                              <v-tooltip
                                bottom
                                color="black"
                                class="white--text"
                              >
                                <template v-slot:activator="{ on }">
                                  <v-icon
                                    v-on="on"
                                    small
                                    @click="DeleteMaquinaInstancia(item)"
                                  >
                                    mdi-delete
                                  </v-icon>
                                </template>
                                <span class="white--text">Eliminar</span>
                              </v-tooltip>
                            </v-col>
                          </v-row>
                        </template>
                      </v-data-table>
                      <div class="text-center">
                        <v-btn
                          x-small
                          color="primary"
                          class="my-2"
                          outlined
                          @click="agregarMaquinaInstancia"
                          >Agregar Alerta de Valor</v-btn
                        >
                      </div>
                    </v-col>
                  </v-row>
                  <v-row v-if="errores.length > 0">
                    <ul>
                      <li
                        class="red--text"
                        v-for="(error, idx) in errores"
                        :key="'error' + idx"
                      >
                        {{ error }}
                      </li>
                    </ul>
                  </v-row>
                </v-container>
              </v-card-text>

              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="CancelarGrnal">
                  CANCELAR
                </v-btn>
                <v-btn color="red darken-1" text @click="save">
                  GUARDAR
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <v-dialog v-model="dialogDelete" persistent max-width="500px">
            <v-card>
              <v-card-title class="text-h6"
                >¿Está seguro que desea eliminar este registro?</v-card-title
              >
              <v-card-text
                >Esta acción no se puede revertir y será
                permanente.</v-card-text
              >
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="closeDelete"
                  >CANCELAR</v-btn
                >
                <v-btn color="red darken-1" text @click="deleteItemConfirm"
                  >ACEPTAR</v-btn
                >
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-toolbar>
      </template>

      <template v-slot:[`item.maquina`]="{ item }">
        <span>{{
          "[" + item.nombre + "]" + " - " + item.marca + " " + item.modelo
        }}</span>
      </template>

      <template v-slot:[`item.idsUsuarios`]="{ item }">
        <ul>
          <li v-for="(idUsuario, x) in item.idsUsuarios" :key="x">
            <span>{{ usuariosNombre(idUsuario) }}</span>
          </li>
        </ul>
      </template>
      <template v-slot:[`item.tiempoInactividad`]="{ item }">
        <span v-if="item.tiempoInactividad.length == 0"
          >Sin alertas configuradas</span
        >
        <span v-else-if="item.tiempoInactividad.length == 1">{{
          item.tiempoInactividad.length + " Alerta Configuradas"
        }}</span>
        <span v-else>{{
          item.tiempoInactividad.length + " Alertas Configuradas"
        }}</span>
      </template>
      <template v-slot:[`item.unidad`]="{ item }">
        <span v-if="item.unidad == null">N/A</span>
        <span v-else>{{ item.unidad }}</span>
      </template>
      <template v-slot:[`item.metodosNotificacion`]="{ item }">
        <ul>
          <li v-for="(ie, x) in item.metodosNotificacion" :key="x">
            <span v-if="ie == 1">Notificación de ManagrX</span>
            <span v-if="ie == 2">WebPush</span>
            <span v-if="ie == 3">Correo Electronico</span>
            <span v-if="ie == 4">Telegram</span>
          </li>
        </ul>
      </template>

      <template v-slot:[`item.alertaValor`]="{ item }">
        <span v-if="item.alertaValor.length == 0"
          >Sin alertas configuradas</span
        >
        <span v-else-if="item.alertaValor.length == 1">{{
          item.alertaValor.length + " Alerta Configuradas"
        }}</span>
        <span v-else>{{
          item.alertaValor.length + " Alertas Configuradas"
        }}</span>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-tooltip bottom color="black" class="white--text">
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" small class="mr-2" @click="editItem(item)">
              edit
            </v-icon>
          </template>
          <span class="white--text">Editar</span>
        </v-tooltip>
        <v-tooltip bottom color="black" class="white--text">
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" small @click="deleteItem(item)"> delete </v-icon>
          </template>
          <span class="white--text">Eliminar</span>
        </v-tooltip>
      </template>
      <template v-slot:no-data>
        <v-btn color="primary" @click="initialize">
          Refrescar
        </v-btn>
      </template>
    </v-data-table>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import { mapState } from "vuex";
export default {
  data() {
    return {
      alarmStep: 1,
      alarmSteps: [
        {
          key: "niveles",
          title: "Niveles",
          subtitle: "Prioridad y color",
          icon: "mdi-alert-circle",
          color: "#dc2626",
          locked: false,
        },
        {
          key: "medios",
          title: "Medios",
          subtitle: "Canales de notificacion",
          icon: "mdi-email",
          color: "#2563eb",
          locked: false,
        },
        {
          key: "programaciones",
          title: "Programacion",
          subtitle: "Frecuencia + horario",
          icon: "mdi-calendar-clock",
          color: "#8b5cf6",
          locked: false,
        },
        {
          key: "reglasMaquina",
          title: "Reglas",
          subtitle: "Sensor o maquina",
          icon: "mdi-bell-alert-outline",
          color: "#dc2626",
          locked: false,
        },
      ],
      prioridadesPermitidas: ["MEDIA", "ALTA", "CRITICA"],
      nivelesAlerta: [],
      nivelAlertaSearch: "",
      dialogNivelAlerta: false,
      dialogConfigurarSeveridades: false,
      severidadesConfig: [],
      savingSeveridades: false,
      loadingNivelesAlerta: false,
      savingNivelAlerta: false,
      deletingNivelAlertaId: null,
      headersNivelesAlerta: [
        { text: "Nombre del nivel", value: "Nombre", align: "left" },
        { text: "Clave", value: "Codigo", align: "left" },
        { text: "Descripcion", value: "Descripcion", align: "left" },
        { text: "Color", value: "ColorHex", align: "left" },
        { text: "Prioridad", value: "Prioridad", align: "center" },
        { text: "Respeta horario", value: "RespetaHorario", align: "center", sortable: false },
        { text: "Estado", value: "Activo", align: "center" },
        { text: "Acciones", value: "actions", align: "center", sortable: false },
      ],
      colorPalette: [
        { name: "Amarillo", hex: "#FFC107" },
        { name: "Naranja", hex: "#FF9800" },
        { name: "Rojo", hex: "#F44336" },
      ],
      nivelAlertaForm: {
        idNivelAlerta: null,
        Codigo: "",
        Nombre: "",
        Descripcion: "",
        ColorHex: "#FFC107",
        RespetaHorario: true,
        Prioridad: null,
      },
      defaultNivelAlertaForm: {
        idNivelAlerta: null,
        Codigo: "",
        Nombre: "",
        Descripcion: "",
        ColorHex: "#FFC107",
        RespetaHorario: true,
        Prioridad: null,
      },
      nivelAlertaErrors: {
        Codigo: null,
        Nombre: null,
        ColorHex: null,
        Prioridad: null,
      },
      nivelAlertaSnackbar: {
        show: false,
        text: "",
        color: "success",
        icon: "mdi-check-circle-outline",
      },
      canalesPermitidos: ["INTERNO", "EMAIL", "EXTERNO"],
      tiposFrecuenciaPermitidos: ["INMEDIATA", "PERIODICA", "UNICA"],
      dialogNotificationCatalog: false,
      notificationCatalogForm: {},
      notificationCatalogErrors: {},
      reglasMaquina: [],
      reglasMaquinaUsuarios: [],
      reglasMaquinaSearch: "",
      loadingReglasMaquina: false,
      savingReglaMaquina: false,
      deletingReglaMaquinaId: null,
      dialogReglaMaquina: false,
      dialogAsignarUsuariosRegla: false,
      loadingAsignarUsuariosRegla: false,
      savingAsignarUsuariosRegla: false,
      asignarUsuariosReglaTarget: null,
      asignarUsuariosReglaForm: {
        IdsUsuarios: [],
      },
      asignarUsuariosDisponiblesSearch: "",
      asignarUsuariosSeleccionDisponibles: [],
      asignarUsuariosSeleccionAsignados: [],
      asignarUsuariosReglaErrors: {
        IdsUsuarios: null,
      },
      maquinaSensorVariables: [],
      loadingMaquinaSensorVariables: false,
      mediosNotificacion: [],
      frecuenciasNotificacion: [],
      horariosNotificacion: [],
      loadingFrecuenciasNotificacion: false,
      loadingHorariosNotificacion: false,
      programacionFormView: false,
      programacionFormEditing: false,
      tipoFallaMaquina: [
        { label: "Sin comunicacion", value: "SIN_COMUNICACION" },
        { label: "Falta configuracion", value: "FALTA_CONFIGURACION" },
        { label: "Servicio no disponible", value: "SERVICIO_NO_DISPONIBLE" },
        { label: "Error de lectura", value: "ERROR_LECTURA" },
        { label: "Token invalido", value: "TOKEN_INVALIDO" },
      ],
      headersReglasMaquina: [
        { text: "Nombre", value: "Nombre", align: "left" },
        { text: "Clave", value: "Codigo", align: "left" },
        { text: "Tipo", value: "TipoAlerta", align: "center" },
        { text: "Objetivo", value: "Objetivo", align: "left" },
        { text: "Nivel", value: "NivelAlerta", align: "center" },
        { text: "Medio", value: "MedioNotificacion", align: "center" },
        { text: "Programacion", value: "ProgramacionNotificacion", align: "center" },
        { text: "Usuarios", value: "usuarios", align: "left", sortable: false },
        { text: "Estado", value: "Activo", align: "center" },
        { text: "Acciones", value: "actions", align: "center", sortable: false },
      ],
      reglaMaquinaForm: {
        IdReglaNotificacion: null,
        Codigo: "",
        Nombre: "",
        Descripcion: "",
        TipoAlerta: "SENSOR",
        IdMaquinaSensorVariable: null,
        IdMaquinaIns: null,
        TipoFallaMaquina: null,
        IdNivelAlerta: null,
        IdMedioNotificacion: null,
        IdProgramacionNotificacion: null,
        Activo: true,
        IdsUsuarios: [],
      },
      defaultReglaMaquinaForm: {
        IdReglaNotificacion: null,
        Codigo: "",
        Nombre: "",
        Descripcion: "",
        TipoAlerta: "SENSOR",
        IdMaquinaSensorVariable: null,
        IdMaquinaIns: null,
        TipoFallaMaquina: null,
        IdNivelAlerta: null,
        IdMedioNotificacion: null,
        IdProgramacionNotificacion: null,
        Activo: true,
        IdsUsuarios: [],
      },
      reglaMaquinaErrors: {
        Codigo: null,
        Nombre: null,
        IdMaquinaSensorVariable: null,
        IdMaquinaIns: null,
        TipoFallaMaquina: null,
        IdNivelAlerta: null,
        IdMedioNotificacion: null,
        IdProgramacionNotificacion: null,
      },
      notificationDays: [
        { key: "lun", short: "L", label: "Lunes", field: "AplicaLunes", color: "#2563eb" },
        { key: "mar", short: "M", label: "Martes", field: "AplicaMartes", color: "#0f766e" },
        { key: "mie", short: "M", label: "Miercoles", field: "AplicaMiercoles", color: "#7c3aed" },
        { key: "jue", short: "J", label: "Jueves", field: "AplicaJueves", color: "#ea580c" },
        { key: "vie", short: "V", label: "Viernes", field: "AplicaViernes", color: "#16a34a" },
        { key: "sab", short: "S", label: "Sabado", field: "AplicaSabado", color: "#db2777" },
        { key: "dom", short: "D", label: "Domingo", field: "AplicaDomingo", color: "#dc2626" },
      ],
      notificationCatalogs: {
        medios: {
          title: "Medios de notificacion",
          subtitle: "Administra los canales por donde se enviaran las notificaciones.",
          shortLabel: "Medios",
          buttonText: "Nuevo medio",
          dialogSubtitle: "Configura el codigo y canal del medio de notificacion.",
          endpoint: "/MediosNotificacion",
          idField: "IdMedioNotificacion",
          icon: "mdi-email",
          dialogIcon: "mdi-email",
          color: "#2563eb",
          exampleCodigo: "Ejemplo: EMAIL",
          exampleNombre: "Ejemplo: CORREO ELECTRONICO",
          exampleDescripcion: "Ejemplo: Envio por correo electronico",
          defaultForm: {
            id: null,
            Codigo: "",
            Nombre: "",
            Descripcion: "",
            Canal: null,
          },
          headers: [
            { text: "Nombre", value: "Nombre", align: "left" },
            { text: "Clave", value: "Codigo", align: "left" },
            { text: "Canal", value: "Canal", align: "center" },
            { text: "Descripcion", value: "Descripcion", align: "left" },
            { text: "Reglas asociadas", value: "ReglasAsociadas", align: "center" },
            { text: "Estado", value: "Activo", align: "center" },
            { text: "Acciones", value: "actions", align: "center", sortable: false },
          ],
        },
        programaciones: {
          title: "Programaciones de notificacion",
          subtitle: "Combina una frecuencia y un horario para crear una programacion reutilizable.",
          shortLabel: "Programaciones",
          buttonText: "Nueva programacion",
          dialogSubtitle: "Asocia un nombre a un par de frecuencia y horario existentes para reutilizar la configuracion en multiples reglas.",
          endpoint: "/ProgramacionesNotificacion",
          idField: "IdProgramacionNotificacion",
          icon: "mdi-calendar-clock",
          dialogIcon: "mdi-calendar-clock",
          color: "#8b5cf6",
          exampleCodigo: "Ejemplo: PROG_TURNO_DIA",
          exampleNombre: "Ejemplo: PROGRAMACION TURNO DIA",
          exampleDescripcion: "Ejemplo: Notificaciones del turno dia",
          defaultForm: {
            id: null,
            Codigo: "",
            Nombre: "",
            Descripcion: "",
            IdFrecuenciaNotificacion: null,
            IdHorarioNotificacion: null,
          },
          headers: [
            { text: "Nombre", value: "Nombre", align: "left" },
            { text: "Clave", value: "Codigo", align: "left" },
            { text: "Frecuencia", value: "FrecuenciaNotificacion", align: "left" },
            { text: "Horario", value: "HorarioNotificacion", align: "left" },
            { text: "Reglas asociadas", value: "ReglasAsociadas", align: "center" },
            { text: "Estado", value: "Activo", align: "center" },
            { text: "Acciones", value: "actions", align: "center", sortable: false },
          ],
        },
      },
      notificationCatalogState: {
        medios: { items: [], search: "", loading: false, saving: false, deletingId: null },
        programaciones: { items: [], search: "", loading: false, saving: false, deletingId: null },
      },
      keys: [],
      loadingTable: false,
      errorTime: false,
      dialogTiempo: false,
      dialogMaquinaInstancia: false,
      headers: [
        {
          text: "Máquina",
          value: "maquina",
          align: "center",
        },
        { text: "Clave", value: "clave", align: "center" },
        { text: "Unidad", value: "unidad", align: "center" },
        {
          text: "Metodos de Notificación",
          value: "metodosNotificacion",
          align: "center",
        },
        { text: "Usuarios a notificar", value: "idsUsuarios", align: "center" },
        {
          text: "Alerta de Inactividad",
          value: "tiempoInactividad",
          align: "center",
        },
        {
          text: "Alerta de Valor",
          value: "alertaValor",
          align: "center",
        },
        {
          text: "Acciones",
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      headersTiempo: [
        {
          text: "Alerta de Inactividad",
          value: "tiempoNoActividad",
          sortable: false,
          align: "center",
        },
        {
          text: "Acciones",
          value: "actions",
          sortable: false,
          align: "center",
          width: "15%",
        },
      ],
      headersMaquinaInstancia: [
        {
          text: "Tiempo agrupado",
          value: "agrupadorTiempo",
          sortable: false,
          align: "center",
        },
        {
          text: "Operación",
          value: "operacion",
          sortable: false,
          align: "center",
        },
        /* {
          text: "Valor 1",
          value: "valor",
          sortable: false,
          align: "center",
        },
        {
          text: "Comparador",
          value: "comparador",
          sortable: false,
          align: "center",
        },
        {
          text: "Valor 2",
          value: "valor2",
          sortable: false,
          align: "center",
        }, */
        { text: "Condición", value: "validacion", align: "center" },
        {
          text: "Acciones",
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      itemsData: [],
      maquinasIns: [],
      metodosNotificacion: [
        {
          id: 1,
          value: "Notificación de ManagrX",
        },
        {
          id: 2,
          value: "WebPush",
        },
        {
          id: 3,
          value: "Correo Electronico",
        },
        {
          id: 4,
          value: "Telegram",
        },
      ],
      itemsOperacion: [
        { value: 1, text: "Suma" },
        { value: 2, text: "Promedio" },
        { value: 3, text: "Maximo" },
        { value: 4, text: "Minimo" },
        { value: 5, text: "Conteo" },
      ],
      itemsComparacion: [
        { value: 1, text: "< Menor" },
        { value: 2, text: "<= Menor o Igual" },
        { value: 3, text: "= Igual" },
        { value: 4, text: "!=	Diferente" },
        { value: 5, text: "> Mayor que" },
        { value: 6, text: ">=	Menor igual que" },
        { value: 7, text: "<>	Entre" },
        { value: 8, text: "!<> No Entre" },
      ],
      segundosTotales: null,
      usuarios: [],
      errores: [],
      tiempoSinActividad: null,
      operacion: null,
      comparador: null,
      valor: null,
      valor2: null,
      dialog: false,
      dialogDelete: false,
      dialogDependenciasNotif: false,
      dependCatalogItem: null,
      dependCatalogLabel: "",
      catalogEstadoNonce: 0,
      dialogDesactivarRegla: false,
      reglaDesactivarItem: null,
      reglaActivoNonce: 0,
      editedIndex: -1,
      editedItem: {
        idMaquinaInstanciaAlerta: null,
        idMaquinaIns: null,
        clave: null,
        unidad: null,
        metodosNotificacion: [],
        idsUsuarios: [],
        tiempoInactividad: [],
        alertaValor: [],
      },
      defaultItem: {
        idMaquinaInstanciaAlerta: null,
        idMaquinaIns: null,
        clave: null,
        unidad: null,
        metodosNotificacion: [],
        idsUsuarios: [],
        tiempoInactividad: [],
        alertaValor: [],
      },
      bloques: [
        {
          value: 1,
          text: "1 Minuto",
          disabled: false,
        },
        {
          value: 2,
          text: "2 Minutos",
          disabled: false,
        },
        {
          value: 5,
          text: "5 Minutos",
          disabled: false,
        },
        {
          value: 10,
          text: "10 Minutos",
          disabled: false,
        },
        {
          value: 30,
          text: "30 Minutos",
          disabled: false,
        },
        {
          value: 60,
          text: "1 Hora",
          disabled: false,
        },
      ],
      erroresMaquinasIns: [
        {
          tiempoSinActividad: false,
          operacion: false,
          comparador: false,
          valor: false,
          valor2: false,
        },
      ],
      newIndex: 0,
      newIndexMaquinaIns: 0,
      idMaquinaInstanciaAlertaPing: null,
      idMaquinaInstanciaAlertaValor: null,
      loadingClaves: false,
    };
  },
  computed: {
    ...mapState(["sensoresTokenMaquina", "idUser"]),
    formTitle() {
      return this.editedIndex === -1 ? "Nueva alerta" : "Editar Alerta";
    },
    currentCatalogKey() {
      return this.alarmSteps[this.alarmStep - 1] ? this.alarmSteps[this.alarmStep - 1].key : "niveles";
    },
    currentCatalogConfig() {
      return this.notificationCatalogs[this.currentCatalogKey] || {};
    },
    currentCatalogState() {
      return this.notificationCatalogState[this.currentCatalogKey] || {};
    },
    selectedFrecuencia() {
      const id = this.notificationCatalogForm.IdFrecuenciaNotificacion;
      if (!id) return null;
      return this.frecuenciasNotificacion.find((f) => f.IdFrecuenciaNotificacion === id) || null;
    },
    selectedHorario() {
      const id = this.notificationCatalogForm.IdHorarioNotificacion;
      if (!id) return null;
      return this.horariosNotificacion.find((h) => h.IdHorarioNotificacion === id) || null;
    },
    horarioDiasResumen() {
      const h = this.selectedHorario;
      if (!h) return "";
      const days = this.notificationDays.filter((d) => h[d.field]).map((d) => d.short);
      return days.join(" · ");
    },
    horarioRangoResumen() {
      const h = this.selectedHorario;
      if (!h || !h.HoraInicio || !h.HoraFin) return "";
      return `${h.HoraInicio} - ${h.HoraFin}`;
    },
    programacionPreview() {
      if (this.currentCatalogKey !== "programaciones") return null;
      const frec = this.frecuenciasNotificacion.find(
        (f) => f.IdFrecuenciaNotificacion === this.notificationCatalogForm.IdFrecuenciaNotificacion
      );
      const hor = this.horariosNotificacion.find(
        (h) => h.IdHorarioNotificacion === this.notificationCatalogForm.IdHorarioNotificacion
      );
      if (!frec && !hor) return null;
      return {
        frecuencia: frec ? this.frecuenciaItemText(frec) : "",
        horario: hor ? this.horarioItemText(hor) : "",
      };
    },
    currentAlarmTitle() {
      if (this.alarmStep === 1) return "Niveles de alerta configurados";
      if (this.alarmStep === 4) return "Reglas de notificacion";
      return this.currentCatalogConfig.title;
    },
    currentAlarmSubtitle() {
      if (this.alarmStep === 1) {
        return "Administra los niveles de alerta que se pueden asignar a las variables para generar notificaciones.";
      }
      if (this.alarmStep === 4) {
        return "Define si una regla se dispara por sensor o por falla de maquina. Los usuarios se asignan despues.";
      }
      return this.currentCatalogConfig.subtitle;
    },
    currentAlarmButtonText() {
      if (this.alarmStep === 1) return "Nuevo nivel de alerta";
      if (this.alarmStep === 4) return "Nueva regla";
      return this.currentCatalogConfig.buttonText;
    },
    currentAlarmLoading() {
      if (this.alarmStep === 1) return this.loadingNivelesAlerta;
      if (this.alarmStep === 4) return this.loadingReglasMaquina;
      return this.currentCatalogState.loading;
    },
    currentSectionTone() {
      return this.alarmStepTone(this.alarmSteps[this.alarmStep - 1] ? this.alarmSteps[this.alarmStep - 1].key : "niveles");
    },
    currentSectionIcon() {
      return this.alarmStepIcon(this.alarmSteps[this.alarmStep - 1] ? this.alarmSteps[this.alarmStep - 1].key : "niveles");
    },
    currentSectionLabel() {
      const labels = {
        niveles: "Niveles de alerta",
        medios: "Medios de notificacion",
        programaciones: "Programaciones",
        reglasMaquina: "Reglas de notificacion",
      };
      const key = this.alarmSteps[this.alarmStep - 1] ? this.alarmSteps[this.alarmStep - 1].key : "niveles";
      return labels[key] || "";
    },
    currentSectionItems() {
      if (this.alarmStep === 1) return this.nivelesAlerta || [];
      if (this.alarmStep === 4) return this.reglasMaquina || [];
      return (this.currentCatalogState && this.currentCatalogState.items) || [];
    },
    currentStats() {
      const items = this.currentSectionItems;
      const active = items.filter((i) => i.Activo !== false).length;
      const inactive = items.filter((i) => i.Activo === false).length;
      const total = items.length;
      const key = this.alarmSteps[this.alarmStep - 1] ? this.alarmSteps[this.alarmStep - 1].key : "niveles";
      const tone = this.currentSectionTone;
      if (key === "programaciones") {
        return [
          { icon: "mdi-calendar-check", value: active, label: "Programaciones activas", hint: `De ${total} totales`, tone: "purple" },
          { icon: "mdi-clock-outline", value: inactive, label: "Programaciones inactivas", hint: inactive ? "No utilizadas" : "Todas en uso", tone: "purple" },
          { icon: "mdi-refresh", value: total, label: "Programaciones totales", hint: "Configuradas", tone: "green" },
          { icon: "mdi-information-outline", value: items.filter((i) => !i.IdHorarioNotificacion).length, label: "Sin horario", hint: "Verificar", tone: "orange" },
        ];
      }
      if (key === "niveles") {
        return [
          { icon: "mdi-shield-check", value: active, label: "Niveles activos", hint: `De ${total} totales`, tone: "blue" },
          { icon: "mdi-shield-off-outline", value: inactive, label: "Niveles inactivos", hint: inactive ? "No utilizados" : "Todos en uso", tone: "blue" },
          { icon: "mdi-flag-outline", value: items.filter((i) => String(i.Prioridad || "").toUpperCase() === "CRITICA").length, label: "Criticos", hint: "Maxima prioridad", tone: "orange" },
          { icon: "mdi-clock-time-eight-outline", value: items.filter((i) => i.RespetaHorario).length, label: "Respetan horario", hint: "Configurados", tone: "green" },
        ];
      }
      if (key === "medios") {
        const counts = items.reduce((acc, it) => {
          const canal = String(it.Canal || "").toUpperCase();
          acc[canal] = (acc[canal] || 0) + 1;
          return acc;
        }, {});
        return [
          { icon: "mdi-email-check", value: active, label: "Medios activos", hint: `De ${total} totales`, tone: "green" },
          { icon: "mdi-email-off-outline", value: inactive, label: "Medios inactivos", hint: inactive ? "No usados" : "Todos en uso", tone: "green" },
          { icon: "mdi-email-outline", value: counts.EMAIL || 0, label: "Por email", hint: "Canales", tone: "blue" },
          { icon: "mdi-bell-outline", value: (counts.INTERNO || 0) + (counts.EXTERNO || 0), label: "Otros canales", hint: "Internos y externos", tone: "purple" },
        ];
      }
      // reglasMaquina
      return [
        { icon: "mdi-format-list-checks", value: active, label: "Reglas activas", hint: `De ${total} totales`, tone: "orange" },
        { icon: "mdi-pause-circle-outline", value: inactive, label: "Reglas inactivas", hint: inactive ? "No aplicables" : "Todas activas", tone: "orange" },
        { icon: "mdi-checkbox-marked-circle-outline", value: items.filter((i) => String(i.TipoAlerta || "").toUpperCase() === "MAQUINA").length, label: "De maquina", hint: "Por equipo", tone: "blue" },
        { icon: "mdi-radar", value: items.filter((i) => String(i.TipoAlerta || "").toUpperCase() === "SENSOR").length, label: "De sensor", hint: "Por variable", tone: "green" },
      ];
    },
    asignarUsuariosReglaTitulo() {
      return this.asignarUsuariosReglaTarget
        ? this.reglaObjetivoLabel(this.asignarUsuariosReglaTarget)
        : "Selecciona los usuarios que recibiran esta regla.";
    },
    usuariosSeleccionadosRegla() {
      const seleccionados = (this.asignarUsuariosReglaForm.IdsUsuarios || []).map((id) => Number(id));
      return (this.usuarios || []).filter((usuario) => seleccionados.includes(Number(usuario.idUsuario)));
    },
    usuariosDisponiblesRegla() {
      const seleccionados = (this.asignarUsuariosReglaForm.IdsUsuarios || []).map((id) => Number(id));
      return (this.usuarios || []).filter((usuario) => !seleccionados.includes(Number(usuario.idUsuario)));
    },
    usuariosDisponiblesFiltradosRegla() {
      const query = this.normalizeText(this.asignarUsuariosDisponiblesSearch);
      if (!query) return this.usuariosDisponiblesRegla;
      return this.usuariosDisponiblesRegla.filter((usuario) => {
        return [
          this.usuarioLabel(usuario),
          this.usuarioRol(usuario),
          usuario.email,
          usuario.Email,
        ].some((value) => this.normalizeText(value).includes(query));
      });
    },
    programacionesNotificacion() {
      return ((this.notificationCatalogState.programaciones || {}).items || []);
    },
    selectedReglaProgramacion() {
      const id = Number(this.reglaMaquinaForm.IdProgramacionNotificacion);
      if (!id) return null;
      return this.programacionesNotificacion.find((item) => Number(item.IdProgramacionNotificacion) === id) || null;
    },
    reglaResumenOrigen() {
      return this.reglaMaquinaForm.TipoAlerta === "MAQUINA" ? "Por maquina" : "Por sensor (variable)";
    },
    reglaResumenObjetivo() {
      if (this.reglaMaquinaForm.TipoAlerta === "MAQUINA") {
        if (this.reglaMaquinaForm.TipoFallaMaquina === "TOKEN_INVALIDO") return "Token invalido";
        const maquina = (this.maquinasIns || []).find((item) => Number(item.idMaquinaIns) === Number(this.reglaMaquinaForm.IdMaquinaIns));
        return maquina ? this.maquinaLabel(maquina) : "No seleccionado";
      }
      const variable = (this.maquinaSensorVariables || []).find((item) => Number(item.idMaquinaSensorVariable) === Number(this.reglaMaquinaForm.IdMaquinaSensorVariable));
      return variable ? this.maquinaSensorVariableLabel(variable) : "No seleccionado";
    },
    reglaResumenNivel() {
      return this.catalogNombre(this.nivelesAlerta, this.reglaMaquinaForm.IdNivelAlerta, "IdNivelAlerta") || "No seleccionado";
    },
    reglaResumenMedio() {
      return this.catalogNombre(this.mediosNotificacion, this.reglaMaquinaForm.IdMedioNotificacion, "IdMedioNotificacion") || "No seleccionado";
    },
    reglaResumenProgramacion() {
      if (this.selectedReglaProgramacion) return this.programacionItemText(this.selectedReglaProgramacion);
      return "No seleccionada";
    },
    reglaResumenEstado() {
      return this.reglaMaquinaForm.Activo === false ? "Inactiva" : "Activa";
    },
    alertasTotalRegistros() {
      return this.alarmSteps.reduce((total, step) => total + this.alarmStepCount(step.key), 0);
    },
    alertasCatalogosActivos() {
      return this.alarmSteps.filter((step) => this.alarmStepCount(step.key) > 0).length;
    },
  },

  watch: {
    dialog(val) {
      val || this.close();
    },
    dialogDelete(val) {
      val || this.closeDelete();
    },
    alarmStep() {
      this.loadCurrentAlarmStep();
    },
    "notificationCatalogForm.TipoFrecuencia"(value) {
      if (value !== "PERIODICA" && this.notificationCatalogForm) {
        this.notificationCatalogForm.IntervaloMinutos = null;
      }
    },
  },

  mounted() {
    this.initialize();
    if (this.sensoresTokenMaquina) {
      // Pre-carga Frecuencias y Horarios para que el dialog de Programacion
      // siempre tenga los dropdowns listos, sin importar por que paso se entre.
      this.loadFrecuenciasHorariosNotificacion();
    }
  },
  methods: {
    initialize(){
      if (this.sensoresTokenMaquina) {
        this.loadCurrentAlarmStep();
        return;
      }
      this.GetData();
      this.getMaquinasIns();
      this.getUsuarios();
    },
    loadCurrentAlarmStep() {
      if (this.alarmStep === 1) {
        this.getNivelesAlerta();
        return;
      }
      if (this.alarmStep === 4) {
        this.getReglasMaquinaStepData();
        return;
      }
      this.getCatalogItems();
    },
    refreshCurrentAlarmStep() {
      this.loadCurrentAlarmStep();
    },
    selectAlarmStep(step) {
      if (this.programacionFormView) {
        this.closeProgramacionFormView();
      }
      if (this.alarmStep === step) {
        this.loadCurrentAlarmStep();
        return;
      }
      this.alarmStep = step;
    },
    alarmStepCount(key) {
      const counts = {
        niveles: (this.nivelesAlerta || []).length,
        medios: ((this.notificationCatalogState.medios || {}).items || []).length,
        programaciones: ((this.notificationCatalogState.programaciones || {}).items || []).length,
        reglasMaquina: (this.reglasMaquina || []).length,
      };
      return counts[key] || 0;
    },
    openCurrentAlarmDialog() {
      if (this.alarmStep === 1) {
        this.openNuevoNivelAlerta();
        return;
      }
      if (this.alarmStep === 4) {
        this.openNuevaReglaMaquina();
        return;
      }
      if (this.currentCatalogKey === "programaciones") {
        this.openProgramacionFormView(null);
        return;
      }
      this.openCatalogDialog();
    },
    uniDlgStyle(hex) {
      const toRgba = (h, alpha) => {
        const m = (h || "").replace("#", "");
        if (m.length !== 6) return `rgba(25, 118, 210, ${alpha})`;
        const r = parseInt(m.substr(0, 2), 16);
        const g = parseInt(m.substr(2, 2), 16);
        const b = parseInt(m.substr(4, 2), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      };
      return {
        "--uni-accent": hex,
        "--uni-accent-soft": toRgba(hex, 0.10),
        "--uni-accent-mid": toRgba(hex, 0.32),
      };
    },
    alarmStepTone(key) {
      const map = { niveles: "blue", medios: "green", programaciones: "purple", reglasMaquina: "orange" };
      return map[key] || "blue";
    },
    alarmStepIcon(key) {
      const map = {
        niveles: "mdi-shield-alert",
        medios: "mdi-send-circle-outline",
        programaciones: "mdi-calendar-clock",
        reglasMaquina: "mdi-radar",
      };
      return map[key] || "mdi-cog-outline";
    },
    alarmStepTitle(key) {
      const map = {
        niveles: "Niveles de alerta",
        medios: "Medios de notificacion",
        programaciones: "Programaciones",
        reglasMaquina: "Reglas de notificacion",
      };
      return map[key] || key;
    },
    alarmStepDescription(key) {
      const map = {
        niveles: "Define los niveles de alerta y su prioridad.",
        medios: "Gestiona los canales disponibles para enviar notificaciones.",
        programaciones: "Crea horarios y frecuencias reutilizables.",
        reglasMaquina: "Crea y administra las reglas de alerta.",
      };
      return map[key] || "";
    },
    sectionToneColor(tone) {
      const map = {
        blue: "#1976d2",
        green: "#2e7d32",
        purple: "#8b5cf6",
        orange: "#ef6c00",
      };
      return map[tone] || "#1976d2";
    },
    openProgramacionFormView(item) {
      this.dialogNotificationCatalog = false;
      // Asegura que las listas de Frecuencias y Horarios esten cargadas
      // (se pueblan al entrar al paso de Reglas; aqui las recargamos para que
      // los dropdowns del form de Programacion siempre tengan datos).
      this.loadFrecuenciasHorariosNotificacion();
      if (item) {
        const id = this.getCatalogItemId(item);
        const fallback = this.normalizeCatalogItem(item);
        this.notificationCatalogForm = Object.assign({}, fallback, { id });
        this.programacionFormEditing = true;
        if (id) {
          this.requestNotificationCatalog({
            method: "get",
            url: `${this.notificationCatalogs.programaciones.endpoint}/Mostrar/${id}`,
          })
            .then((response) => {
              if (response.status === 403) {
                this.handleNotificationCatalogForbidden();
                return;
              }
              const detail = this.normalizeCatalogItem(response.data || item);
              this.notificationCatalogForm = Object.assign({}, detail, { id });
            })
            .catch((error) => console.log(error));
        }
      } else {
        this.notificationCatalogForm = Object.assign(
          {},
          this.notificationCatalogs.programaciones.defaultForm
        );
        this.notificationCatalogErrors = {};
        this.programacionFormEditing = false;
      }
      this.programacionFormView = true;
    },
    closeProgramacionFormView() {
      this.programacionFormView = false;
      this.programacionFormEditing = false;
      this.notificationCatalogForm = Object.assign(
        {},
        this.notificationCatalogs.programaciones.defaultForm
      );
      this.notificationCatalogErrors = {};
    },
    saveProgramacionFromView() {
      if (!this.validateCatalogForm()) return;
      const isEditing = !!this.notificationCatalogForm.id;
      const endpoint = this.notificationCatalogs.programaciones.endpoint;
      this.notificationCatalogState.programaciones.saving = true;
      this.requestNotificationCatalog({
        method: isEditing ? "put" : "post",
        url: isEditing
          ? `${endpoint}/Actualizar/${this.notificationCatalogForm.id}`
          : `${endpoint}/Crear`,
        data: this.buildCatalogPayload(),
      })
        .then((response) => {
          if (response.status === 403) {
            this.handleNotificationCatalogForbidden();
            return;
          }
          this.showNivelAlertaSnackbar(
            isEditing ? "Programacion actualizada." : "Programacion creada.",
            "success",
            "mdi-check-circle-outline"
          );
          this.closeProgramacionFormView();
          this.getCatalogItems();
        })
        .catch((error) => {
          console.log(error);
          this.showNivelAlertaSnackbar("No se pudo guardar la programacion.", "error", "mdi-alert-circle-outline");
        })
        .finally(() => {
          this.notificationCatalogState.programaciones.saving = false;
        });
    },
    requestNotificationCatalog(config) {
      return axios({
        ...config,
        validateStatus: (status) => status === 403 || (status >= 200 && status < 300),
      });
    },
    requestReglaMaquina(config) {
      return axios({
        ...config,
        validateStatus: (status) => status === 403 || (status >= 200 && status < 300),
      });
    },
    getReglasMaquinaStepData() {
      this.getReglasMaquina();
      this.getNivelesAlerta();
      this.getMaquinasIns();
      this.getReglasMaquinaUsuarios();
      this.getMaquinaSensorVariables();
      this.getReglaMaquinaCatalogos();
    },
    getReglaMaquinaCatalogos() {
      this.requestReglaMaquina({ method: "get", url: "/MediosNotificacion" })
        .then((response) => {
          this.mediosNotificacion = Array.isArray(response.data)
            ? response.data.map((item) => this.normalizeCatalogItem(item, "IdMedioNotificacion"))
            : [];
        })
        .catch((error) => console.log(error));
      this.loadFrecuenciasHorariosNotificacion();
      const programacionesState = this.notificationCatalogState.programaciones;
      programacionesState.loading = true;
      this.requestReglaMaquina({ method: "get", url: "/ProgramacionesNotificacion" })
        .then((response) => {
          programacionesState.items = Array.isArray(response.data)
            ? response.data.map((item) => this.normalizeCatalogItem(item, "IdProgramacionNotificacion"))
            : [];
          this.syncReglaProgramacion();
        })
        .catch((error) => console.log(error))
        .finally(() => { programacionesState.loading = false; });
    },
    loadFrecuenciasHorariosNotificacion() {
      // Carga focalizada de Frecuencias + Horarios (la usa el dropdown del
      // form de Programacion y el step de Reglas). Refresca siempre para
      // garantizar datos actualizados.
      this.loadingFrecuenciasNotificacion = true;
      this.requestReglaMaquina({ method: "get", url: "/FrecuenciasNotificacion" })
        .then((response) => {
          if (response.status === 403) {
            this.frecuenciasNotificacion = [];
            return;
          }
          this.frecuenciasNotificacion = Array.isArray(response.data)
            ? response.data.map((item) => this.normalizeCatalogItem(item, "IdFrecuenciaNotificacion"))
            : [];
        })
        .catch((error) => {
          console.log(error);
          this.frecuenciasNotificacion = [];
        })
        .finally(() => { this.loadingFrecuenciasNotificacion = false; });

      this.loadingHorariosNotificacion = true;
      this.requestReglaMaquina({ method: "get", url: "/HorariosNotificacion" })
        .then((response) => {
          if (response.status === 403) {
            this.horariosNotificacion = [];
            return;
          }
          this.horariosNotificacion = Array.isArray(response.data)
            ? response.data.map((item) => this.normalizeCatalogItem(item, "IdHorarioNotificacion"))
            : [];
        })
        .catch((error) => {
          console.log(error);
          this.horariosNotificacion = [];
        })
        .finally(() => { this.loadingHorariosNotificacion = false; });
    },
    normalizeReglaMaquina(item = {}) {
      return {
        IdReglaNotificacion: item.IdReglaNotificacion || item.idReglaNotificacion || null,
        Codigo: item.Codigo || item.codigo || "",
        Nombre: item.Nombre || item.nombre || "",
        Descripcion: item.Descripcion || item.descripcion || "",
        TipoAlerta: item.TipoAlerta || item.tipoAlerta || "MAQUINA",
        IdMaquinaSensorVariable: item.IdMaquinaSensorVariable || item.idMaquinaSensorVariable || null,
        IdMaquinaIns: item.IdMaquinaIns || item.idMaquinaIns || null,
        Maquina: item.Maquina || item.maquina || null,
        TipoFallaMaquina: item.TipoFallaMaquina || item.tipoFallaMaquina || null,
        IdNivelAlerta: item.IdNivelAlerta || item.idNivelAlerta || null,
        NivelAlerta: item.NivelAlerta || item.nivelAlerta || null,
        IdMedioNotificacion: item.IdMedioNotificacion || item.idMedioNotificacion || null,
        MedioNotificacion: item.MedioNotificacion || item.medioNotificacion || null,
        IdProgramacionNotificacion: item.IdProgramacionNotificacion || item.idProgramacionNotificacion || null,
        ProgramacionNotificacion: item.ProgramacionNotificacion || item.programacionNotificacion || null,
        IdFrecuenciaNotificacion: item.IdFrecuenciaNotificacion || item.idFrecuenciaNotificacion || null,
        FrecuenciaNotificacion: item.FrecuenciaNotificacion || item.frecuenciaNotificacion || null,
        IdHorarioNotificacion: item.IdHorarioNotificacion || item.idHorarioNotificacion || null,
        HorarioNotificacion: item.HorarioNotificacion || item.horarioNotificacion || null,
        Activo: item.Activo ?? item.activo ?? true,
      };
    },
    normalizeMaquinaSensorVariable(item = {}) {
      const normalized = {
        ...item,
        idMaquinaSensorVariable: item.idMaquinaSensorVariable || item.IdMaquinaSensorVariable || item.id || item.Id || null,
        idMaquinaIns: item.idMaquinaIns || item.IdMaquinaIns || item.idMaquina || item.IdMaquina || null,
        maquina: item.maquina || item.Maquina || "",
        idSensorVariable: item.idSensorVariable || item.IdSensorVariable || null,
        idSensor: item.idSensor || item.IdSensor || null,
        sensorCodigo: item.sensorCodigo || item.SensorCodigo || "",
        sensorNombre: item.sensorNombre || item.SensorNombre || "",
        idVariable: item.idVariable || item.IdVariable || null,
        variableCodigo: item.variableCodigo || item.VariableCodigo || "",
        variableNombre: item.variableNombre || item.VariableNombre || "",
        condicion: typeof item.condicion === "boolean" ? item.condicion : item.Condicion,
      };
      return {
        ...normalized,
        label: this.maquinaSensorVariableLabel(normalized),
      };
    },
    normalizeReglaMaquinaUsuario(item = {}) {
      return {
        IdReglaNotificacionUsuario: item.IdReglaNotificacionUsuario || item.idReglaNotificacionUsuario || null,
        IdReglaNotificacion: item.IdReglaNotificacion || item.idReglaNotificacion || null,
        IdUsuario: item.IdUsuario || item.idUsuario || null,
        Usuario: item.Usuario || item.usuario || null,
        Activo: item.Activo ?? item.activo ?? true,
      };
    },
    getReglasMaquina() {
      this.loadingReglasMaquina = true;
      this.requestReglaMaquina({ method: "get", url: "/ReglasNotificacion", params: { incluirInactivos: true } })
        .then((response) => {
          if (response.status === 403) {
            this.showNivelAlertaSnackbar("No tienes permiso para consultar reglas de sensor.", "warning", "mdi-alert-circle-outline");
            this.reglasMaquina = [];
            return;
          }
          const items = Array.isArray(response.data) ? response.data : [];
          this.reglasMaquina = items.map(this.normalizeReglaMaquina);
        })
        .catch((error) => {
          console.log(error);
          this.showNivelAlertaSnackbar("No se pudieron cargar las reglas de sensor.", "error", "mdi-alert-circle-outline");
        })
        .finally(() => {
          this.loadingReglasMaquina = false;
        });
    },
    getReglasMaquinaUsuarios() {
      return this.requestReglaMaquina({ method: "get", url: "/ReglaNotificacionUsuarios" })
        .then((response) => {
          this.reglasMaquinaUsuarios = Array.isArray(response.data)
            ? response.data.map(this.normalizeReglaMaquinaUsuario)
            : [];
        })
        .catch((error) => console.log(error));
    },
    getMaquinaSensorVariables() {
      this.loadingMaquinaSensorVariables = true;
      this.requestReglaMaquina({ method: "get", url: "/MaquinaSensorVariables" })
        .then((response) => {
          const items = Array.isArray(response.data) ? response.data : [];
          this.maquinaSensorVariables = items
            .map((item) => this.normalizeMaquinaSensorVariable(item))
            .filter((item) => item.condicion !== false);
        })
        .catch((error) => {
          console.log(error);
          this.maquinaSensorVariables = [];
          this.showNivelAlertaSnackbar("No se pudieron cargar las variables de sensor por maquina.", "error", "mdi-alert-circle-outline");
        })
        .finally(() => {
          this.loadingMaquinaSensorVariables = false;
        });
    },
    resetReglaMaquinaErrors() {
      this.reglaMaquinaErrors = {
        Codigo: null,
        Nombre: null,
        IdMaquinaSensorVariable: null,
        IdMaquinaIns: null,
        TipoFallaMaquina: null,
        IdNivelAlerta: null,
        IdMedioNotificacion: null,
        IdProgramacionNotificacion: null,
      };
    },
    resetReglaMaquinaForm() {
      this.reglaMaquinaForm = Object.assign({}, this.defaultReglaMaquinaForm, { IdsUsuarios: [] });
      this.resetReglaMaquinaErrors();
    },
    openNuevaReglaMaquina() {
      this.resetReglaMaquinaForm();
      this.dialogReglaMaquina = true;
    },
    closeReglaMaquinaDialog() {
      this.dialogReglaMaquina = false;
      this.resetReglaMaquinaForm();
    },
    selectTipoAlertaRegla(tipoAlerta) {
      if (this.reglaMaquinaForm.TipoAlerta === tipoAlerta) return;
      this.reglaMaquinaForm.TipoAlerta = tipoAlerta;
      this.onTipoAlertaReglaChange();
    },
    onTipoAlertaReglaChange() {
      this.reglaMaquinaForm.IdMaquinaSensorVariable = null;
      this.reglaMaquinaForm.IdMaquinaIns = null;
      this.reglaMaquinaForm.TipoFallaMaquina = null;
      this.resetReglaMaquinaErrors();
    },
    onTipoFallaMaquinaChange() {
      if (this.reglaMaquinaForm.TipoFallaMaquina === "TOKEN_INVALIDO") {
        this.reglaMaquinaForm.IdMaquinaIns = null;
        this.reglaMaquinaErrors.IdMaquinaIns = null;
      }
      this.reglaMaquinaErrors.TipoFallaMaquina = null;
    },
    validateReglaMaquina() {
      this.resetReglaMaquinaErrors();
      if (!this.reglaMaquinaForm.Codigo) this.reglaMaquinaErrors.Codigo = "Capture la clave de la regla";
      if (!this.reglaMaquinaForm.Nombre) this.reglaMaquinaErrors.Nombre = "Capture el nombre de la regla";
      if (this.reglaMaquinaForm.TipoAlerta === "SENSOR" && !this.reglaMaquinaForm.IdMaquinaSensorVariable) {
        this.reglaMaquinaErrors.IdMaquinaSensorVariable = "Seleccione una maquina / sensor / variable";
      }
      if (
        this.reglaMaquinaForm.TipoAlerta === "MAQUINA" &&
        this.reglaMaquinaForm.TipoFallaMaquina !== "TOKEN_INVALIDO" &&
        !this.reglaMaquinaForm.IdMaquinaIns
      ) {
        this.reglaMaquinaErrors.IdMaquinaIns = "Seleccione una maquina";
      }
      if (this.reglaMaquinaForm.TipoAlerta === "MAQUINA" && !this.reglaMaquinaForm.TipoFallaMaquina) {
        this.reglaMaquinaErrors.TipoFallaMaquina = "Seleccione una falla";
      }
      if (!this.reglaMaquinaForm.IdNivelAlerta) this.reglaMaquinaErrors.IdNivelAlerta = "Seleccione un nivel";
      if (!this.reglaMaquinaForm.IdMedioNotificacion) this.reglaMaquinaErrors.IdMedioNotificacion = "Seleccione un medio";
      if (!this.reglaMaquinaForm.IdProgramacionNotificacion) {
        this.reglaMaquinaErrors.IdProgramacionNotificacion = "Seleccione una programacion";
      }
      return !Object.values(this.reglaMaquinaErrors).some(Boolean);
    },
    buildReglaMaquinaPayload() {
      const esSensor = this.reglaMaquinaForm.TipoAlerta === "SENSOR";
      const esTokenInvalido = this.reglaMaquinaForm.TipoFallaMaquina === "TOKEN_INVALIDO";
      const payload = {
        codigo: this.reglaMaquinaForm.Codigo,
        nombre: this.reglaMaquinaForm.Nombre,
        descripcion: this.reglaMaquinaForm.Descripcion || "",
        tipoAlerta: this.reglaMaquinaForm.TipoAlerta,
        idMaquinaSensorVariable: esSensor ? this.reglaMaquinaForm.IdMaquinaSensorVariable : null,
        idMaquinaIns: esSensor || esTokenInvalido ? null : this.reglaMaquinaForm.IdMaquinaIns,
        tipoFallaMaquina: esSensor ? null : this.reglaMaquinaForm.TipoFallaMaquina,
        idNivelAlerta: this.reglaMaquinaForm.IdNivelAlerta,
        idMedioNotificacion: this.reglaMaquinaForm.IdMedioNotificacion,
        idProgramacionNotificacion: this.reglaMaquinaForm.IdProgramacionNotificacion,
      };
      if (this.reglaMaquinaForm.IdReglaNotificacion) {
        payload.idUsuarioModificador = this.idUser || 1;
      } else {
        payload.idUsuarioCreador = this.idUser || 1;
      }
      return payload;
    },
    saveReglaMaquina() {
      if (!this.validateReglaMaquina()) return;
      const isEditing = !!this.reglaMaquinaForm.IdReglaNotificacion;
      this.savingReglaMaquina = true;
      this.requestReglaMaquina({
        method: isEditing ? "put" : "post",
        url: isEditing
          ? `/ReglasNotificacion/Actualizar/${this.reglaMaquinaForm.IdReglaNotificacion}`
          : "/ReglasNotificacion/Crear",
        data: this.buildReglaMaquinaPayload(),
      })
        .then((response) => {
          if (response.status === 403) {
            this.showNivelAlertaSnackbar("No tienes permiso para guardar reglas de sensor.", "warning", "mdi-alert-circle-outline");
            return Promise.reject({ handled: true });
          }
          // Status 2xx ya cuenta como exito. No exigimos que el backend
          // devuelva el Id en el body (algunos endpoints devuelven 204/sin body).
          this.showNivelAlertaSnackbar(
            isEditing ? "Regla de notificacion actualizada." : "Regla de notificacion creada.",
            "success",
            "mdi-check-circle-outline"
          );
          this.closeReglaMaquinaDialog();
          this.getReglasMaquinaStepData();
        })
        .catch((error) => {
          if (error && error.handled) return;
          console.log(error);
          const raw = error?.response?.data?.message || error?.response?.data?.msg || (typeof error?.response?.data === "string" ? error.response.data : "");
          const clean = (raw || "").replace(/^\s*\[[^\]]+\]\s*:?\s*/i, "").trim();
          this.showNivelAlertaSnackbar(
            clean || "No se pudo guardar la regla de notificacion.",
            "error",
            "mdi-alert-circle-outline"
          );
        })
        .finally(() => {
          this.savingReglaMaquina = false;
        });
    },
    saveReglaMaquinaUsuarios(idRegla, idsUsuarios = []) {
      if (!idRegla) return Promise.resolve();
      const reglaId = Number(idRegla);
      const actuales = this.reglasMaquinaUsuarios.filter((item) => Number(item.IdReglaNotificacion) === reglaId && item.Activo !== false);
      const seleccionados = idsUsuarios.map((id) => Number(id));
      const desactivar = actuales
        .filter((item) => !seleccionados.includes(Number(item.IdUsuario)) && item.IdReglaNotificacionUsuario)
        .map((item) => this.requestReglaMaquina({
          method: "put",
          url: `/ReglaNotificacionUsuarios/Desactivar/${item.IdReglaNotificacionUsuario}`,
        }));
      const crear = seleccionados
        .filter((idUsuario) => !actuales.some((item) => Number(item.IdUsuario) === idUsuario))
        .map((idUsuario) => this.requestReglaMaquina({
          method: "post",
          url: "/ReglaNotificacionUsuarios/Crear",
          data: { IdReglaNotificacion: idRegla, IdUsuario: idUsuario },
        }));
      return Promise.all([...desactivar, ...crear]);
    },
    openAsignarUsuariosRegla(item) {
      const regla = this.normalizeReglaMaquina(item);
      if (!regla.IdReglaNotificacion) return;
      this.asignarUsuariosReglaTarget = regla;
      this.asignarUsuariosReglaErrors = { IdsUsuarios: null };
      this.asignarUsuariosDisponiblesSearch = "";
      this.asignarUsuariosSeleccionDisponibles = [];
      this.asignarUsuariosSeleccionAsignados = [];
      this.loadingAsignarUsuariosRegla = true;
      this.dialogAsignarUsuariosRegla = true;

      Promise.all([
        this.getUsuarios(),
        this.getReglasMaquinaUsuarios(),
      ])
        .then(() => {
          this.asignarUsuariosReglaForm = {
            IdsUsuarios: this.reglasMaquinaUsuarios
              .filter((usuario) => Number(usuario.IdReglaNotificacion) === Number(regla.IdReglaNotificacion) && usuario.Activo !== false)
              .map((usuario) => Number(usuario.IdUsuario))
              .filter(Boolean),
          };
        })
        .finally(() => {
          this.loadingAsignarUsuariosRegla = false;
        });
    },
    closeAsignarUsuariosRegla() {
      this.dialogAsignarUsuariosRegla = false;
      this.asignarUsuariosReglaTarget = null;
      this.asignarUsuariosReglaForm = { IdsUsuarios: [] };
      this.asignarUsuariosDisponiblesSearch = "";
      this.asignarUsuariosSeleccionDisponibles = [];
      this.asignarUsuariosSeleccionAsignados = [];
      this.asignarUsuariosReglaErrors = { IdsUsuarios: null };
    },
    toggleUsuarioDisponibleRegla(idUsuario) {
      const id = Number(idUsuario);
      this.asignarUsuariosSeleccionDisponibles = this.asignarUsuariosSeleccionDisponibles.includes(id)
        ? this.asignarUsuariosSeleccionDisponibles.filter((item) => item !== id)
        : [...this.asignarUsuariosSeleccionDisponibles, id];
    },
    toggleUsuarioAsignadoRegla(idUsuario) {
      const id = Number(idUsuario);
      this.asignarUsuariosSeleccionAsignados = this.asignarUsuariosSeleccionAsignados.includes(id)
        ? this.asignarUsuariosSeleccionAsignados.filter((item) => item !== id)
        : [...this.asignarUsuariosSeleccionAsignados, id];
    },
    agregarUsuariosSeleccionadosRegla() {
      const actuales = (this.asignarUsuariosReglaForm.IdsUsuarios || []).map((id) => Number(id));
      const nuevos = this.asignarUsuariosSeleccionDisponibles.filter((id) => !actuales.includes(Number(id)));
      this.asignarUsuariosReglaForm.IdsUsuarios = [...actuales, ...nuevos];
      this.asignarUsuariosSeleccionDisponibles = [];
      this.asignarUsuariosReglaErrors.IdsUsuarios = null;
    },
    agregarTodosUsuariosRegla() {
      this.asignarUsuariosReglaForm.IdsUsuarios = (this.usuarios || []).map((usuario) => Number(usuario.idUsuario)).filter(Boolean);
      this.asignarUsuariosSeleccionDisponibles = [];
      this.asignarUsuariosReglaErrors.IdsUsuarios = null;
    },
    quitarUsuarioRegla(idUsuario) {
      const id = Number(idUsuario);
      this.asignarUsuariosReglaForm.IdsUsuarios = (this.asignarUsuariosReglaForm.IdsUsuarios || []).filter((item) => Number(item) !== id);
      this.asignarUsuariosSeleccionAsignados = this.asignarUsuariosSeleccionAsignados.filter((item) => Number(item) !== id);
    },
    quitarUsuariosSeleccionadosRegla() {
      const quitar = this.asignarUsuariosSeleccionAsignados.map((id) => Number(id));
      this.asignarUsuariosReglaForm.IdsUsuarios = (this.asignarUsuariosReglaForm.IdsUsuarios || []).filter((id) => !quitar.includes(Number(id)));
      this.asignarUsuariosSeleccionAsignados = [];
    },
    quitarTodosUsuariosRegla() {
      this.asignarUsuariosReglaForm.IdsUsuarios = [];
      this.asignarUsuariosSeleccionAsignados = [];
    },
    saveAsignarUsuariosRegla() {
      if (!this.asignarUsuariosReglaTarget?.IdReglaNotificacion) return;
      this.savingAsignarUsuariosRegla = true;
      this.saveReglaMaquinaUsuarios(
        this.asignarUsuariosReglaTarget.IdReglaNotificacion,
        this.asignarUsuariosReglaForm.IdsUsuarios
      )
        .then(() => this.getReglasMaquinaUsuarios())
        .then(() => {
          this.showNivelAlertaSnackbar("Usuarios asignados.", "success", "mdi-check-circle-outline");
          this.closeAsignarUsuariosRegla();
        })
        .catch((error) => {
          console.log(error);
          this.showNivelAlertaSnackbar("No se pudieron asignar los usuarios.", "error", "mdi-alert-circle-outline");
        })
        .finally(() => {
          this.savingAsignarUsuariosRegla = false;
        });
    },
    editReglaMaquina(item) {
      const regla = this.normalizeReglaMaquina(item);
      this.reglaMaquinaForm = Object.assign({}, this.defaultReglaMaquinaForm, regla);
      this.syncReglaProgramacion();
      this.resetReglaMaquinaErrors();
      this.dialogReglaMaquina = true;
    },
    onToggleReglaMaquina(item) {
      // Activar es directo; desactivar pide confirmacion.
      if (item.Activo === false) {
        this.ejecutarToggleReglaMaquina(item, true);
      } else {
        this.reglaDesactivarItem = item;
        this.dialogDesactivarRegla = true;
      }
    },
    confirmarDesactivarRegla() {
      const item = this.reglaDesactivarItem;
      this.dialogDesactivarRegla = false;
      this.reglaDesactivarItem = null;
      if (item) this.ejecutarToggleReglaMaquina(item, false);
    },
    cancelarDesactivarRegla() {
      this.dialogDesactivarRegla = false;
      this.reglaDesactivarItem = null;
      // Revertir el interruptor a su estado real.
      this.reglaActivoNonce += 1;
    },
    ejecutarToggleReglaMaquina(item, activar) {
      const id = item.IdReglaNotificacion;
      if (!id) return;
      this.deletingReglaMaquinaId = id;
      this.requestReglaMaquina({
        method: "put",
        url: `/ReglasNotificacion/${activar ? "Activar" : "Desactivar"}/${id}`,
      })
        .then((response) => {
          if (response.status === 403) {
            this.showNivelAlertaSnackbar("No tienes permiso para modificar reglas de sensor.", "warning", "mdi-alert-circle-outline");
            this.getReglasMaquina();
            return;
          }
          this.showNivelAlertaSnackbar(
            activar ? "Regla de sensor activada." : "Regla de sensor desactivada.",
            "success",
            "mdi-check-circle-outline"
          );
          this.getReglasMaquina();
        })
        .catch((error) => {
          console.log(error);
          this.showNivelAlertaSnackbar(
            activar ? "No se pudo activar la regla de sensor." : "No se pudo desactivar la regla de sensor.",
            "error",
            "mdi-alert-circle-outline"
          );
          this.getReglasMaquina();
        })
        .finally(() => {
          this.deletingReglaMaquinaId = null;
          // Forzar que el interruptor refleje el estado real.
          this.reglaActivoNonce += 1;
        });
    },
    maquinaNombre(idMaquinaIns) {
      const maquina = (this.maquinasIns || []).find((item) => item.idMaquinaIns === idMaquinaIns || item.IdMaquinaIns === idMaquinaIns);
      return maquina ? this.maquinaLabel(maquina) : `Maquina ${idMaquinaIns || ""}`;
    },
    tipoAlertaIcon(tipoAlerta) {
      return tipoAlerta === "MAQUINA" ? "mdi-factory" : "mdi-speedometer";
    },
    tipoAlertaColor(tipoAlerta) {
      return tipoAlerta === "MAQUINA" ? "blue darken-2" : "teal darken-2";
    },
    tipoAlertaLabel(tipoAlerta) {
      return tipoAlerta === "MAQUINA" ? "Maquina" : "Sensor";
    },
    reglaObjetivoLabel(item = {}) {
      if (item.TipoAlerta === "MAQUINA") {
        if (item.TipoFallaMaquina === "TOKEN_INVALIDO") return this.fallaMaquinaLabel(item.TipoFallaMaquina);
        return item.Maquina || this.maquinaNombre(item.IdMaquinaIns);
      }
      return this.maquinaSensorVariableLabelById(item.IdMaquinaSensorVariable);
    },
    reglaObjetivoSubtitle(item = {}) {
      if (item.TipoAlerta === "MAQUINA") {
        return this.fallaMaquinaLabel(item.TipoFallaMaquina);
      }
      return this.maquinaSensorVariableMachineById(item.IdMaquinaSensorVariable);
    },
    fallaMaquinaLabel(value) {
      const falla = this.tipoFallaMaquina.find((item) => item.value === value);
      return falla ? falla.label : value;
    },
    catalogNombre(items, id, idField) {
      const found = (items || []).find((item) => Number(item[idField]) === Number(id));
      return found ? found.Nombre : "N/D";
    },
    reglaUsuariosLabel(item) {
      const usuariosRegla = this.reglasMaquinaUsuarios.filter((usuario) => (
        Number(usuario.IdReglaNotificacion) === Number(item.IdReglaNotificacion) && usuario.Activo !== false
      ));
      if (!usuariosRegla.length) return "Sin usuarios";
      return usuariosRegla.map((usuario) => usuario.Usuario || this.usuarioNombre(usuario.IdUsuario)).join(", ");
    },
    usuarioNombre(idUsuario) {
      const usuario = (this.usuarios || []).find((item) => item.idUsuario === idUsuario || item.IdUsuario === idUsuario);
      return usuario ? this.usuarioLabel(usuario) : `Usuario ${idUsuario}`;
    },
    usuarioLabel(item = {}) {
      return item.nombre || item.Nombre || item.usuario || item.Usuario || item.email || item.Email || `Usuario ${item.idUsuario || item.IdUsuario || ""}`;
    },
    usuarioRol(item = {}) {
      return item.rol || item.Rol || item.puesto || item.Puesto || item.perfil || item.Perfil || item.tipoUsuario || item.TipoUsuario || "Usuario";
    },
    usuarioIniciales(item = {}) {
      const nombre = this.usuarioLabel(item).trim();
      const partes = nombre.split(/\s+/).filter(Boolean);
      if (!partes.length) return "US";
      return partes.slice(0, 2).map((parte) => parte.charAt(0).toUpperCase()).join("");
    },
    normalizeText(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();
    },
    maquinaSensorVariableLabel(item = {}) {
      const maquina = item.maquina || item.Maquina || this.maquinaNombre(item.idMaquinaIns || item.IdMaquinaIns);
      const sensor = item.sensorNombre || item.SensorNombre || item.sensorCodigo || item.SensorCodigo || "Sensor";
      const variable = [
        item.variableCodigo || item.VariableCodigo,
        item.variableNombre || item.VariableNombre,
      ].filter(Boolean).join(" - ") || "Variable";
      return [maquina, sensor, variable].filter(Boolean).join(" | ");
    },
    maquinaSensorVariableLabelById(idMaquinaSensorVariable) {
      const item = (this.maquinaSensorVariables || []).find((variable) => (
        Number(variable.idMaquinaSensorVariable) === Number(idMaquinaSensorVariable)
      ));
      return item ? this.maquinaSensorVariableLabel(item) : `Variable ${idMaquinaSensorVariable || ""}`;
    },
    maquinaSensorVariableMachineById(idMaquinaSensorVariable) {
      const item = (this.maquinaSensorVariables || []).find((variable) => (
        Number(variable.idMaquinaSensorVariable) === Number(idMaquinaSensorVariable)
      ));
      return item ? (item.maquina || this.maquinaNombre(item.idMaquinaIns)) : "Maquina no disponible";
    },
    handleNotificationCatalogForbidden() {
      this.showNivelAlertaSnackbar(
        "No tienes permiso para esta accion del catalogo.",
        "warning",
        "mdi-alert-circle-outline"
      );
    },
    getCatalogItemId(item = {}) {
      const field = this.currentCatalogConfig.idField;
      return item[field] || item.id || item.Id || null;
    },
    normalizeCatalogItem(item = {}, idFieldParam = null) {
      const idField = idFieldParam || this.currentCatalogConfig.idField;
      const normalized = {
        ...item,
        Codigo: item.Codigo || item.codigo || "",
        Nombre: item.Nombre || item.nombre || "",
        Descripcion: item.Descripcion || item.descripcion || "",
        Canal: item.Canal || item.canal || "",
        TipoFrecuencia: item.TipoFrecuencia || item.tipoFrecuencia || "",
        IntervaloMinutos: item.IntervaloMinutos ?? item.intervaloMinutos ?? null,
        HoraInicio: this.toTimeInput(item.HoraInicio || item.horaInicio || "08:00:00"),
        HoraFin: this.toTimeInput(item.HoraFin || item.horaFin || "18:00:00"),
        IdFrecuenciaNotificacion: item.IdFrecuenciaNotificacion ?? item.idFrecuenciaNotificacion ?? null,
        IdHorarioNotificacion: item.IdHorarioNotificacion ?? item.idHorarioNotificacion ?? null,
        FrecuenciaNotificacion: item.FrecuenciaNotificacion || item.frecuenciaNotificacion || "",
        HorarioNotificacion: item.HorarioNotificacion || item.horarioNotificacion || "",
        Activo: typeof item.Activo === "boolean" ? item.Activo : item.activo,
        ReglasAsociadas: item.ReglasAsociadas ?? item.reglasAsociadas ?? 0,
        ReglasAsociadasLista: item.ReglasAsociadasLista || item.reglasAsociadasLista || [],
        FechaCreacion: item.FechaCreacion || item.fechaCreacion || null,
        FechaModificacion: item.FechaModificacion || item.fechaModificacion || null,
      };
      if (idField) {
        normalized[idField] = item[idField] || item[idField.charAt(0).toLowerCase() + idField.slice(1)] || item.id || item.Id || null;
      }
      this.notificationDays.forEach((day) => {
        normalized[day.field] = this.toBoolean(
          item[day.field] !== undefined
            ? item[day.field]
            : item[day.field.charAt(0).toLowerCase() + day.field.slice(1)]
        );
      });
      return normalized;
    },
    getCatalogItems() {
      const state = this.currentCatalogState;
      const config = this.currentCatalogConfig;
      if (!config.endpoint) return;
      state.loading = true;
      this.requestNotificationCatalog({
        method: "get",
        url: config.endpoint,
        params: { incluirInactivos: true },
      })
        .then((response) => {
          if (response.status === 403) {
            this.handleNotificationCatalogForbidden();
            state.items = [];
            return;
          }
          const items = Array.isArray(response.data) ? response.data : [];
          state.items = items.map((item) => this.normalizeCatalogItem(item));
        })
        .catch((error) => {
          console.log(error);
          this.showNivelAlertaSnackbar(`No se pudieron cargar ${config.shortLabel.toLowerCase()}.`, "error", "mdi-alert-circle-outline");
        })
        .finally(() => {
          state.loading = false;
        });
    },
    resetCatalogForm() {
      this.notificationCatalogForm = Object.assign({}, this.currentCatalogConfig.defaultForm || {});
      this.notificationCatalogErrors = {};
    },
    openCatalogDialog() {
      this.resetCatalogForm();
      this.dialogNotificationCatalog = true;
    },
    closeCatalogDialog() {
      this.dialogNotificationCatalog = false;
      this.resetCatalogForm();
    },
    validateCatalogForm() {
      const errors = {};
      if (!this.notificationCatalogForm.Codigo) errors.Codigo = "Capture la clave";
      if (!this.notificationCatalogForm.Nombre) errors.Nombre = "Capture el nombre";

      if (this.currentCatalogKey === "medios" && !this.canalesPermitidos.includes(this.notificationCatalogForm.Canal)) {
        errors.Canal = "Seleccione un canal valido";
      }

      if (this.currentCatalogKey === "programaciones") {
        if (!this.notificationCatalogForm.IdFrecuenciaNotificacion) {
          errors.IdFrecuenciaNotificacion = "Seleccione una frecuencia";
        }
        if (!this.notificationCatalogForm.IdHorarioNotificacion) {
          errors.IdHorarioNotificacion = "Seleccione un horario";
        }
      }

      this.notificationCatalogErrors = errors;
      return !Object.values(errors).some(Boolean);
    },
    buildCatalogPayload() {
      const form = this.notificationCatalogForm;
      const payload = {
        Codigo: form.Codigo,
        Nombre: form.Nombre,
        Descripcion: form.Descripcion || "",
      };

      if (this.currentCatalogKey === "medios") {
        payload.Canal = form.Canal;
      }

      if (this.currentCatalogKey === "programaciones") {
        payload.IdFrecuenciaNotificacion = form.IdFrecuenciaNotificacion;
        payload.IdHorarioNotificacion = form.IdHorarioNotificacion;
      }

      return payload;
    },
    saveCatalogItem() {
      if (!this.validateCatalogForm()) return;
      const state = this.currentCatalogState;
      const config = this.currentCatalogConfig;
      const isEditing = !!this.notificationCatalogForm.id;
      state.saving = true;
      this.requestNotificationCatalog({
        method: isEditing ? "put" : "post",
        url: isEditing
          ? `${config.endpoint}/Actualizar/${this.notificationCatalogForm.id}`
          : `${config.endpoint}/Crear`,
        data: this.buildCatalogPayload(),
      })
        .then((response) => {
          if (response.status === 403) {
            this.handleNotificationCatalogForbidden();
            return;
          }
          this.showNivelAlertaSnackbar(
            isEditing ? `${config.shortLabel} actualizado.` : `${config.shortLabel} creado.`,
            "success",
            "mdi-check-circle-outline"
          );
          this.dialogNotificationCatalog = false;
          this.resetCatalogForm();
          this.getCatalogItems();
        })
        .catch((error) => {
          console.log(error);
          this.showNivelAlertaSnackbar(`No se pudo guardar ${config.shortLabel.toLowerCase()}.`, "error", "mdi-alert-circle-outline");
        })
        .finally(() => {
          state.saving = false;
        });
    },
    editCatalogItem(item) {
      const id = this.getCatalogItemId(item);
      if (!id) return;
      const config = this.currentCatalogConfig;
      this.requestNotificationCatalog({
        method: "get",
        url: `${config.endpoint}/Mostrar/${id}`,
      })
        .then((response) => {
          if (response.status === 403) {
            this.handleNotificationCatalogForbidden();
            return;
          }
          const detail = this.normalizeCatalogItem(response.data || item);
          this.notificationCatalogForm = Object.assign({}, detail, { id });
          this.dialogNotificationCatalog = true;
        })
        .catch((error) => {
          console.log(error);
          const detail = this.normalizeCatalogItem(item);
          this.notificationCatalogForm = Object.assign({}, detail, { id });
          this.dialogNotificationCatalog = true;
        });
    },
    toggleCatalogItem(item) {
      const id = this.getCatalogItemId(item);
      if (!id) return;
      const state = this.currentCatalogState;
      const config = this.currentCatalogConfig;
      const activar = item.Activo === false;

      // Si se intenta desactivar y tiene reglas asociadas, se bloquea y se muestra el detalle.
      if (!activar && item.ReglasAsociadas > 0) {
        this.verAsociacionesCatalog(item);
        this.catalogEstadoNonce += 1;
        return;
      }

      state.deletingId = id;
      this.requestNotificationCatalog({
        method: "put",
        url: `${config.endpoint}/${activar ? "Activar" : "Desactivar"}/${id}`,
      })
        .then((response) => {
          if (response.status === 403) {
            this.handleNotificationCatalogForbidden();
            this.getCatalogItems();
            return;
          }
          this.showNivelAlertaSnackbar(
            `${config.shortLabel} ${activar ? "activado" : "desactivado"}.`,
            "success",
            "mdi-check-circle-outline"
          );
          this.getCatalogItems();
        })
        .catch((error) => {
          const status = error && error.response ? error.response.status : null;
          if (!activar && status === 409) {
            this.verAsociacionesCatalog(item);
          } else {
            console.log(error);
            this.showNivelAlertaSnackbar(
              `No se pudo ${activar ? "activar" : "desactivar"} ${config.shortLabel.toLowerCase()}.`,
              "error",
              "mdi-alert-circle-outline"
            );
          }
          this.getCatalogItems();
        })
        .finally(() => {
          state.deletingId = null;
          this.catalogEstadoNonce += 1;
        });
    },
    verAsociacionesCatalog(item) {
      this.dependCatalogItem = item;
      this.dependCatalogLabel = this.currentCatalogConfig.shortLabel || "";
      this.dialogDependenciasNotif = true;
    },
    catalogBadge(item) {
      if (this.currentCatalogKey === "medios") return item.Canal || "Canal";
      if (this.currentCatalogKey === "programaciones") return "Programacion";
      return "";
    },
    frecuenciaItemText(item) {
      if (!item) return "";
      const name = item.Nombre || item.Codigo || "";
      const tipo = item.TipoFrecuencia ? ` - ${item.TipoFrecuencia}` : "";
      const interval = item.IntervaloMinutos ? ` (${item.IntervaloMinutos} min)` : "";
      return `${name}${tipo}${interval}`;
    },
    horarioItemText(item) {
      if (!item) return "";
      const name = item.Nombre || item.Codigo || "";
      const range = item.HoraInicio && item.HoraFin ? ` (${item.HoraInicio} - ${item.HoraFin})` : "";
      return `${name}${range}`;
    },
    programacionItemText(item) {
      if (!item) return "";
      const name = item.Nombre || item.Codigo || "";
      const frecuencia = item.FrecuenciaNotificacion ? ` - ${item.FrecuenciaNotificacion}` : "";
      const horario = item.HorarioNotificacion ? ` / ${item.HorarioNotificacion}` : "";
      return `${name}${frecuencia}${horario}`;
    },
    applyProgramacionRegla() {
      this.reglaMaquinaErrors.IdProgramacionNotificacion = null;
    },
    syncReglaProgramacion() {
      // El payload ya no envia Frec/Horario por separado; se usa solo IdProgramacionNotificacion.
    },
    catalogItemColor(item = {}) {
      if (this.currentCatalogKey === "medios") {
        const canal = String(item.Canal || "").toUpperCase();
        if (canal === "EMAIL") return "#2563eb";
        if (canal === "INTERNO") return "#0f766e";
        if (canal === "EXTERNO") return "#ea580c";
      }
      if (this.currentCatalogKey === "programaciones") {
        return "#8b5cf6";
      }
      return this.currentCatalogConfig.color || "#2563eb";
    },
    catalogItemIcon(item = {}) {
      if (this.currentCatalogKey === "medios") {
        const canal = String(item.Canal || "").toUpperCase();
        if (canal === "EMAIL") return "mdi-email";
        if (canal === "INTERNO") return "mdi-bell";
        if (canal === "EXTERNO") return "mdi-send";
      }
      if (this.currentCatalogKey === "programaciones") {
        return "mdi-calendar-clock";
      }
      return this.currentCatalogConfig.icon;
    },
    catalogItemSoftStyle(item = {}) {
      const color = this.catalogItemColor(item);
      return {
        color,
        background: `${color}16`,
      };
    },
    toBoolean(value) {
      return value === true || value === 1 || String(value).toLowerCase() === "true";
    },
    horarioDiaActivo(item, day) {
      return this.toBoolean(item[day.field]);
    },
    notificationDayStyle(day, active) {
      if (!active) return {};
      return {
        color: day.color,
        background: `${day.color}14`,
        borderColor: `${day.color}4d`,
      };
    },
    toApiTime(value) {
      if (!value) return "";
      return value.length === 5 ? `${value}:00` : value;
    },
    toTimeInput(value) {
      if (!value) return "";
      return String(value).slice(0, 5);
    },
    normalizeNivelAlerta(item = {}) {
      return {
        ...item,
        IdNivelAlerta: item.IdNivelAlerta || item.idNivelAlerta || item.id || item.Id || null,
        Codigo: item.Codigo || item.codigo || "",
        Nombre: item.Nombre || item.nombre || "",
        Descripcion: item.Descripcion || item.descripcion || "",
        ColorHex: item.ColorHex || item.colorHex || "#0f766e",
        RespetaHorario: typeof item.RespetaHorario === "boolean" ? item.RespetaHorario : item.respetaHorario,
        Prioridad: item.Prioridad || item.prioridad || "MEDIA",
        Activo: typeof item.Activo === "boolean" ? item.Activo : item.activo,
        FechaCreacion: item.FechaCreacion || item.fechaCreacion || null,
        FechaModificacion: item.FechaModificacion || item.fechaModificacion || null,
      };
    },
    requestNivelAlerta(config) {
      return axios({
        ...config,
        validateStatus: (status) => status === 403 || (status >= 200 && status < 300),
      });
    },
    handleNivelAlertaForbidden() {
      this.showNivelAlertaSnackbar(
        "No tienes permiso para esta accion de niveles de alerta.",
        "warning",
        "mdi-alert-circle-outline"
      );
    },
    getNivelesAlerta() {
      this.loadingNivelesAlerta = true;
      this.requestNivelAlerta({
        method: "get",
        url: "/NivelesAlerta",
      })
        .then((response) => {
          if (response.status === 403) {
            this.handleNivelAlertaForbidden();
            this.nivelesAlerta = [];
            return;
          }
          const items = Array.isArray(response.data) ? response.data : [];
          this.nivelesAlerta = items.map(this.normalizeNivelAlerta);
        })
        .catch((error) => {
          console.log(error);
          this.showNivelAlertaSnackbar("No se pudieron cargar los niveles de alerta.", "error", "mdi-alert-circle-outline");
        })
        .finally(() => {
          this.loadingNivelesAlerta = false;
        });
    },
    validateNivelAlerta() {
      this.nivelAlertaErrors = {
        Codigo: null,
        Nombre: null,
        ColorHex: null,
        Prioridad: null,
      };
      if (!this.nivelAlertaForm.Codigo) this.nivelAlertaErrors.Codigo = "Capture el codigo";
      if (!this.nivelAlertaForm.Nombre) this.nivelAlertaErrors.Nombre = "Capture el nombre";
      if (!this.prioridadesPermitidas.includes(this.nivelAlertaForm.Prioridad)) {
        this.nivelAlertaErrors.Prioridad = "Seleccione una prioridad valida";
      }
      if (!/^#[0-9A-Fa-f]{6}$/.test(this.nivelAlertaForm.ColorHex || "")) {
        this.nivelAlertaErrors.ColorHex = "Use formato #RRGGBB";
      }
      return !Object.values(this.nivelAlertaErrors).some(Boolean);
    },
    buildNivelAlertaPayload() {
      return {
        Codigo: this.nivelAlertaForm.Codigo,
        Nombre: this.nivelAlertaForm.Nombre,
        Descripcion: this.nivelAlertaForm.Descripcion,
        ColorHex: this.nivelAlertaForm.ColorHex,
        RespetaHorario: this.nivelAlertaForm.RespetaHorario,
        Prioridad: this.nivelAlertaForm.Prioridad,
      };
    },
    saveNivelAlerta() {
      if (!this.validateNivelAlerta()) return;
      const isEditing = !!this.nivelAlertaForm.idNivelAlerta;
      this.savingNivelAlerta = true;
      this.requestNivelAlerta({
        method: isEditing ? "put" : "post",
        url: isEditing
          ? `/NivelesAlerta/Actualizar/${this.nivelAlertaForm.idNivelAlerta}`
          : "/NivelesAlerta/Crear",
        data: this.buildNivelAlertaPayload(),
      })
        .then((response) => {
          if (response.status === 403) {
            this.handleNivelAlertaForbidden();
            return;
          }
          this.showNivelAlertaSnackbar(
            isEditing ? "Nivel de alerta actualizado." : "Nivel de alerta creado.",
            "success",
            "mdi-check-circle-outline"
          );
          this.resetNivelAlertaForm();
          this.dialogNivelAlerta = false;
          this.getNivelesAlerta();
        })
        .catch((error) => {
          console.log(error);
          this.showNivelAlertaSnackbar("No se pudo guardar el nivel de alerta.", "error", "mdi-alert-circle-outline");
        })
        .finally(() => {
          this.savingNivelAlerta = false;
        });
    },
    editNivelAlerta(item) {
      const detailId = item.IdNivelAlerta;
      if (!detailId) return;
      this.requestNivelAlerta({
        method: "get",
        url: `/NivelesAlerta/Mostrar/${detailId}`,
      })
        .then((response) => {
          if (response.status === 403) {
            this.handleNivelAlertaForbidden();
            return;
          }
          const detail = this.normalizeNivelAlerta(response.data || item);
          this.nivelAlertaForm = {
            idNivelAlerta: detail.IdNivelAlerta,
            Codigo: detail.Codigo,
            Nombre: detail.Nombre,
            Descripcion: detail.Descripcion,
            ColorHex: detail.ColorHex,
            RespetaHorario: detail.RespetaHorario !== false,
            Prioridad: detail.Prioridad,
          };
          this.dialogNivelAlerta = true;
        })
        .catch((error) => {
          console.log(error);
          const detail = this.normalizeNivelAlerta(item);
          this.nivelAlertaForm = {
            idNivelAlerta: detail.IdNivelAlerta,
            Codigo: detail.Codigo,
            Nombre: detail.Nombre,
            Descripcion: detail.Descripcion,
            ColorHex: detail.ColorHex,
            RespetaHorario: detail.RespetaHorario !== false,
            Prioridad: detail.Prioridad,
          };
          this.dialogNivelAlerta = true;
        });
    },
    desactivarNivelAlerta(item) {
      const id = item.IdNivelAlerta;
      if (!id) return;
      this.deletingNivelAlertaId = id;
      this.requestNivelAlerta({
        method: "put",
        url: `/NivelesAlerta/Desactivar/${id}`,
      })
        .then((response) => {
          if (response.status === 403) {
            this.handleNivelAlertaForbidden();
            return;
          }
          this.showNivelAlertaSnackbar("Nivel de alerta desactivado.", "success", "mdi-check-circle-outline");
          this.getNivelesAlerta();
        })
        .catch((error) => {
          console.log(error);
          this.showNivelAlertaSnackbar("No se pudo desactivar el nivel de alerta.", "error", "mdi-alert-circle-outline");
        })
        .finally(() => {
          this.deletingNivelAlertaId = null;
        });
    },
    resetNivelAlertaForm() {
      this.nivelAlertaForm = Object.assign({}, this.defaultNivelAlertaForm);
      this.nivelAlertaErrors = {
        Codigo: null,
        Nombre: null,
        ColorHex: null,
        Prioridad: null,
      };
    },
    openNuevoNivelAlerta() {
      // Los niveles son fijos. No se permite crear nuevos.
      this.openConfigurarSeveridades();
    },
    openConfigurarSeveridades() {
      const lista = Array.isArray(this.nivelesAlerta) ? this.nivelesAlerta : [];
      if (!lista.length) {
        this.showNivelAlertaSnackbar(
          "No hay severidades configuradas para editar.",
          "warning",
          "mdi-alert-circle-outline"
        );
        return;
      }
      // Copia editable: pre-ordenamos para mostrar ADVERTENCIA primero, luego ALARMA
      const order = (n) => {
        const c = String(n.Codigo || "").toUpperCase();
        if (c.includes("ADVERTENCIA")) return 1;
        if (c.includes("ALARMA")) return 2;
        return 99;
      };
      this.severidadesConfig = lista
        .map((n) => ({
          IdNivelAlerta: n.IdNivelAlerta || n.idNivelAlerta || null,
          Codigo: n.Codigo || "",
          Nombre: n.Nombre || "",
          Descripcion: n.Descripcion || "",
          ColorHex: n.ColorHex || "#FACC15",
          Prioridad: n.Prioridad || "MEDIA",
          RespetaHorario: n.RespetaHorario !== false,
          Activo: n.Activo !== false,
        }))
        .sort((a, b) => order(a) - order(b));
      this.dialogConfigurarSeveridades = true;
    },
    closeConfigurarSeveridadesDialog() {
      this.dialogConfigurarSeveridades = false;
      this.severidadesConfig = [];
    },
    saveSeveridades() {
      if (!this.severidadesConfig.length) return;
      // Validaciones minimas: descripcion no vacia, color hex valido
      for (const item of this.severidadesConfig) {
        if (!item.IdNivelAlerta) {
          this.showNivelAlertaSnackbar(
            "Una de las severidades no tiene identificador.",
            "error",
            "mdi-alert-circle-outline"
          );
          return;
        }
      }
      this.savingSeveridades = true;
      const requests = this.severidadesConfig.map((item) =>
        this.requestNivelAlerta({
          method: "put",
          url: `/NivelesAlerta/Actualizar/${item.IdNivelAlerta}`,
          data: {
            Codigo: item.Codigo,
            Nombre: item.Nombre,
            Descripcion: item.Descripcion || "",
            ColorHex: item.ColorHex,
            RespetaHorario: !!item.RespetaHorario,
            Prioridad: item.Prioridad,
          },
        })
      );
      Promise.all(requests)
        .then(() => {
          this.showNivelAlertaSnackbar(
            "Severidades actualizadas correctamente.",
            "success",
            "mdi-check-circle-outline"
          );
          this.closeConfigurarSeveridadesDialog();
          this.getNivelesAlerta();
        })
        .catch((error) => {
          console.log(error);
          this.showNivelAlertaSnackbar(
            "No se pudieron guardar todas las severidades.",
            "error",
            "mdi-alert-circle-outline"
          );
        })
        .finally(() => {
          this.savingSeveridades = false;
        });
    },
    severidadTone(item) {
      const codigo = String((item && item.Codigo) || "").toUpperCase();
      if (codigo.includes("ALARMA")) return "alarma";
      return "advertencia";
    },
    severidadIcon(item) {
      return this.severidadTone(item) === "alarma" ? "mdi-alert-octagon" : "mdi-alert";
    },
    severidadSublabel(item) {
      return this.severidadTone(item) === "alarma" ? "Nivel de alarma" : "Nivel de advertencia";
    },
    severidadPriorityNote(prioridad) {
      const p = String(prioridad || "").toUpperCase();
      if (p === "CRITICA") return "Las notificaciones criticas se enviaran siempre, sin importar el horario.";
      if (p === "ALTA") return "Las notificaciones con prioridad alta se enviaran segun la programacion configurada.";
      return "Las notificaciones con prioridad media se enviaran segun la programacion configurada.";
    },
    severidadHorarioNote(item) {
      if (item.RespetaHorario) return "Se enviara segun las programaciones configuradas.";
      return "Se enviara siempre, sin importar el horario.";
    },
    severidadPriorityNumber(prioridad) {
      const p = String(prioridad || "").toUpperCase();
      if (p === "CRITICA") return 5;
      if (p === "ALTA") return 4;
      return 3;
    },
    severidadPriorityLabel(prioridad) {
      const p = String(prioridad || "").toUpperCase();
      if (p === "CRITICA") return "Critica";
      if (p === "ALTA") return "Alta";
      return "Media";
    },
    closeNivelAlertaDialog() {
      this.dialogNivelAlerta = false;
      this.resetNivelAlertaForm();
    },
    selectNivelAlertaColor(hex) {
      this.nivelAlertaForm.ColorHex = hex;
      this.nivelAlertaErrors.ColorHex = null;
    },
    colorName(hex) {
      const color = this.colorPalette.find((item) => item.hex.toLowerCase() === String(hex || "").toLowerCase());
      return color ? color.name : "Personalizado";
    },
    softColor(hex) {
      if (!hex || !/^#[0-9A-Fa-f]{6}$/.test(hex)) return "#f1f5f9";
      return `${hex}24`;
    },
    priorityLabel(priority) {
      if (priority === "CRITICA") return "Critica";
      if (priority === "ALTA") return "Alta";
      return "Media";
    },
    priorityNumber(priority) {
      if (priority === "CRITICA") return 4;
      if (priority === "ALTA") return 3;
      return 2;
    },
    priorityTag(priority) {
      if (priority === "CRITICA") return "Nivel critico";
      if (priority === "ALTA") return "Nivel de control";
      return "Nivel informativo";
    },
    priorityColor(priority) {
      if (priority === "CRITICA") return "red darken-2";
      if (priority === "ALTA") return "orange darken-2";
      return "amber darken-2";
    },
    priorityIcon(priority) {
      if (priority === "CRITICA") return "mdi-alert-octagon-outline";
      if (priority === "ALTA") return "mdi-alert-circle-outline";
      return "mdi-alert-outline";
    },
    showNivelAlertaSnackbar(text, color = "success", icon = "mdi-check-circle-outline") {
      this.nivelAlertaSnackbar = {
        show: true,
        text,
        color,
        icon,
      };
    },
    maquinaLabel: ({ nombre, marca, modelo }) =>
      `[${nombre}] - ${marca} ${modelo}`.trim(),
    usuariosNombre(id) {
      var users = this.usuarios;
      return users.find((element) => element.idUsuario == id) == undefined
        ? ""
        : users.find((element) => element.idUsuario == id).nombre;
    },
    agregarTiempo() {
      this.newIndex -= 1;
      const newObj = {
        idMaquinaInstanciaAlertaPing: this.newIndex,
        tiempoNoActividad: null,
      };
      this.editedItem.tiempoInactividad.push(newObj);
    },
    agregarMaquinaInstancia() {
      this.newIndexMaquinaIns -= 1;
      const newObj = {
        idMaquinaInstanciaAlertaValor: this.newIndexMaquinaIns,
        agrupadorTiempo: null,
        operacion: null,
        comparador: null,
        valor: null,
        valor2: null,
      };
      this.editedItem.alertaValor.push(newObj);
    },
    secondsToMinutes(item) {
      var operacion = item / 60;
      if (operacion >= 60) return "1 Hora";
      else if (operacion < 60)
        return operacion + (operacion > 1 ? " Minutos" : " Minuto");
    },
    secondsToMinute(item) {
      return item / 60;
    },
    GetData() {
      this.loadingTable = true;
      axios
        .get("/Alerta/ListarMAquinaInstanciaAlertas/")
        .then((response) => {
          this.itemsData = response.data;
          this.loadingTable = false;
        })
        .catch((error) => {
          console.log(error);
        });
    },
    getMaquinasIns() {
      return axios
        .get("/MaquinaInstancias")
        .then((response) => {
          this.maquinasIns = Array.isArray(response.data)
            ? response.data.map((maquina) => ({
              ...maquina,
              idMaquinaIns: maquina.idMaquinaIns || maquina.IdMaquinaIns,
            }))
            : [];
        })
        .catch((error) => {
          console.log(error);
        });
    },
    getUsuarios() {
      return axios
        .get("/Usuarios")
        .then((response) => {
          this.usuarios = Array.isArray(response.data)
            ? response.data.map((usuario) => ({
              ...usuario,
              idUsuario: usuario.idUsuario || usuario.IdUsuario,
              nombre: usuario.nombre || usuario.Nombre || usuario.Usuario || usuario.usuario || usuario.email || usuario.Email,
            }))
            : [];
        })
        .catch((error) => {
          console.log(error);
        });
    },
    validacion() {
      this.errores = [];
      if (
        this.editedItem.idMaquinaIns == null ||
        this.editedItem.idMaquinaIns < 1
      ) {
        this.errores.push("Seleccione al menos una maquina");
      }

      if (this.editedItem.clave == null) {
        this.errores.push("Ingrese una clave");
      }

      if (this.editedItem.metodosNotificacion.length == 0) {
        this.errores.push("Seleccione al menos un metodo de notificación");
      }

      if (this.editedItem.idsUsuarios.length == 0) {
        this.errores.push("Seleccione al menos un usuario");
      }

      if (
        this.editedItem.tiempoInactividad.length <= 0 &&
        this.editedItem.alertaValor.length <= 0
      )
        this.errores.push(
          "Ingrese al menos una Alerta de Inactividad o una Alerta de Valor"
        );

      if (this.editedItem.tiempoInactividad.length > 0) {
        var obj = this.editedItem.tiempoInactividad;
        obj.forEach((element) => {
          if (element.tiempoNoActividad == null) {
            this.errores.push(
              "Ingresa campos no vacios o nulos dentro de Tiempo de Inactividad"
            );
            return;
          }
        });
      }

      if (this.editedItem.alertaValor.length > 0) {
        var obj = this.editedItem.alertaValor;
        obj.forEach((element) => {
          if (
            (element.agrupadorTiempo == null || element.agrupadorTiempo < 1) &&
            (element.operacion == null || element.operacion < 1) &&
            (element.comparador == null || element.comparador < 1) &&
            element.valor == null
          ) {
            this.errores.push(
              "Ingresa campos no vacios o nulos dentro de Alerta de Valor"
            );
            return;
          }
        });
      }

      return this.errores.length > 0 ? true : false;
    },
    CancelarGrnal() {
      this.dialog = false;
      this.editedItem = Object.assign({}, this.defaultItem);
      this.editedIndex = -1;
      this.newIndex = 0;
      this.newIndexMaquinaIns = 0;
      this.idMaquinaInstanciaAlertaPing = null;
      this.idMaquinaInstanciaAlertaValor = null;
      this.dialogTiempo = false;
      this.dialogMaquinaInstancia = false;
      this.tiempoSinActividad = null;
      this.operacion = null;
      this.comparador = null;
      this.valor = null;
      this.valor2 = null;
      this.dialog = false;
      this.dialogDelete = false;
      this.editedIndex = -1;
    },
    editItem(item) {
      this.editedIndex = this.itemsData.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },
    deleteItem(item) {
      this.editedIndex = this.itemsData.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialogDelete = true;
    },
    deleteItemConfirm() {
      if (
        this.editedItem.idMaquinaInstanciaAlerta == null ||
        this.editedItem.idMaquinaInstanciaAlerta < 1
      )
        return;
      axios
        .put(
          "/Alerta/EliminarAlerta/" + this.editedItem.idMaquinaInstanciaAlerta
        )
        .then(() => {
          this.GetData();
        })
        .catch((error) => {
          Error.log(error);
        });
      this.closeDelete();
    },
    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },
    closeDelete() {
      this.dialogDelete = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },
    showDialogTime(item) {
      this.dialogTiempo = true;
      this.idMaquinaInstanciaAlertaPing = item.idMaquinaInstanciaAlertaPing;
      if (item.tiempoNoActividad != null)
        this.tiempoSinActividad = this.bloques.find(
          (bloq) => bloq.value == item.tiempoNoActividad / 60
        ).value;
    },
    showDialogMaquinaInstancia(item) {
      this.dialogMaquinaInstancia = true;
      this.idMaquinaInstanciaAlertaValor = item.idMaquinaInstanciaAlertaValor;
      let index = this.editedItem.alertaValor.findIndex(
        (a) =>
          a.idMaquinaInstanciaAlertaValor == this.idMaquinaInstanciaAlertaValor
      );
      this.tiempoSinActividad =
        this.editedItem.alertaValor[index].agrupadorTiempo / 60;
      this.operacion = this.editedItem.alertaValor[index].operacion;
      this.comparador = this.editedItem.alertaValor[index].comparador;
      this.valor = this.editedItem.alertaValor[index].valor;
      this.valor2 = this.editedItem.alertaValor[index].valor2;
    },
    closeDialosTime() {
      this.dialogTiempo = false;
      this.errorTime = false;
      this.tiempoSinActividad = null;
    },
    EditTimeClose() {
      if (this.tiempoSinActividad == null) {
        this.errorTime = true;
        return;
      }
      this.dialogTiempo = false;
      this.errorTime = false;
      this.editedItem.tiempoInactividad.find(
        (x) =>
          x.idMaquinaInstanciaAlertaPing == this.idMaquinaInstanciaAlertaPing
      ).tiempoNoActividad = this.tiempoSinActividad * 60;
      this.tiempoSinActividad = null;
    },
    CloseMaquinaInstancia() {
      this.dialogMaquinaInstancia = false;
      this.tiempoSinActividad = null;
      this.operacion = null;
      this.comparador = null;
      this.valor = null;
      this.valor2 = null;
      this.erroresMaquinasIns.tiempoSinActividad = false;
      this.erroresMaquinasIns.operacion = false;
      this.erroresMaquinasIns.comparador = false;
      this.erroresMaquinasIns.valor = false;
      this.erroresMaquinasIns.valor2 = false;
    },
    editMaquinaInstanciaClose() {
      if (!this.validarMaquinaInstancia()) return;

      this.dialogMaquinaInstancia = false;
      let index = this.editedItem.alertaValor.findIndex(
        (a) =>
          a.idMaquinaInstanciaAlertaValor == this.idMaquinaInstanciaAlertaValor
      );
      this.editedItem.alertaValor[index].agrupadorTiempo =
        this.tiempoSinActividad * 60;
      this.editedItem.alertaValor[index].operacion = this.operacion;
      this.editedItem.alertaValor[index].comparador = this.comparador;
      this.editedItem.alertaValor[index].valor = parseInt(this.valor);
      this.editedItem.alertaValor[index].valor2 =
        this.comparador == 7 || this.comparador == 8
          ? parseInt(this.valor2)
          : null;
      this.tiempoSinActividad = null;

      this.erroresMaquinasIns.tiempoSinActividad = false;
      this.erroresMaquinasIns.operacion = false;
      this.erroresMaquinasIns.comparador = false;
      this.erroresMaquinasIns.valor = false;
      this.erroresMaquinasIns.valor2 = false;
    },
    validarMaquinaInstancia() {
      if (this.tiempoSinActividad == null || this.tiempoSinActividad < 1) {
        this.erroresMaquinasIns.tiempoSinActividad = true;
        return false;
      }

      if (this.operacion == null || this.operacion < 1) {
        this.erroresMaquinasIns.operacion = true;
        return false;
      }

      if (this.comparador == null < this.comparador < 1) {
        this.erroresMaquinasIns.comparador = true;
        return false;
      }

      if (this.valor == null || !this.$utils.numbers.isFloat(this.valor)) {
        this.erroresMaquinasIns.valor = true;
        return false;
      }

      if (
        (this.valor2 == null || !this.$utils.numbers.isFloat(this.valor) || this.valor2 < this.valor) &&
        (this.comparador == 7 || this.comparador == 8)
      ) {
        this.erroresMaquinasIns.valor2 = true;
        return false;
      }

      return true;
    },
    DeleteTime(item) {
      var pos = this.editedItem.tiempoInactividad.indexOf(item);
      this.editedItem.tiempoInactividad.splice(pos, 1);
    },
    DeleteMaquinaInstancia(item) {
      var pos = this.editedItem.alertaValor.indexOf(item);
      this.editedItem.alertaValor.splice(pos, 1);
    },
    save() {
      if (this.validacion()) return;

      var objeto = new Object();
      objeto.idMaquinaInstanciaAlerta =
        this.editedItem.idMaquinaInstanciaAlerta == null
          ? 0
          : this.editedItem.idMaquinaInstanciaAlerta;
      objeto.clave = this.editedItem.clave;
      objeto.idMaquinaIns = this.editedItem.idMaquinaIns;
      objeto.unidad = this.editedItem.unidad;
      objeto.metodosNotificacion = this.editedItem.metodosNotificacion;
      objeto.idsUsuarios = this.editedItem.idsUsuarios;
      objeto.tiempoInactividad = this.editedItem.tiempoInactividad;
      objeto.maquinaInstanciaAlertaValor = this.editedItem.alertaValor;
      //-1 crear
      axios
        .post("/Alerta/CrearActualizarAlertas", objeto)
        .then((response) => {
          this.GetData();
          this.saving = false;
          this.close();
        })
        .catch((error) => {
          this.saving = false;
          console.log(error);
        });

      this.close();
    },
    getKeys() {
      if (this.editedItem.idMaquinaIns == null) return;
      var fecha = moment()
        .add(-7, "days")
        .format("YYYY-MM-DD");
      this.loadingClaves = true;

      axios
        .get("/SensorDatos/Keys", {
          params: {
            inicio: fecha + " 00:00",
            fin: null,
            idMaquinaIns: this.editedItem.idMaquinaIns,
          },
        })
        .then((response) => {
          this.keys = response.data;
          this.loadingClaves = false;
          if (this.configSelected == "" || this.configSelected == null)
            this.clavesSeleccionadas = this.keys;
        })
        .catch((error) => {
          this.loadingClaves = false;
          console.log(error);
        });
    },
  },
};
</script>

<style>
.v-data-table-header th {
  white-space: nowrap;
}

.alarm-builder {
  background: #f4f6f9;
  min-height: 72vh;
  padding: 24px;
  font-family: inherit;
}

.alarm-builder__header-card {
  border-radius: 8px !important;
  overflow: hidden;
  border-color: rgba(15, 118, 110, 0.18) !important;
}

.alarm-builder__header {
  position: relative;
  display: flex;
  align-items: center;
  gap: 18px;
  background:
    linear-gradient(135deg, rgba(15, 118, 110, 0.1) 0%, rgba(37, 99, 235, 0.06) 56%, rgba(255, 255, 255, 0.95) 100%),
    #ffffff;
}

.alarm-builder__header::after {
  content: "";
  position: absolute;
  right: 24px;
  bottom: 0;
  width: 220px;
  height: 4px;
  background: linear-gradient(90deg, #0f766e, #2563eb, #f59e0b);
  border-radius: 999px 999px 0 0;
}

.alarm-builder__header-icon {
  box-shadow: 0 16px 32px rgba(15, 118, 110, 0.22);
}

.alarm-builder__header-copy {
  flex: 1;
  min-width: 0;
}

.alarm-builder__kicker {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #0f766e;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.alarm-builder__kicker-line {
  width: 28px;
  height: 2px;
  background: #0f766e;
  border-radius: 999px;
}

.alarm-builder__title {
  margin: 4px 0 0;
  color: #0f172a;
  font-size: 2rem;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.05;
}

.alarm-builder__title span {
  color: #64748b;
  font-weight: 800;
}

.alarm-builder__title-accent {
  width: 86px;
  height: 3px;
  margin: 10px 0 8px;
  background: linear-gradient(90deg, #0f766e, #2563eb);
  border-radius: 999px;
}

.alarm-builder__subtitle {
  margin: 0;
  color: #64748b;
  font-size: 0.86rem;
}

.alarm-builder__step-chip {
  background: rgba(255, 255, 255, 0.82) !important;
  font-weight: 800;
}

.alarm-sidebar,
.alarm-levels-card,
.alarm-form-card,
.alarm-list-card {
  border-radius: 8px !important;
  border-color: rgba(15, 23, 42, 0.08) !important;
  background: #ffffff !important;
}

.alarm-sidebar {
  padding: 14px 16px;
  z-index: 4;
  width: 100%;
}

.alarm-sidebar__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 2px 12px;
}

.alarm-sidebar__label {
  color: #334155;
  font-size: 0.82rem;
  font-weight: 800;
  white-space: nowrap;
}

.alarm-sidebar__steps {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.alarm-sidebar__step {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 68px;
  padding: 12px;
  border: 1px solid #e6edf6;
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.alarm-sidebar__step--active {
  border-color: rgba(15, 118, 110, 0.45);
  background: #f0fdfa;
  box-shadow: 0 10px 22px rgba(15, 118, 110, 0.1);
}

.alarm-sidebar__step--locked {
  cursor: default;
  opacity: 0.7;
}

.alarm-sidebar__step:not(.alarm-sidebar__step--locked):hover {
  border-color: rgba(37, 99, 235, 0.3);
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.08);
  transform: translateY(-1px);
}

.alarm-sidebar__step-icon {
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: 8px;
  flex: 0 0 38px;
  box-shadow: 0 12px 22px rgba(15, 23, 42, 0.12);
}

.alarm-sidebar__step-copy {
  flex: 1;
  min-width: 0;
}

.alarm-sidebar__step-kicker {
  color: #64748b;
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
}

.alarm-sidebar__step-title {
  color: #0f172a;
  font-size: 0.94rem;
  font-weight: 850;
  line-height: 1.2;
}

.alarm-sidebar__step-subtitle {
  color: #64748b;
  font-size: 0.76rem;
  line-height: 1.25;
}

.alarm-section-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  background: linear-gradient(135deg, rgba(15, 118, 110, 0.08), rgba(255, 255, 255, 0));
}

.alarm-section-banner__icon {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border-radius: 8px;
  background: #ecfdf5;
}

.alarm-section-banner__title {
  color: #0f172a;
  font-size: 1rem;
  font-weight: 850;
}

.alarm-section-banner__subtitle {
  color: #64748b;
  font-size: 0.8rem;
}

.alarm-table-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 22px 24px;
  border-bottom: 1px solid #e5eaf3;
  background: #ffffff;
}

.alarm-table-header__copy {
  min-width: 0;
}

.alarm-table-header__title {
  color: #07133f;
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.2;
}

.alarm-table-header__subtitle {
  color: #314266;
  font-size: 0.8rem;
  font-weight: 500;
  margin-top: 6px;
}

.alarm-table-header__button {
  border-radius: 4px !important;
  min-height: 42px;
  font-weight: 800;
  box-shadow: 0 10px 22px rgba(37, 99, 235, 0.22) !important;
}

.alarm-form-card,
.alarm-list-card {
  padding: 16px;
  height: 100%;
}

.alarm-form-card__header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.alarm-form-card__title {
  color: #0f172a;
  font-weight: 850;
}

.alarm-form-card__subtitle {
  color: #64748b;
  font-size: 0.76rem;
}

.alarm-form-card__actions,
.alarm-list-card__toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
}

.alarm-form-card__actions {
  margin-top: 14px;
}

.alarm-list-card__toolbar {
  margin-bottom: 12px;
}

.alarm-list-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
}

.alarm-list-card__search {
  max-width: 360px;
}

.alarm-color-dot {
  display: inline-block;
  width: 16px;
  height: 16px;
  margin-top: 3px;
  margin-right: 8px;
  border-radius: 4px;
  border: 1px solid rgba(15, 23, 42, 0.14);
}

.alarm-color-dot--large {
  width: 28px;
  height: 28px;
  margin: 0;
  flex: 0 0 28px;
}

.alarm-color-dot--table {
  width: 13px;
  height: 13px;
  margin: 0;
  border-radius: 999px;
}

.alarm-level-name {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
}

.alarm-level-name__title {
  color: #07133f;
  font-weight: 800;
  line-height: 1.2;
}

.alarm-level-name__tag {
  border-radius: 4px !important;
  font-size: 0.68rem !important;
  font-weight: 800 !important;
  margin-top: 5px;
}

.alarm-level-name__subtitle {
  color: #64748b;
  font-size: 0.76rem;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.alarm-description-cell {
  max-width: 270px;
  color: #26395f;
  font-size: 0.8rem;
  font-weight: 500;
  line-height: 1.45;
}

.alarm-code-chip {
  border-radius: 4px !important;
  font-size: 0.72rem !important;
  font-weight: 900 !important;
}

.alarm-catalog-value-chip {
  border-radius: 6px !important;
  font-size: 0.72rem !important;
  font-weight: 900 !important;
}

.alarm-color-cell {
  display: flex;
  align-items: center;
  gap: 9px;
}

.alarm-color-cell__name {
  color: #07133f;
  font-size: 0.78rem;
  font-weight: 850;
}

.alarm-color-cell__hex {
  color: #314266;
  font-size: 0.74rem;
  font-weight: 750;
}

.alarm-priority-cell,
.alarm-schedule-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  color: #314266;
  font-size: 0.78rem;
  font-weight: 800;
}

.alarm-priority-cell__number {
  min-width: 28px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-weight: 900;
}

.alarm-schedule-cell__hint {
  color: #43557a;
  font-size: 0.7rem;
  font-weight: 800;
  max-width: 120px;
  line-height: 1.25;
}

.alarm-days-cell {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

.alarm-days-cell__day {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 25px;
  height: 25px;
  border-radius: 5px;
  color: #94a3b8;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  font-size: 0.7rem;
  font-weight: 800;
}

.alarm-days-cell__day--active {
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.36);
}

.alarm-levels-table::v-deep th {
  color: #07133f !important;
  font-weight: 700 !important;
  font-size: 0.78rem !important;
  height: 54px !important;
  background: #fbfcff !important;
}

.alarm-levels-table::v-deep td {
  height: 86px !important;
  border-bottom: 1px solid #e8edf5 !important;
}

.alarm-actions-cell {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 82px;
}

.estado-switch {
  display: inline-flex;
  margin-top: 0;
  padding-top: 0;
}

.asoc-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.25;
}
.asoc-cell__count {
  font-weight: 600;
  color: #334e68;
}
.asoc-cell__link {
  font-size: 12px;
  color: #1976d2;
  cursor: pointer;
}
.asoc-cell__link:hover {
  text-decoration: underline;
}
.asoc-cell__empty {
  font-size: 12px;
  color: #94a3b8;
}
.depend-dialog__title {
  font-weight: 700;
  color: #b45309;
  word-break: normal;
}
.depend-dialog__list {
  margin: 0;
  padding-left: 18px;
}
.depend-dialog__list li {
  margin-bottom: 2px;
}

.regla-desactivar-card {
  border: 1px solid #fde6bd;
  border-left: 4px solid #f59e0b;
  border-radius: 10px;
  padding: 12px 16px;
  background: #fffaf0;
}
.regla-desactivar-card__name {
  font-weight: 700;
  color: #102a43;
}
.regla-desactivar-card__obj {
  font-size: 12px;
  color: #7b8794;
  margin-top: 2px;
}

.alarm-action-btn {
  width: 32px !important;
  height: 32px !important;
  border-radius: 6px !important;
  border: 1px solid currentColor !important;
  opacity: 1 !important;
}

.alarm-action-btn::v-deep .v-icon {
  opacity: 1 !important;
  visibility: visible !important;
}

.alarm-action-btn--edit {
  color: #0d47a1 !important;
  background: #e8f1ff !important;
}

.alarm-action-btn--delete {
  color: #c62828 !important;
  background: #fdecec !important;
}

.alarm-dialog-card {
  border-radius: 18px !important;
  overflow: hidden;
  background: #ffffff !important;
}

.alarm-dialog-card--featured {
  border: 1px solid #e6ebf2 !important;
  box-shadow: 0 6px 32px -18px rgba(15, 39, 78, 0.18) !important;
}

.alarm-dialog-card__header {
  padding: 22px 24px 12px !important;
}

.alarm-dialog-hero {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 20px 22px 18px;
  border-bottom: 1px solid #eef1f6;
  background: #ffffff;
}

.alarm-dialog-hero__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 11px;
  color: #8b5cf6;
  background: rgba(139, 92, 246, 0.12);
  border: 0;
  box-shadow: none;
  flex: 0 0 38px;
  margin-top: 2px;
}

.alarm-dialog-hero__icon .v-icon {
  color: #8b5cf6 !important;
  font-size: 22px !important;
}

.alarm-dialog-hero__copy {
  min-width: 0;
}

.alarm-dialog-hero__step {
  color: #8b5cf6;
  font-size: 12.5px;
  font-weight: 800;
  letter-spacing: 0.14em;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.alarm-dialog-card__body {
  padding: 20px 22px 14px !important;
}

.alarm-dialog-section-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #4c1d95;
  font-size: 12.5px;
  font-weight: 800;
  letter-spacing: 0.14em;
  margin-bottom: 12px;
  text-transform: uppercase;
}

.alarm-dialog-section-label::before {
  content: "";
  width: 18px;
  height: 3px;
  border-radius: 999px;
  background: #8b5cf6;
}

.alarm-dialog-section-label--spaced {
  margin-top: 8px;
}

.alarm-dialog-card--featured::v-deep .v-input--is-focused fieldset {
  border-width: 2px;
  border-color: #8b5cf6 !important;
}

.alarm-dialog-card--featured::v-deep .v-text-field--outlined fieldset {
  border-color: #dbe4f0;
}

.alarm-dialog-card__title {
  color: #0f172a;
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.alarm-dialog-card__subtitle {
  color: #64748b;
  font-size: 13px;
  margin-top: 6px;
  line-height: 1.5;
  max-width: 720px;
}

.alarm-dialog-card__actions {
  padding: 14px 22px 20px !important;
  border-top: 1px solid #eef1f6;
  background: #ffffff;
}

.alarm-dialog-card__actions--featured {
  border-top: 1px solid #eef1f6;
  background: #ffffff;
  padding: 14px 22px 20px !important;
}

.alarm-dialog-card__actions--featured .v-btn {
  border-radius: 10px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
}

.alarm-dialog-card__actions .v-btn {
  border-radius: 10px !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
}

.alarm-dialog-card__actions .v-btn.v-btn--has-bg.primary,
.alarm-dialog-card__actions .v-btn.primary {
  background: #8b5cf6 !important;
  box-shadow: 0 4px 14px -4px rgba(139, 92, 246, 0.5) !important;
}

.alarm-dialog-card__actions .v-btn.primary:hover {
  background: #7c3aed !important;
  box-shadow: 0 6px 18px -4px rgba(139, 92, 246, 0.55) !important;
}

.alarm-dialog-card__actions .v-btn--text {
  color: #475569 !important;
}

.rule-dialog {
  border-radius: 16px !important;
  background: #ffffff !important;
  overflow: hidden;
  color: #0f172a;
}

.rule-dialog__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  padding: 24px 26px 20px;
  border-top: 4px solid #8b5cf6;
  border-bottom: 1px solid #e6ebf2;
  background: #ffffff;
}

.rule-dialog__title {
  margin: 0;
  color: #18205f;
  font-size: 25px;
  font-weight: 900;
  line-height: 1.08;
}

.rule-dialog__subtitle {
  margin: 10px 0 0;
  color: #33406f;
  font-size: 13px;
  font-weight: 600;
}

.rule-dialog__actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.rule-dialog__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  min-width: 118px;
  height: 36px;
  padding: 0 18px;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  border: 1px solid transparent;
  transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease;
}

.rule-dialog__btn:active:not(:disabled) {
  transform: translateY(1px);
}

.rule-dialog__btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.rule-dialog__btn--ghost {
  color: #18205f;
  background: #ffffff;
  border-color: #d9e0f2;
}

.rule-dialog__btn--ghost >>> .v-icon {
  color: #18205f !important;
}

.rule-dialog__btn--primary {
  color: #ffffff;
  background: linear-gradient(135deg, #ff6d00, #ff3d00);
  box-shadow: 0 10px 20px -12px rgba(255, 61, 0, 0.95);
}

.rule-dialog__btn--primary >>> .v-icon {
  color: #ffffff !important;
}

.rule-dialog__body {
  padding: 0 !important;
}

.rule-dialog__content {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 290px;
  gap: 18px;
  padding: 20px 22px 24px;
  background: #ffffff;
}

.rule-dialog__form {
  min-width: 0;
}

.rule-dialog__form .v-input {
  font-size: 13px;
}

.rule-dialog__form ::v-deep .v-input__slot {
  min-height: 36px !important;
}

.rule-dialog__form ::v-deep fieldset {
  border-color: #dbe4f0 !important;
}

.rule-dialog__form ::v-deep .v-input--is-focused fieldset {
  border-color: #ff6d00 !important;
  border-width: 1px !important;
}

.rule-source-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 28px;
  margin-bottom: 16px;
}

.rule-source-card {
  display: grid;
  grid-template-columns: 22px 56px minmax(0, 1fr);
  align-items: center;
  gap: 16px;
  min-height: 92px;
  padding: 16px 18px;
  border: 1px solid #dbe4f0;
  border-radius: 6px;
  background: #ffffff;
  color: #18205f;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.rule-source-card--active {
  border-color: #ff6d00;
  background: rgba(255, 109, 0, 0.045);
  box-shadow: inset 0 0 0 1px rgba(255, 109, 0, 0.20);
}

.rule-source-card__radio {
  width: 16px;
  height: 16px;
  border: 1px solid #cbd5e1;
  border-radius: 50%;
  position: relative;
}

.rule-source-card--active .rule-source-card__radio {
  border-color: #ff6d00;
}

.rule-source-card--active .rule-source-card__radio::after {
  content: "";
  position: absolute;
  inset: 4px;
  border-radius: 50%;
  background: #ff6d00;
}

.rule-source-card__icon {
  width: 56px;
  height: 56px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
}

.rule-source-card--active .rule-source-card__icon {
  background: rgba(255, 109, 0, 0.10);
}

.rule-source-card__icon >>> .v-icon {
  color: #65729a !important;
  font-size: 31px !important;
}

.rule-source-card--active .rule-source-card__icon >>> .v-icon {
  color: #ff6d00 !important;
}

.rule-source-card strong {
  display: block;
  color: #18205f;
  font-size: 13px;
  font-weight: 900;
  margin-bottom: 6px;
}

.rule-source-card small {
  display: block;
  color: #34416e;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
}

.rule-field__label {
  display: block;
  color: #18205f;
  font-size: 12px;
  font-weight: 900;
  margin-bottom: 7px;
}

.rule-field__label span {
  color: #ff3d00;
}

.rule-help {
  display: flex;
  align-items: center;
  gap: 9px;
  min-height: 30px;
  margin-top: 10px;
  padding: 8px 11px;
  border-radius: 5px;
  font-size: 11.5px;
  font-weight: 700;
  line-height: 1.3;
}

.rule-help--info {
  color: #283a78;
  background: #f1f4ff;
  border: 1px solid #dfe6ff;
}

.rule-help--warning {
  color: #a44000;
  background: #fff3ed;
  border: 1px solid #ffe1d4;
}

.rule-help--success {
  color: #08734e;
  background: #edfdf4;
  border: 1px solid #d8f7e5;
}

.rule-help--inline {
  align-self: end;
  margin-top: 0;
}

.rule-summary {
  align-self: start;
  position: sticky;
  top: 0;
  min-width: 0;
  padding: 20px 18px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #ffffff;
  box-shadow: 0 12px 28px -24px rgba(15, 23, 42, 0.28);
}

.rule-summary__title {
  margin: 0 0 10px;
  color: #ff4d00;
  font-size: 14px;
  font-weight: 900;
}

.rule-summary__subtitle {
  margin: 0 0 20px;
  color: #34416e;
  font-size: 12px;
  line-height: 1.45;
  font-weight: 600;
}

.rule-summary__item {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr);
  gap: 12px;
  align-items: start;
  margin-bottom: 18px;
}

.rule-summary__item >>> .v-icon {
  color: #5f6b98 !important;
  font-size: 21px !important;
}

.rule-summary__item strong {
  display: block;
  color: #18205f;
  font-size: 12px;
  font-weight: 900;
  margin-bottom: 4px;
}

.rule-summary__item span {
  display: block;
  color: #8b96b8;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.35;
}

.rule-summary__item .rule-summary__accent {
  color: #ff4d00;
}

.rule-summary__note {
  display: flex;
  gap: 9px;
  margin-top: 24px;
  padding: 12px;
  border-radius: 6px;
  background: #fff8eb;
  border: 1px solid #ffe1ad;
  color: #5f4a23;
  font-size: 11.5px;
  font-weight: 700;
  line-height: 1.35;
}

.rule-summary__note >>> .v-icon {
  color: #f59e0b !important;
  flex-shrink: 0;
}

.alarm-palette-label {
  color: #334155;
  font-size: 0.78rem;
  font-weight: 800;
  margin-bottom: 8px;
}

.alarm-rule-type {
  margin-bottom: 16px;
}

.alarm-rule-type__step {
  color: #8b5cf6;
  font-size: 12.5px;
  font-weight: 800;
  letter-spacing: 0.14em;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.alarm-rule-type__hint {
  color: #64748b;
  font-size: 0.84rem;
  font-weight: 600;
  margin-bottom: 14px;
}

.alarm-rule-type__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.alarm-rule-type__card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  min-height: 110px;
  padding: 18px 18px;
  border: 1px solid #e6ebf2;
  border-radius: 14px;
  background: #ffffff;
  color: #0f172a;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.16s ease, box-shadow 0.16s ease, background 0.16s ease;
}

.alarm-rule-type__card--active {
  border-color: rgba(239, 108, 0, 0.42);
  box-shadow: 0 0 0 2px rgba(239, 108, 0, 0.18), 0 10px 24px rgba(239, 108, 0, 0.14);
  background: rgba(239, 108, 0, 0.04);
}

.alarm-rule-type__main {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.alarm-rule-type__icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(239, 108, 0, 0.10);
  flex: 0 0 auto;
  transition: background 0.2s ease, transform 0.2s ease;
}

.alarm-rule-type__card:hover .alarm-rule-type__icon-wrap {
  background: rgba(239, 108, 0, 0.16);
  transform: scale(1.04);
}

.alarm-rule-type__card--active .alarm-rule-type__icon-wrap {
  background: linear-gradient(135deg, #ef6c00 0%, #e65100 100%);
  box-shadow: 0 6px 16px -4px rgba(239, 108, 0, 0.45);
}

.alarm-rule-type__icon {
  color: #ef6c00 !important;
  font-size: 28px !important;
}

.alarm-rule-type__card--active .alarm-rule-type__icon {
  color: #ffffff !important;
}

.alarm-rule-type__title {
  color: #0f172a;
  font-size: 0.98rem;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 6px;
}

.alarm-rule-type__description {
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.35;
}

.alarm-rule-type__radio {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: 2px solid #cbd5e1;
  border-radius: 50%;
  flex: 0 0 auto;
}

.alarm-rule-type__card--active .alarm-rule-type__radio {
  border-color: #ef6c00;
}

.alarm-rule-type__radio span {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ef6c00;
}

.alarm-user-picker {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 140px minmax(0, 1.25fr);
  gap: 28px;
  align-items: center;
}

.alarm-user-picker--loading {
  opacity: 0.72;
  pointer-events: none;
}

.alarm-user-picker__panel {
  min-width: 0;
}

.alarm-user-picker__title {
  color: #8b5cf6;
  font-size: 12.5px;
  font-weight: 800;
  letter-spacing: 0.14em;
  margin-bottom: 12px;
  text-transform: uppercase;
}

.alarm-user-picker__search {
  margin-bottom: 0;
}

.alarm-user-picker__list {
  min-height: 320px;
  max-height: 360px;
  overflow-y: auto;
  border: 1px solid #dbe4f0;
  border-radius: 8px;
  background: #ffffff;
}

.alarm-user-picker__list--selected {
  min-height: 372px;
}

.alarm-user-picker__row {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr) 100px 32px;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 58px;
  padding: 10px 14px;
  border: 0;
  border-bottom: 1px solid #e2e8f0;
  background: #ffffff;
  color: #172554;
  cursor: pointer;
  text-align: left;
}

.alarm-user-picker__row:last-child {
  border-bottom: 0;
}

.alarm-user-picker__row:hover,
.alarm-user-picker__row--checked {
  background: #f8fbff;
}

.alarm-user-picker__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #071a44;
  color: #ffffff;
  font-size: 0.68rem;
  font-weight: 800;
}

.alarm-user-picker__name {
  min-width: 0;
  color: #172554;
  font-size: 0.86rem;
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.alarm-user-picker__role {
  color: #334155;
  font-size: 0.72rem;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.alarm-user-picker__check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 2px solid #cbd5e1;
  border-radius: 4px;
}

.alarm-user-picker__row--checked .alarm-user-picker__check {
  border-color: #2563eb;
}

.alarm-user-picker__actions {
  display: grid;
  gap: 18px;
  justify-items: stretch;
}

.alarm-user-picker__actions .v-btn {
  min-height: 44px;
  text-transform: none;
  font-weight: 800;
  letter-spacing: 0;
}

.alarm-user-picker__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  color: #64748b;
  font-size: 0.85rem;
  font-weight: 700;
  text-align: center;
  padding: 18px;
}

.alarm-user-picker__note {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 10px 14px;
  border: 1px solid #dbe4f0;
  border-top: 0;
  border-radius: 0 0 8px 8px;
  background: #f8fbff;
  color: #334155;
  font-size: 0.76rem;
  font-weight: 700;
}

.alarm-palette {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.alarm-palette__item {
  display: flex;
  align-items: center;
  gap: 9px;
  min-height: 54px;
  padding: 8px 10px;
  border: 1px solid #dbe4f0;
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
}

.alarm-palette__item--active,
.alarm-palette__item:hover {
  border-color: #2563eb;
  box-shadow: 0 8px 18px rgba(37, 99, 235, 0.12);
  transform: translateY(-1px);
}

.alarm-palette__swatch {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  flex: 0 0 22px;
}

.alarm-palette__name {
  color: #07133f;
  font-size: 0.8rem;
  font-weight: 850;
}

.alarm-field-error {
  color: #d50000;
  font-size: 0.75rem;
  margin-top: 6px;
}

.alarm-days-editor {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px 12px;
  padding: 10px 12px;
  border: 1px solid #dbe4f0;
  border-radius: 8px;
  background: #f8fafc;
}

.alarm-days-editor__item {
  margin-top: 0 !important;
}

/* ============================================================
   ALERTAS PAGE — clean, flat (matches Catalogo.vue)
   ============================================================ */
.alertas-page {
  padding: 18px 22px 32px;
  background: #f2f7ff;
  min-height: 100%;
}

.alertas-page__header {
  margin-bottom: 20px;
}

.alertas-page__title {
  margin: 0;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #0a1929;
  line-height: 1.1;
  background: linear-gradient(120deg, #0a1929 0%, #1976d2 70%, #00acc1 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.alertas-page__subtitle {
  margin: 6px 0 0;
  color: #475569;
  font-size: 13.5px;
  line-height: 1.5;
  max-width: 640px;
}

.alertas-hero {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  padding: 22px 26px;
  border-radius: 18px;
  background:
    linear-gradient(135deg, rgba(25, 118, 210, 0.04) 0%, rgba(0, 188, 212, 0.03) 50%, rgba(142, 36, 170, 0.04) 100%),
    #ffffff;
  border: 1px solid #e6ebf2;
  box-shadow: 0 1px 2px rgba(15, 39, 78, 0.04);
  overflow: hidden;
}

.alertas-hero::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #1976d2 0%, #00897b 33%, #8e24aa 66%, #ef6c00 100%);
}

.alertas-hero__main {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  flex: 1;
  min-width: 0;
}

.alertas-hero__icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  color: #ffffff;
  flex-shrink: 0;
  box-shadow: 0 4px 12px -4px rgba(25, 118, 210, 0.45);
}

.alertas-hero__icon >>> .v-icon {
  color: #ffffff !important;
}

.alertas-hero__text {
  flex: 1;
  min-width: 0;
}

.alertas-hero__stats {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 4px 4px 4px 18px;
  border-left: 1px solid #eef1f6;
  flex-shrink: 0;
}

.alertas-hero__stat {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.alertas-hero__stat-value {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
  line-height: 1;
}

.alertas-hero__stat-label {
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
}

.alertas-hero__stat-divider {
  width: 1px;
  height: 32px;
  background: #eef1f6;
}

/* ---------- Navigation cards ---------- */
.alertas-nav {
  margin-bottom: 20px;
}

.alertas-nav__label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: #475569;
  margin-bottom: 14px;
  text-transform: uppercase;
}

.alertas-nav__grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.alertas-nav-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px;
  background: #ffffff;
  border: 1px solid #dce9f8;
  border-radius: 16px;
  cursor: pointer;
  transition: transform 0.22s cubic-bezier(0.4, 0, 0.2, 1),
              box-shadow 0.22s cubic-bezier(0.4, 0, 0.2, 1),
              border-color 0.22s ease;
  outline: none;
  overflow: hidden;
}

.alertas-nav-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 32px -16px rgba(15, 39, 78, 0.22),
              0 4px 8px -4px rgba(15, 39, 78, 0.08);
}

.alertas-nav-card:focus-visible {
  box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.22);
}

.alertas-nav-card__top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.alertas-nav-card__icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.25s ease;
}

.alertas-nav-card:hover .alertas-nav-card__icon {
  transform: scale(1.05) rotate(-3deg);
}

.alertas-nav-card__body {
  flex: 1;
  min-width: 0;
}

.alertas-nav-card__title {
  margin: 0;
  font-size: 14.5px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.005em;
}

.alertas-nav-card__description {
  margin: 4px 0 10px;
  font-size: 12.5px;
  color: #64748b;
  line-height: 1.45;
}

.alertas-nav-card__pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 700;
}

.alertas-nav-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid #eef1f6;
  gap: 8px;
}

.alertas-nav-card__count {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  min-width: 0;
}

.alertas-nav-card__count-num {
  font-size: 19px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.01em;
  line-height: 1;
}

.alertas-nav-card__count-label {
  font-size: 11px;
  font-weight: 500;
  color: #94a3b8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.alertas-nav-card__cta {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  transition: gap 0.2s ease;
}

.alertas-nav-card:hover .alertas-nav-card__cta { gap: 6px; }

.alertas-nav-card__cta >>> .v-icon {
  font-size: 14px !important;
  transition: transform 0.2s ease;
}

.alertas-nav-card__glow {
  position: absolute;
  inset: -50% -50% auto auto;
  width: 180px;
  height: 180px;
  border-radius: 50%;
  opacity: 0;
  filter: blur(40px);
  pointer-events: none;
  transition: opacity 0.3s ease;
}

.alertas-nav-card:hover .alertas-nav-card__glow {
  opacity: 0.5;
}

.alertas-nav-card--active .alertas-nav-card__glow {
  opacity: 0.7;
}

/* ---- color variants (flat tints, no gradients) ---- */
.alertas-nav-card--blue .alertas-nav-card__icon {
  background: linear-gradient(135deg, #d6efff 0%, #8fd3ff 100%);
}
.alertas-nav-card--blue .alertas-nav-card__icon >>> .v-icon { color: #0277bd !important; }
.alertas-nav-card--blue .alertas-nav-card__pill {
  background: rgba(3, 169, 244, 0.16);
  color: #0277bd;
}
.alertas-nav-card--blue .alertas-nav-card__pill >>> .v-icon { color: #0277bd !important; }
.alertas-nav-card--blue .alertas-nav-card__cta { color: #0277bd; }
.alertas-nav-card--blue .alertas-nav-card__cta >>> .v-icon { color: #0277bd !important; }
.alertas-nav-card--blue .alertas-nav-card__glow { background: #03a9f4; }
.alertas-nav-card--blue.alertas-nav-card--active {
  border-color: #03a9f4;
  background: linear-gradient(180deg, rgba(3, 169, 244, 0.13) 0%, #ffffff 100%);
  box-shadow: 0 0 0 2px rgba(3, 169, 244, 0.24),
              0 14px 34px -16px rgba(3, 169, 244, 0.58);
}

.alertas-nav-card--green .alertas-nav-card__icon {
  background: linear-gradient(135deg, #d9fff0 0%, #8ff2c2 100%);
}
.alertas-nav-card--green .alertas-nav-card__icon >>> .v-icon { color: #008c5a !important; }
.alertas-nav-card--green .alertas-nav-card__pill {
  background: rgba(0, 200, 83, 0.16);
  color: #008c5a;
}
.alertas-nav-card--green .alertas-nav-card__pill >>> .v-icon { color: #008c5a !important; }
.alertas-nav-card--green .alertas-nav-card__cta { color: #008c5a; }
.alertas-nav-card--green .alertas-nav-card__cta >>> .v-icon { color: #008c5a !important; }
.alertas-nav-card--green .alertas-nav-card__glow { background: #00c853; }
.alertas-nav-card--green.alertas-nav-card--active {
  border-color: #00c853;
  background: linear-gradient(180deg, rgba(0, 200, 83, 0.13) 0%, #ffffff 100%);
  box-shadow: 0 0 0 2px rgba(0, 200, 83, 0.24),
              0 14px 34px -16px rgba(0, 200, 83, 0.55);
}

.alertas-nav-card--purple .alertas-nav-card__icon {
  background: linear-gradient(135deg, #f8d7ff 0%, #e18cff 100%);
}
.alertas-nav-card--purple .alertas-nav-card__icon >>> .v-icon { color: #8e24aa !important; }
.alertas-nav-card--purple .alertas-nav-card__pill {
  background: rgba(213, 0, 249, 0.15);
  color: #8e24aa;
}
.alertas-nav-card--purple .alertas-nav-card__pill >>> .v-icon { color: #8e24aa !important; }
.alertas-nav-card--purple .alertas-nav-card__cta { color: #8e24aa; }
.alertas-nav-card--purple .alertas-nav-card__cta >>> .v-icon { color: #8e24aa !important; }
.alertas-nav-card--purple .alertas-nav-card__glow { background: #d500f9; }
.alertas-nav-card--purple.alertas-nav-card--active {
  border-color: #d500f9;
  background: linear-gradient(180deg, rgba(213, 0, 249, 0.12) 0%, #ffffff 100%);
  box-shadow: 0 0 0 2px rgba(213, 0, 249, 0.22),
              0 14px 34px -16px rgba(213, 0, 249, 0.52);
}

.alertas-nav-card--orange .alertas-nav-card__icon {
  background: linear-gradient(135deg, #ffe2c5 0%, #ffb06b 100%);
}
.alertas-nav-card--orange .alertas-nav-card__icon >>> .v-icon { color: #e65100 !important; }
.alertas-nav-card--orange .alertas-nav-card__pill {
  background: rgba(255, 109, 0, 0.16);
  color: #e65100;
}
.alertas-nav-card--orange .alertas-nav-card__pill >>> .v-icon { color: #e65100 !important; }
.alertas-nav-card--orange .alertas-nav-card__cta { color: #e65100; }
.alertas-nav-card--orange .alertas-nav-card__cta >>> .v-icon { color: #e65100 !important; }
.alertas-nav-card--orange .alertas-nav-card__glow { background: #ff6d00; }
.alertas-nav-card--orange.alertas-nav-card--active {
  border-color: #ff6d00;
  background: linear-gradient(180deg, rgba(255, 109, 0, 0.13) 0%, #ffffff 100%);
  box-shadow: 0 0 0 2px rgba(255, 109, 0, 0.24),
              0 14px 34px -16px rgba(255, 109, 0, 0.56);
}

/* ---------- Section header ---------- */
.alertas-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 14px;
  padding: 4px 4px 4px 14px;
  position: relative;
}

.alertas-section__header::before {
  content: "";
  position: absolute;
  left: 0;
  top: 4px;
  bottom: 4px;
  width: 3px;
  border-radius: 0 2px 2px 0;
}

.alertas-section__header--blue::before { background: #03a9f4; }
.alertas-section__header--green::before { background: #00c853; }
.alertas-section__header--purple::before { background: #d500f9; }
.alertas-section__header--orange::before { background: #ff6d00; }

.alertas-section__title-block {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.alertas-section__icon {
  width: 38px;
  height: 38px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.25s ease, color 0.25s ease;
}

.alertas-section__header--blue .alertas-section__icon {
  background: linear-gradient(135deg, #03a9f4, #1976d2);
}
.alertas-section__header--green .alertas-section__icon {
  background: linear-gradient(135deg, #00c853, #00a887);
}
.alertas-section__header--purple .alertas-section__icon {
  background: linear-gradient(135deg, #d500f9, #8e24aa);
}
.alertas-section__header--orange .alertas-section__icon {
  background: linear-gradient(135deg, #ff9800, #ff5722);
}

.alertas-section__icon >>> .v-icon { color: #ffffff !important; }

.alertas-section__label {
  font-size: 16px;
  font-weight: 800;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  margin: 0 0 4px;
  line-height: 1.1;
}

.alertas-section__header--blue .alertas-section__label { color: #0277bd; }
.alertas-section__header--green .alertas-section__label { color: #008c5a; }
.alertas-section__header--purple .alertas-section__label { color: #8e24aa; }
.alertas-section__header--orange .alertas-section__label { color: #e65100; }

.alertas-section__description {
  margin: 0;
  font-size: 13px;
  color: #64748b;
  line-height: 1.45;
}

.alertas-section__cta {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease;
}

.alertas-section__cta:active { transform: translateY(1px); }

.alertas-section__cta--blue { background: linear-gradient(135deg, #03a9f4, #1976d2); box-shadow: 0 8px 18px -8px rgba(3, 169, 244, 0.75); }
.alertas-section__cta--blue:hover { box-shadow: 0 10px 22px -8px rgba(3, 169, 244, 0.85); }
.alertas-section__cta--green { background: linear-gradient(135deg, #00c853, #00a887); box-shadow: 0 8px 18px -8px rgba(0, 200, 83, 0.72); }
.alertas-section__cta--green:hover { box-shadow: 0 10px 22px -8px rgba(0, 200, 83, 0.82); }
.alertas-section__cta--purple { background: linear-gradient(135deg, #d500f9, #8e24aa); box-shadow: 0 8px 18px -8px rgba(213, 0, 249, 0.72); }
.alertas-section__cta--purple:hover { box-shadow: 0 10px 22px -8px rgba(213, 0, 249, 0.82); }
.alertas-section__cta--orange { background: linear-gradient(135deg, #ff9800, #ff5722); box-shadow: 0 8px 18px -8px rgba(255, 109, 0, 0.74); }
.alertas-section__cta--orange:hover { box-shadow: 0 10px 22px -8px rgba(255, 109, 0, 0.84); }

.alertas-section__cta >>> .v-icon { color: #fff !important; }

/* ---------- Table card polish ---------- */
.alertas-table-card {
  border-radius: 16px !important;
  border-color: #e6ebf2 !important;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(15, 39, 78, 0.04);
}

.alertas-table-card >>> .v-data-table tbody tr {
  transition: background 0.15s ease;
}
.alertas-table-card >>> .v-data-table tbody tr:hover {
  background: #f8fafc !important;
}
.alertas-table-card >>> .v-data-table th {
  font-weight: 600 !important;
  font-size: 11.5px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em !important;
  color: #64748b !important;
  background: #fafbfc;
}

/* ============================================================
   PROGRAMACION FORM VIEW (full page)
   ============================================================ */
.prog-form {
  position: relative;
  max-width: 1040px;
  margin: 0 auto;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 16px;
  padding: 20px 22px 22px;
  box-shadow: 0 6px 32px -18px rgba(15, 39, 78, 0.18);
}

.prog-form--dialog {
  max-width: none;
  margin: 0;
  border: 0;
  border-radius: 18px;
  box-shadow: none;
}

.prog-form__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  padding-bottom: 18px;
  margin-bottom: 20px;
  border-bottom: 1px solid #eef1f6;
}

.prog-form__breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: #94a3b8;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.prog-form__breadcrumb >>> .v-icon {
  font-size: 14px !important;
  color: #cbd5e1 !important;
}

.prog-form__breadcrumb-sep {
  opacity: 0.5;
}

.prog-form__breadcrumb-current {
  color: #8b5cf6;
  font-weight: 600;
}

.prog-form__back {
  color: #475569;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  text-decoration: none;
  transition: color 0.15s ease;
}

.prog-form__back:hover { color: #8b5cf6; }
.prog-form__back >>> .v-icon { color: currentColor !important; }

.prog-form__title {
  margin: 0;
  font-size: 24px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.prog-form__subtitle {
  margin: 6px 0 0;
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
  max-width: 720px;
}

.prog-form__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.prog-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.1s ease;
  border: 1px solid transparent;
}

.prog-btn:active:not(:disabled) { transform: translateY(1px); }
.prog-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.prog-btn--ghost {
  background: #ffffff;
  color: #475569;
  border-color: #e2e8f0;
}
.prog-btn--ghost:hover:not(:disabled) {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.prog-btn--primary {
  background: #8b5cf6;
  color: #ffffff;
  box-shadow: 0 4px 14px -4px rgba(139, 92, 246, 0.5);
}
.prog-btn--primary:hover:not(:disabled) {
  background: #7c3aed;
  box-shadow: 0 6px 18px -4px rgba(139, 92, 246, 0.55);
}
.prog-btn--primary >>> .v-icon { color: #fff !important; }

/* sections inside the form */
.prog-form__section {
  margin-bottom: 22px;
}

.prog-form__section-title {
  font-size: 12.5px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #8b5cf6;
  margin: 0 0 12px;
}

.prog-form__section-subtitle {
  margin: -6px 0 14px;
  font-size: 12.5px;
  color: #64748b;
}

.prog-form__grid {
  display: grid;
  grid-template-columns: 1.4fr 0.8fr;
  gap: 14px 18px;
}

.prog-form__field { display: flex; flex-direction: column; }
.prog-form__field--name { grid-column: 1; }
.prog-form__field--full { grid-column: 1 / -1; }

.prog-form__label {
  font-size: 12.5px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
}

.prog-form__required { color: #e53935; }

/* Split (frec + horario) cards */
.prog-form__split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 22px;
}

.prog-form__split-card {
  position: relative;
  padding: 18px 20px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(15, 39, 78, 0.04);
}

.prog-form__split-card::before {
  content: "";
  position: absolute;
  left: 0;
  top: 18px;
  bottom: 18px;
  width: 3px;
  background: #8b5cf6;
  border-radius: 0 2px 2px 0;
}

.prog-form__split-header {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 14px;
}

.prog-form__split-icon {
  width: 38px;
  height: 38px;
  border-radius: 11px;
  background: rgba(139, 92, 246, 0.12);
  color: #8b5cf6;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.prog-form__split-icon >>> .v-icon { color: #8b5cf6 !important; }

.prog-form__split-title {
  margin: 0;
  font-size: 12.5px;
  font-weight: 800;
  color: #8b5cf6;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.prog-form__split-description {
  margin: 4px 0 0;
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
}

.prog-form__note {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-top: 12px;
  padding: 10px 12px;
  background: rgba(139, 92, 246, 0.06);
  border: 1px solid rgba(139, 92, 246, 0.16);
  border-radius: 10px;
  font-size: 12px;
  color: #475569;
  line-height: 1.45;
}

.prog-form__note >>> .v-icon { color: #8b5cf6 !important; flex-shrink: 0; }

/* Summary */
.prog-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  padding: 16px;
  background: rgba(139, 92, 246, 0.04);
  border: 1px solid rgba(139, 92, 246, 0.22);
  border-radius: 14px;
}

.prog-summary__cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 14px 10px;
  background: #ffffff;
  border-radius: 10px;
  border: 1px solid #e6ebf2;
  text-align: center;
  transition: border-color 0.18s ease, transform 0.18s ease;
}

.prog-summary__cell:hover {
  border-color: rgba(139, 92, 246, 0.35);
  transform: translateY(-2px);
}

.prog-summary__icon {
  color: #8b5cf6 !important;
  font-size: 20px !important;
}

.prog-summary__label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #8b5cf6;
}

.prog-summary__value {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
  word-break: break-word;
}

.prog-summary__value--empty {
  color: #94a3b8;
  font-weight: 500;
  font-style: italic;
}

.prog-form__footer {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #eef1f6;
  font-size: 12px;
  color: #64748b;
}

.prog-form__footer >>> .v-icon { color: #94a3b8 !important; }

.prog-form-fade-enter-active,
.prog-form-fade-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.prog-form-fade-enter,
.prog-form-fade-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

/* ---------- Responsive ---------- */
@media (max-width: 1200px) {
  .alertas-nav__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .prog-summary { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 900px) {
  .alertas-page { padding: 12px 14px 24px; }
  .alertas-page__title { font-size: 26px; }
  .alertas-hero {
    flex-direction: column;
    align-items: stretch;
    padding: 18px;
  }
  .alertas-hero__stats {
    padding-left: 0;
    border-left: none;
    border-top: 1px solid #eef1f6;
    padding-top: 12px;
    justify-content: space-around;
  }
  .alertas-hero__stat {
    align-items: flex-start;
  }
  .alertas-section__header { flex-direction: column; align-items: stretch; }
  .alertas-section__cta { justify-content: center; }
  .prog-form__grid { grid-template-columns: 1fr; }
  .prog-form__split { grid-template-columns: 1fr; }
  .prog-form__header { flex-direction: column; align-items: stretch; }
  .prog-form__actions { justify-content: stretch; }
  .prog-form__actions > * { flex: 1; justify-content: center; }
}

@media (max-width: 600px) {
  .alertas-nav__grid { grid-template-columns: 1fr; }
  .prog-summary { grid-template-columns: 1fr; }
}

.alarm-fk-subtitle {
  font-size: 11px !important;
  color: #64748b !important;
  letter-spacing: 0.02em;
}

.programacion-preview {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  margin-top: 4px;
  border-radius: 12px;
  background: rgba(139, 92, 246, 0.04);
  border: 1px solid rgba(139, 92, 246, 0.22);
}

.programacion-preview__chip {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid rgba(139, 92, 246, 0.18);
  flex: 1;
  min-width: 0;
}

.programacion-preview__label {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #8b5cf6;
}

.programacion-preview__value {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 320px;
}

.programacion-preview__link {
  flex-shrink: 0;
}

@media (max-width: 960px) {
  .alarm-builder {
    padding: 14px;
  }

  .alarm-builder__header {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .alarm-builder__title {
    font-size: 1.55rem;
  }

  .alarm-sidebar {
    position: static;
  }

  .alarm-sidebar__steps {
    grid-template-columns: 1fr;
  }

  .alarm-table-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .alarm-table-header__button {
    width: 100%;
  }

  .alarm-palette {
    grid-template-columns: 1fr;
  }

  .alarm-dialog-hero {
    align-items: flex-start;
    padding: 18px;
  }

  .alarm-dialog-card__body {
    padding: 18px 18px 10px !important;
  }

  .alarm-rule-type__grid {
    grid-template-columns: 1fr;
  }

  .alarm-user-picker {
    grid-template-columns: 1fr;
    gap: 18px;
  }

  .alarm-user-picker__actions {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .alarm-days-editor {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* ============================================================
   UNIFIED DIALOG SYSTEM
   All 4 create/edit dialogs share this structure.
   Each scope sets --uni-accent (color) via inline style.
   ============================================================ */

.uni-dlg {
  --uni-accent: #1976d2;
  --uni-accent-soft: rgba(25, 118, 210, 0.10);
  --uni-accent-mid: rgba(25, 118, 210, 0.18);
  position: relative;
  background: #ffffff;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 30px 60px -28px rgba(15, 23, 42, 0.35);
}

/* Cool accent stripe on top of every unified dialog */
.uni-dlg::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: var(--uni-accent);
}

.uni-dlg__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid #eef1f6;
  position: relative;
}

.uni-dlg__title-row {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  flex: 1;
  min-width: 0;
}

.uni-dlg__icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--uni-accent-soft);
  flex-shrink: 0;
}

.uni-dlg__icon >>> .v-icon {
  color: var(--uni-accent) !important;
  font-size: 22px !important;
}

.uni-dlg__title-block { flex: 1; min-width: 0; }

.uni-dlg__eyebrow {
  display: inline-block;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--uni-accent);
  margin-bottom: 4px;
}

.uni-dlg__title {
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.015em;
  line-height: 1.2;
}

.uni-dlg__subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  color: #64748b;
  line-height: 1.45;
  max-width: 580px;
}

.uni-dlg__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.uni-dlg__btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 9px 16px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 10px;
  border: 1px solid transparent;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, border-color 0.15s ease,
              box-shadow 0.15s ease, transform 0.1s ease;
}

.uni-dlg__btn:active:not(:disabled) { transform: translateY(1px); }
.uni-dlg__btn:disabled { opacity: 0.6; cursor: not-allowed; }

.uni-dlg__btn--ghost {
  background: #ffffff;
  color: #475569;
  border-color: #e2e8f0;
}
.uni-dlg__btn--ghost:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.uni-dlg__btn--primary {
  background: var(--uni-accent);
  color: #ffffff;
  box-shadow: 0 4px 12px -4px var(--uni-accent-mid);
}
.uni-dlg__btn--primary:hover:not(:disabled) {
  filter: brightness(0.94);
  box-shadow: 0 6px 16px -4px var(--uni-accent-mid);
}
.uni-dlg__btn--primary >>> .v-icon { color: #ffffff !important; }

.uni-dlg__body {
  padding: 22px 24px 16px;
  max-height: 70vh;
  overflow-y: auto;
}

/* Numbered section blocks */
.uni-dlg__section {
  margin-bottom: 20px;
}

.uni-dlg__step {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
}

.uni-dlg__step-num {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
  background: var(--uni-accent-soft);
  color: var(--uni-accent);
  letter-spacing: 0;
}

.uni-dlg__step-label {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--uni-accent);
}

.uni-dlg__hint {
  margin: 0 0 12px 32px;
  font-size: 12.5px;
  color: #64748b;
  line-height: 1.45;
}

.uni-dlg__field-label {
  display: block;
  font-size: 12.5px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 6px;
}

.uni-dlg__required { color: #ef4444; font-weight: 700; }

/* Split (frec/horario, etc.) */
.uni-dlg__split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin: 8px 0;
}

.uni-dlg__split-card {
  position: relative;
  padding: 16px 18px 14px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(15, 39, 78, 0.04);
}

.uni-dlg__split-card::before {
  content: "";
  position: absolute;
  left: 0;
  top: 14px;
  bottom: 14px;
  width: 3px;
  background: var(--uni-accent);
  border-radius: 0 2px 2px 0;
}

.uni-dlg__split-header {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;
}

.uni-dlg__split-icon {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--uni-accent-soft);
  flex-shrink: 0;
}

.uni-dlg__split-icon >>> .v-icon { color: var(--uni-accent) !important; font-size: 17px !important; }

.uni-dlg__split-title {
  margin: 0;
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--uni-accent);
}

.uni-dlg__split-description {
  margin: 1px 0 0;
  font-size: 11.5px;
  color: #64748b;
  line-height: 1.35;
}

.uni-dlg__note {
  display: flex;
  gap: 6px;
  align-items: flex-start;
  margin-top: 8px;
  padding: 8px 10px;
  background: var(--uni-accent-soft);
  border-radius: 8px;
  font-size: 11.5px;
  color: #475569;
  line-height: 1.4;
}
.uni-dlg__note >>> .v-icon { color: var(--uni-accent) !important; flex-shrink: 0; }

/* Summary section (used in all 4 dialogs) */
.uni-dlg__summary {
  margin-top: 10px;
  padding: 16px;
  background: var(--uni-accent-soft);
  border: 1px solid var(--uni-accent-mid);
  border-radius: 14px;
}

.uni-dlg__summary-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  font-weight: 800;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--uni-accent);
  margin-bottom: 4px;
}

.uni-dlg__summary-title >>> .v-icon {
  color: var(--uni-accent) !important;
  font-size: 16px !important;
}

.uni-dlg__summary-hint {
  margin: 0 0 12px;
  font-size: 11.5px;
  color: #64748b;
  line-height: 1.4;
}

.uni-dlg__summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
}

.uni-dlg__summary-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 12px 10px;
  background: #ffffff;
  border-radius: 10px;
  border: 1px solid #e6ebf2;
  text-align: center;
  transition: transform 0.18s ease, border-color 0.18s ease;
}

.uni-dlg__summary-cell:hover {
  transform: translateY(-2px);
  border-color: var(--uni-accent);
}

.uni-dlg__summary-cell >>> .v-icon {
  color: var(--uni-accent) !important;
  font-size: 18px !important;
}

.uni-dlg__summary-label {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  color: var(--uni-accent);
}

.uni-dlg__summary-value {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.3;
  word-break: break-word;
}

.uni-dlg__summary-value--empty {
  color: #94a3b8;
  font-weight: 500;
  font-style: italic;
}

.uni-dlg__summary-swatch {
  display: inline-block;
  width: 18px;
  height: 18px;
  border-radius: 5px;
  margin-right: 4px;
  vertical-align: middle;
  border: 1px solid rgba(15, 39, 78, 0.08);
}

.uni-dlg__footer-note {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 10px 24px 18px;
  font-size: 11.5px;
  color: #94a3b8;
  border-top: 1px solid #eef1f6;
  margin-top: 4px;
}
.uni-dlg__footer-note >>> .v-icon { color: #94a3b8 !important; }

@media (max-width: 900px) {
  .uni-dlg__header { flex-direction: column; align-items: stretch; }
  .uni-dlg__actions { justify-content: stretch; }
  .uni-dlg__actions > * { flex: 1; justify-content: center; }
  .uni-dlg__split { grid-template-columns: 1fr; }
}

/* ============================================================
   CONFIGURAR SEVERIDADES — modal compact editor
   ============================================================ */
.severidades-page {
  background: #ffffff;
  padding: 22px 26px 24px;
  border-radius: 18px;
  border: 1px solid #e6ebf2;
  box-shadow: 0 30px 60px -28px rgba(15, 23, 42, 0.35);
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
}

.severidades-page::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #facc15 0%, #f59e0b 50%, #ef4444 100%);
  border-radius: 18px 18px 0 0;
}

.severidades-page__header {
  margin-bottom: 16px;
}

.severidades-page__back-row {
  margin-bottom: 14px;
}

.severidades-page__back {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 13px;
  font-weight: 600;
  color: #1976d2;
  cursor: pointer;
  text-decoration: none;
  transition: color 0.15s ease;
}

.severidades-page__back:hover { color: #1565c0; }

.severidades-page__back >>> .v-icon { color: currentColor !important; }

.severidades-page__title-row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.severidades-page__icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(250, 204, 21, 0.16) 0%, rgba(239, 68, 68, 0.14) 100%);
  flex-shrink: 0;
}

.severidades-page__icon >>> .v-icon {
  color: #b45309 !important;
  font-size: 22px !important;
}

.severidades-page__title-block { flex: 1; min-width: 0; }

.severidades-page__title {
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
  line-height: 1.15;
}

.severidades-page__subtitle {
  margin: 4px 0 0;
  font-size: 12.5px;
  color: #64748b;
  line-height: 1.45;
  max-width: 640px;
}

.severidades-page__actions {
  display: flex;
  gap: 10px;
  flex-shrink: 0;
}

.severidades-page__btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 10px 18px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 10px;
  border: 1px solid transparent;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s ease, border-color 0.15s ease,
              box-shadow 0.15s ease, transform 0.1s ease;
}

.severidades-page__btn:active:not(:disabled) { transform: translateY(1px); }
.severidades-page__btn:disabled { opacity: 0.6; cursor: not-allowed; }

.severidades-page__btn--ghost {
  background: #ffffff;
  color: #475569;
  border-color: #e2e8f0;
}
.severidades-page__btn--ghost:hover:not(:disabled) {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.severidades-page__btn--primary {
  background: #1976d2;
  color: #ffffff;
  box-shadow: 0 4px 12px -4px rgba(25, 118, 210, 0.45);
}
.severidades-page__btn--primary:hover:not(:disabled) {
  background: #1565c0;
  box-shadow: 0 6px 16px -4px rgba(25, 118, 210, 0.55);
}
.severidades-page__btn--primary >>> .v-icon { color: #fff !important; }

/* Info banner */
.severidades-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: rgba(25, 118, 210, 0.08);
  border: 1px solid rgba(25, 118, 210, 0.18);
  border-radius: 10px;
  font-size: 12.5px;
  color: #1e3a8a;
  margin-bottom: 16px;
}

/* Tabla layout */
.severidades-table-head {
  display: grid;
  grid-template-columns: 1.1fr 1.4fr 1fr 1.3fr 1.1fr 0.7fr;
  gap: 10px;
  padding: 8px 14px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: #475569;
}

.severidades-row {
  display: grid;
  grid-template-columns: 1.1fr 1.4fr 1fr 1.3fr 1.1fr 0.7fr;
  gap: 10px;
  padding: 14px;
  background: #ffffff;
  border: 1px solid #e6ebf2;
  border-radius: 12px;
  margin-bottom: 10px;
  align-items: start;
  transition: box-shadow 0.18s ease;
}

.severidades-row:hover {
  box-shadow: 0 6px 20px -10px rgba(15, 39, 78, 0.16);
}

.severidades-row--advertencia {
  background: linear-gradient(135deg, rgba(250, 204, 21, 0.06) 0%, #ffffff 60%);
}
.severidades-row--alarma {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.06) 0%, #ffffff 60%);
}

/* Name col */
.severidades-row__name {
  display: flex;
  gap: 10px;
  align-items: center;
}

.severidades-row__name-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.severidades-row__name-icon >>> .v-icon {
  font-size: 20px !important;
}

.severidades-row--advertencia .severidades-row__name-icon {
  background: rgba(250, 204, 21, 0.18);
}
.severidades-row--advertencia .severidades-row__name-icon >>> .v-icon { color: #ca8a04 !important; }

.severidades-row--alarma .severidades-row__name-icon {
  background: rgba(239, 68, 68, 0.16);
}
.severidades-row--alarma .severidades-row__name-icon >>> .v-icon { color: #dc2626 !important; }

.severidades-row__name-title {
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.04em;
  color: #0f172a;
  margin-bottom: 4px;
}

.severidades-row__name-chip {
  display: inline-block;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.05em;
  padding: 3px 9px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.06);
  color: #475569;
}

.severidades-row--advertencia .severidades-row__name-chip {
  background: rgba(250, 204, 21, 0.18);
  color: #b45309;
}
.severidades-row--alarma .severidades-row__name-chip {
  background: rgba(239, 68, 68, 0.16);
  color: #b91c1c;
}

/* Field */
.severidades-row__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.severidades-row__field--center {
  align-items: center;
  justify-content: center;
}

.severidades-row__field-hint {
  font-size: 11px;
  color: #94a3b8;
}

.severidades-row__color-swatch {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
  border: 1px solid rgba(15, 39, 78, 0.12);
}

.severidades-row__priority-note {
  font-size: 11px;
  line-height: 1.4;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid transparent;
}

.severidades-row__priority-note--adv {
  background: rgba(245, 158, 11, 0.08);
  border-color: rgba(245, 158, 11, 0.2);
  color: #b45309;
}
.severidades-row__priority-note--alarma {
  background: rgba(239, 68, 68, 0.08);
  border-color: rgba(239, 68, 68, 0.2);
  color: #b91c1c;
}

.severidades-row__horario {
  display: flex;
  align-items: center;
  gap: 8px;
}

.severidades-row__horario-label {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
}

.severidades-row__estado-chip {
  font-weight: 700 !important;
  letter-spacing: 0.02em;
}

/* Chip Fijo en tabla de niveles (table row action) */
.alarm-fixed-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  border-radius: 999px;
  background: #f1f5f9;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

@media (max-width: 1100px) {
  .severidades-table-head { display: none; }
  .severidades-row { grid-template-columns: 1fr; gap: 14px; }
  .severidades-row__field--center { align-items: flex-start; }
}

@media (max-width: 720px) {
  .severidades-page { padding: 16px 14px 30px; }
  .severidades-page__title-row { flex-direction: column; }
  .severidades-page__actions { width: 100%; justify-content: stretch; }
  .severidades-page__actions > * { flex: 1; justify-content: center; }
  .severidades-page__title { font-size: 22px; }
}
</style>
